import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchJobs, getBrokers, createJob, updateJob, getCompany, updateLocation, getLocations, jobDisplayNumber } from "../../services/firestore";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { computeJobBilling, guardOwnsCar, stopCoveredByGuard, vaultNightsBetween } from "../../services/billing";
import { driveAlongRoute, driveBetween, geocode } from "../../services/mapsDistance";
import { coordsForAirport, cityForAirport } from "../../constants/airports";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { formatPhone } from "../../utils/phone";
import PlacesAutocomplete from "../../components/PlacesAutocomplete";
import PodCropper from "../../components/PodCropper";
import SameRunPanel from "../../components/owner/SameRunPanel";
import { downloadInvoiceWorkbook, generateInvoiceWorkbooks } from "../../services/invoiceWorkbook";
import { downloadPodPdf, generatePodPdf } from "../../services/podPdf";
import { isFolderSaveSupported, pickSaveFolder, forgetSaveFolder, getSaveFolder, savedFolderName, folderPathForJob } from "../../services/saveFolder";
import { createInvoicesForJob, hasSentInvoicesForJob } from "../../services/invoices";
import { composeJobDraft, isGmailConnected, getAccessToken } from "../../services/gmail";
import { updateInvoice } from "../../services/invoices";
import { uploadExpenseReceipt, uploadGuardPhoto } from "../../services/storage";
import { isDaiJob, directBillsByDate, jobReadyByDate } from "../../services/billingReadiness";
import { hawbLabel } from "../../services/hawbLabel";
import "./OwnerLayout.css";

// ── Time-string helpers (HH:MM 24h, independent of any Date object) ──
// We keep times as "HH:MM" strings so the <input type="time"> round-trips
// cleanly and there's no timezone to reason about.
function pad2(n) { return String(n).padStart(2, "0"); }

function minutesOf(hhmm) {
  if (!hhmm || !/^\d{1,2}:\d{2}$/.test(hhmm)) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}
function toHHMM(mins) {
  if (mins == null) return "";
  const m = ((mins % (24 * 60)) + 24 * 60) % (24 * 60); // wrap to 0-1439
  return `${pad2(Math.floor(m / 60))}:${pad2(m % 60)}`;
}
function addMinutes(hhmm, delta) {
  const m = minutesOf(hhmm);
  if (m == null) return "";
  return toHHMM(m + delta);
}
// "25 min" / "1h 05m" for Google drive durations.
function fmtDriveSec(sec) {
  if (sec == null) return "";
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${pad2(m % 60)}m`;
}

// Pull HH:MM from either a string time ("10:00") or a Date/Timestamp.
function tsToHHMM(ts) {
  if (!ts) return "";
  if (typeof ts === "string") {
    if (/^\d{1,2}:\d{2}$/.test(ts)) return ts.length === 5 ? ts : `0${ts}`;
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

// ── Ferrari/Executive billing-recall helpers ─────────────────────────
// Extract the city from a Google-formatted address ("1 Stoney Creek Rd,
// Mount Pleasant, SC 29464, USA" → "Mount Pleasant"). Mirrors the helper
// in monthlyFerrariWorkbook.js.
function cityFromAddress(address) {
  if (!address) return "";
  const parts = String(address).split(",").map((s) => s.trim()).filter(Boolean);
  if (parts.length >= 3) return parts[1];
  if (parts.length === 2) return parts[0];
  return "";
}

// "44 Sharon Road, CHARLOTTE, NC 28210" → "Charlotte, NC" — shown beside
// each stop on the billing list so a residential name still has a place.
function cityStateOf(address) {
  const parts = String(address || "").split(",").map((x) => x.trim()).filter(Boolean);
  if (parts[parts.length - 1] === "USA") parts.pop();
  if (parts.length < 3) return "";
  const city = parts[parts.length - 2].toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const st = (parts[parts.length - 1].match(/^[A-Za-z]{2}\b/) || [""])[0].toUpperCase();
  return st ? `${city}, ${st}` : city;
}

// Samples from a location's brokerStats entry — the per-trip history, or
// a single synthetic sample from the legacy last* fields for locations
// billed before history tracking existed.
function recallSamplesOf(entry) {
  if (!entry) return [];
  const samples = Array.isArray(entry.samples) ? entry.samples : [];
  if (samples.length === 0 && (entry.lastHours != null || entry.lastMiles != null)) {
    return [{ hours: Number(entry.lastHours) || 0, miles: Number(entry.lastMiles) || 0 }];
  }
  return samples;
}

// Most common value of `key` across samples (grouped at `decimals`
// places) — one outlier day shouldn't move the suggestion. Ties go to
// the most recently billed of the tied values; if every trip differs,
// fall back to the median — always an actually-billed value, never a
// synthetic mean.
function usualFromSamples(samples, key, decimals) {
  const count = samples.length;
  if (!count) return null;
  const vals = samples.map((x, i) => ({ v: Number(x[key]) || 0, i }));
  const keyOf = (v) => v.toFixed(decimals);
  const freq = new Map();
  for (const { v } of vals) {
    const k = keyOf(v);
    freq.set(k, (freq.get(k) || 0) + 1);
  }
  const maxFreq = Math.max(...freq.values());
  if (maxFreq > 1) {
    let best = null;
    for (const s of vals) {
      if (freq.get(keyOf(s.v)) !== maxFreq) continue;
      if (!best || s.i > best.i) best = s;
    }
    return best.v;
  }
  const sorted = vals.map((x) => x.v).sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)];
}

const BILLING_TABS = [
  { key: "all",      label: "All" },
  { key: "malca",    label: "Malca" },
  { key: "orion",    label: "Orion direct" },
  { key: "bundled",  label: "Bundled (DAI)" },
  { key: "ferrari",  label: "Ferrari monthly" },
  { key: "exec",     label: "Executive monthly" },
];

// Names that should map to each tab regardless of how the broker doc spelled
// them ("DAI" vs "Dana Augustine Inc.", "Malca-Amit" vs "Malca", etc.).
const NAME_HINTS = {
  malca:    ["malca"],
  orion:    ["lali", "effy", "iberjoya", "mwi", "orion"],
  bundled:  ["dai", "dana augustine", "augustine"],
  ferrari:  ["ferrari"],
  exec:     ["executive", "exec"],
};

function matchTab(job, key, dispatcherName) {
  if (key === "all") return true;
  const bt = (job.billingType || "").toLowerCase();
  const bn = (job.brokerName || "").toLowerCase();
  if (key === "bundled" && bt === "bundled") return true;
  if (key === "ferrari" && bt === "monthly") return true; // monthly roll-up
  const hints = NAME_HINTS[key] || [];
  // Ferrari / Executive are DISPATCHER-level tabs — a broker like
  // Kallati carries no hint in its name, but its rate card names
  // Executive Security Services as the dispatcher. Match those two
  // tabs on the dispatcher too (rate card first — see dispatcherFor).
  // Not applied to the orion/malca keys: Orion dispatches Malca, so a
  // dispatcher match there would double-file Malca jobs.
  if (key === "ferrari" || key === "exec") {
    const dn = (dispatcherName || job.dispatcherName || "").toLowerCase();
    if (hints.some((h) => dn.includes(h))) return true;
  }
  return hints.some((h) => bn.includes(h));
}

function formatMoney(n) {
  if (n == null || (n === 0 && n !== 0)) return "—";
  return `$${Number(n).toFixed(2)}`;
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  // Some test/legacy jobs have a completedAt value that's set but
  // unparseable (empty string, malformed object). new Date() returns
  // the "Invalid Date" sentinel for those — render as "—" instead so
  // the column doesn't expose the JS internal name to the owner.
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Name kept for backward compat; we use 24h everywhere now per house style.
function formatTime12(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

function formatTime24(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
}

export default function ReadyToBill() {
  const { userProfile } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [reviewJob, setReviewJob] = useState(null);
  // Brief banner shown after Approve & invoice so the user knows the job
  // didn't vanish — it became N invoices on the Invoices page.
  const [approvedToast, setApprovedToast] = useState(null);
  // When true, the list also includes already-invoiced jobs so the
  // owner can re-open the Review modal in read-only mode and see what
  // was billed. The Review modal's existing invoice-lock logic prevents
  // accidental edits to a billed job.
  const [showInvoiced, setShowInvoiced] = useState(false);
  const navigate = useNavigate();
  const [isPhone, setIsPhone] = useState(typeof window !== "undefined" && window.innerWidth < 700);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    // Subscribe to ALL jobs so we can surface two distinct pools client-side:
    //   1) Per-job billing — status="complete" and not invoiced (existing flow).
    //   2) Bundled (DAI) — jobs grouped by bundleId where the bundle's last
    //      scheduledDate is in the past and not yet forwarded. These don't
    //      go through a guard workflow (flat-rate, forwarded email is the
    //      bill) so they never reach status="complete" — we surface them
    //      by date instead.
    const unsub = watchJobs(userProfile.companyId, null, (data) => {
      // Hide already-billed jobs unless toggled — "sent"/"paid" count as
      // billed too, not just "invoiced". Status filtering for per-job vs
      // bundle is applied later in the tab-specific logic.
      setJobs(showInvoiced ? data : data.filter((j) => !["invoiced", "sent", "paid"].includes(j.invoiceStatus)));
      setLoading(false);
    });
    getBrokers(userProfile.companyId).then(setBrokers);
    getCompany(userProfile.companyId).then(setCompany);
    return unsub;
  }, [userProfile, showInvoiced]);

  // Today as YYYY-MM-DD for comparing scheduledDate strings (which are
  // stored in that exact format). Pure string compare via localeCompare,
  // no Date object / timezone math required.
  const todayYmd = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }, []);

  // Group jobs by bundleId. DAI-style bundled brokers create N jobs
  // sharing one bundleId from a single schedule email (one email = one
  // batch); we treat the whole bundle as one billing unit. We surface
  // BOTH:
  //   - "ready" batches — every job's scheduledDate is in the past, so
  //     the whole batch can be forwarded.
  //   - "filling" batches — some jobs are still upcoming. We show these
  //     with an "X of N ready" count and a disabled Forward button so
  //     the owner waits until the batch is complete (the "wait for 3
  //     of 3 before billing" rule) instead of forwarding a half-done
  //     batch or forgetting about it entirely.
  // A batch drops off the list once every job in it is invoiced.
  const bundleList = useMemo(() => {
    const byId = new Map();
    for (const j of jobs) {
      // Every DAI-style job belongs to this tab — matchTab handles both
      // the explicit billingType==="bundled" and the broker-name hints.
      if (!matchTab(j, "bundled")) continue;
      // Multi-stop schedule jobs share a bundleId (one email = one
      // batch). A standalone DAI job (created on its own, no schedule
      // email) has no bundleId — we give it a synthetic solo key so it
      // shows here as a 1-of-1 batch instead of only appearing in the
      // per-job All tab. All DAI work lives in one place this way.
      const key = j.bundleId || `solo:${j.id}`;
      const cur = byId.get(key) || { bundleId: key, jobs: [], isSolo: !j.bundleId };
      cur.jobs.push(j);
      byId.set(key, cur);
    }
    const out = [];
    for (const b of byId.values()) {
      const hasUninvoiced = b.jobs.some((j) => j.invoiceStatus !== "invoiced");
      const fullyInvoiced = !hasUninvoiced;
      // Fully-invoiced batch — normally nothing left to do, so we drop
      // it. BUT when the owner has flipped "Show invoiced" on, surface
      // it as a read-only "Invoiced" row so they can audit batches that
      // were already forwarded (otherwise a fully-invoiced DAI bundle is
      // invisible in BOTH modes, which is how old May batches went
      // missing). Forward is disabled on these.
      if (fullyInvoiced && !showInvoiced) continue;

      const dates = b.jobs.map((j) => j.scheduledDate).filter(Boolean).sort();
      const earliest = dates[0] || null;
      const latest   = dates[dates.length - 1] || null;

      // Per-job readiness: a job is "ready" once its scheduledDate is
      // today-or-earlier, OR it's already been marked complete by the
      // guard (covers jobs that ran early). "Today-or-earlier" (not
      // strictly-before) means a batch becomes forwardable ON the day of
      // its last job, not the morning after. Undated jobs are never
      // counted ready — the owner needs to fix the date first. Shared
      // with the Dashboard via jobReadyByDate so both surfaces agree.
      const isJobReady = (j) => jobReadyByDate(j, todayYmd);
      const readyCount = b.jobs.filter(isJobReady).length;
      const totalCount = b.jobs.length;
      const ready = readyCount === totalCount && totalCount > 0;

      // Entirely-future batch (no job has happened yet) — don't surface
      // it on Ready to Bill at all. It's upcoming work, not billing
      // work; it lives in the Job Inbox until its first job is past.
      // Matches the owner's Airtable model where a job only shows in
      // "To Invoice" once its date is at/before today. The batch starts
      // appearing here at 1/N and fills toward N/N.
      if (readyCount === 0) continue;

      // Next upcoming date among the not-yet-ready jobs — shown on
      // filling batches so the owner knows when to circle back.
      const upcomingDates = b.jobs
        .filter((j) => !isJobReady(j))
        .map((j) => j.scheduledDate)
        .filter(Boolean)
        .sort();
      const nextUpcoming = upcomingDates[0] || null;

      const brokerName = b.jobs[0]?.brokerName || "";
      const dispatcherName = b.jobs[0]?.dispatcherName || "";
      const repName = b.jobs[0]?.salesRepName || "";
      out.push({
        bundleId: b.bundleId,
        isSolo: !!b.isSolo,
        jobIds: b.jobs.map((j) => j.id),
        jobs: b.jobs,
        brokerName,
        dispatcherName,
        repName,
        earliest,
        latest,
        jobCount: totalCount,
        readyCount,
        totalCount,
        ready,
        fullyInvoiced,
        nextUpcoming,
      });
    }
    // Order: actionable (ready, not invoiced) → filling → already
    // invoiced (history). Within each group, oldest earliest-date first.
    const rank = (x) => (x.fullyInvoiced ? 2 : (x.ready ? 0 : 1));
    out.sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      return String(a.earliest).localeCompare(String(b.earliest));
    });
    return out;
  }, [jobs, todayYmd, showInvoiced]);

  // Count of batches actually ready to forward — drives the tab badge
  // and sidebar so "filling" batches don't nag the owner about work
  // they can't do yet.
  const readyBundleCount = useMemo(
    () => bundleList.filter((b) => b.ready && !b.fullyInvoiced).length,
    [bundleList]
  );

  // Schedule-batch context for NON-DAI bundled directs (LALI / EFFY /
  // Iberjoya / MWI). These bill hourly per-job but arrive as one
  // schedule email with several jobs — the owner likes seeing "job 2
  // of 3 in this rep's schedule" while reviewing each one individually.
  // Maps bundleId → { total, complete } across every job in the bundle.
  const scheduleBatchInfo = useMemo(() => {
    const map = new Map();
    for (const j of jobs) {
      if (!j.bundleId) continue;
      if (matchTab(j, "bundled")) continue; // DAI handled separately
      const cur = map.get(j.bundleId) || { total: 0, complete: 0 };
      cur.total += 1;
      if (j.status === "complete") cur.complete += 1;
      map.set(j.bundleId, cur);
    }
    // A bundle of one is just a job — "0 of 1 in schedule done" is noise.
    // Same size-1 suppression the Job inbox applies to its 📎 chip.
    for (const [key, info] of map.entries()) {
      if (info.total < 2) map.delete(key);
    }
    return map;
  }, [jobs]);

  // Sort by scheduledDate ascending so upcoming/recent jobs read top-
  // to-bottom in the same order the owner thinks of them. Jobs with
  // no scheduledDate sink to the bottom (filtered intake from broker
  // emails that haven't had a date entered).
  //
  // For per-job rows we ONLY include status="complete" jobs — that's
  // the historical contract for Ready to Bill. ALL DAI-style jobs
  // (bundled OR standalone) are handled by the bundle/batch view, so
  // they're excluded here — they shouldn't double up as per-job rows
  // on the All tab. (Before, a standalone DAI job with no bundleId
  // leaked into All; now it's a 1-of-1 batch in the DAI tab instead.)
  const isBundledTab = activeTab === "bundled";
  // Which per-job rows are visible as "needs billing":
  //   - DAI is always excluded here — it lives in the Bundled (DAI) tab.
  //   - Cancelled jobs never show.
  //   - A completed job (any broker) shows, as before.
  //   - Directs ALSO show by date: once the scheduled date is
  //     today-or-earlier, even if a guard never tapped "complete".
  //     Directs bill off the schedule (Orion brokers, Malca, Executive/
  //     Kallati, Ferrari alike — see directBillsByDate), so we'd rather
  //     over-surface than silently miss a bill when a guard forgets to
  //     mark the job done.
  // Malca / Ferrari / Executive inbound/outbound keep the historical
  // complete-only rule.
  const isBillableVisible = (j) => {
    if (isDaiJob(j)) return false;
    if (j.status === "cancelled") return false;
    if (j.status === "complete") return true;
    if (directBillsByDate(j)) return jobReadyByDate(j, todayYmd);
    return false;
  };
  // Dispatcher for a job, rate card first. Jobs snapshot dispatcherName
  // at creation, which goes stale when a broker's rate card is fixed
  // later (Kallati jobs stored "Orion"; the card says Executive
  // Security Services) — so the broker doc is the source of truth and
  // the job's stored value is only a fallback.
  const dispatcherFor = (job) =>
    (job.brokerName && brokerForJob(job)?.dispatcherName) || job.dispatcherName || "";

  // Per-job rows = visible-billable jobs that match the active tab.
  // Non-DAI bundled directs (LALI / EFFY / Iberjoya / MWI schedule
  // emails) ARE included — they bill hourly per-job, so each job is its
  // own reviewable row.
  const tabJobs = jobs
    .filter(isBillableVisible)
    .filter((j) => matchTab(j, activeTab, dispatcherFor(j)))
    .slice()
    .sort((a, b) => {
      const da = a.scheduledDate || "9999-99-99";
      const db = b.scheduledDate || "9999-99-99";
      if (da !== db) return da.localeCompare(db);
      return (a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99");
    });
  const totalBillable = tabJobs.reduce((s, j) => s + (j.billedAmount || 0), 0);
  // Header count: the billable jobs across the tabs (the All tab's count).
  const listedCount = jobs.filter(isBillableVisible).length;

  function brokerForJob(job) {
    const wanted = (job.brokerName || "").toLowerCase();
    return brokers.find((b) => (b.name || "").toLowerCase() === wanted)
        || brokers.find((b) => wanted.includes((b.name || "").toLowerCase()) || (b.name || "").toLowerCase().includes(wanted));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="page-header">
        <div>
          <div className="page-title">Ready to bill</div>
          <div className="page-subtitle">
            {listedCount} job{listedCount !== 1 ? "s" : ""}
            {showInvoiced ? " (including invoiced — read-only)" : " awaiting invoice"}
          </div>
        </div>
        {/* Toggle to include already-invoiced jobs in the list. Owner
            uses this to re-open the Review modal in read-only mode and
            see what was billed on a past job. The modal's existing
            invoice-lock prevents accidental edits. */}
        <button
          className={`btn btn-sm ${showInvoiced ? "btn-blue" : ""}`}
          onClick={() => setShowInvoiced((v) => !v)}
          title="Include already-invoiced jobs in the list (read-only)"
        >
          {showInvoiced ? "✓ Showing invoiced" : "Show invoiced"}
        </button>
      </div>

      {/* Approval confirmation toast — shows N invoices were created and
          where to find them. Auto-hides after 8s; user can also dismiss. */}
      {approvedToast && (
        <div style={{
          margin: "10px 20px 0", padding: "10px 14px",
          background: approvedToast.gmailError ? "#FFF7E0" : "#E0F4E8",
          border: `1px solid ${approvedToast.gmailError ? "#F0D88C" : "#a9d4c2"}`,
          borderRadius: 8, display: "flex", alignItems: "center", gap: 12,
          fontSize: 12, color: approvedToast.gmailError ? "#74540C" : "#0E7C46",
          flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 18 }}>✓</span>
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>{approvedToast.jobNumber}</strong> approved —
            {" "}{approvedToast.count} invoice{approvedToast.count !== 1 ? "s" : ""} created in Draft.
            {approvedToast.gmailDraftUrl && (
              <span> &nbsp;📧 Gmail draft composed.</span>
            )}
            {approvedToast.gmailNeedsConnect && (
              <button
                onClick={async () => {
                  try {
                    await getAccessToken({ interactive: true, forceConsent: true });
                    setApprovedToast((t) => t && { ...t, gmailNeedsConnect: false, gmailError: "Gmail connected — re-approve this job to create the draft." });
                  } catch (err) {
                    console.error("Connect Gmail failed", err);
                    alert("Couldn't connect Gmail: " + (err?.message || err));
                  }
                }}
                style={{
                  background: "#185FA5", color: "#fff", border: "none", borderRadius: 6,
                  padding: "4px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                  marginLeft: 8, whiteSpace: "nowrap",
                }}
              >Connect Gmail</button>
            )}
            {approvedToast.gmailError && (
              <div style={{ marginTop: 4, fontSize: 11 }}>
                Gmail draft skipped: {approvedToast.gmailError}
              </div>
            )}
          </div>
          {approvedToast.gmailDraftUrl && (
            <a
              href={approvedToast.gmailDraftUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-blue"
              style={{ textDecoration: "none" }}
            >📧 Open draft</a>
          )}
          <button
            className="btn btn-sm btn-blue"
            onClick={() => navigate("/owner/invoices")}
          >View invoices →</button>
          <button
            onClick={() => setApprovedToast(null)}
            style={{ background: "none", border: "none", color: "inherit", fontSize: 16, cursor: "pointer", padding: 0 }}
            title="Dismiss"
          >✕</button>
        </div>
      )}

      <div style={{ display: "flex", borderBottom: "1px solid #e8e8e8", background: "#fff", paddingLeft: 20, flexShrink: 0 }}>
        {BILLING_TABS.map((tab) => {
          // Tab counts:
          //   bundled  → number of batches READY to forward (filling
          //              batches are shown in the tab but don't inflate
          //              the badge — the owner can't act on them yet)
          //   anything else → visible-billable jobs in that tab (uses the
          //              same isBillableVisible rule as the list, so each
          //              tab's badge counts date-passed directs too).
          const count = tab.key === "bundled"
            ? readyBundleCount
            : jobs.filter((j) => isBillableVisible(j) && matchTab(j, tab.key, dispatcherFor(j))).length;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              padding: "10px 16px", fontSize: 12, fontWeight: 600,
              border: "none", background: "none", cursor: "pointer",
              color: activeTab === tab.key ? "#185FA5" : "#888",
              borderBottom: `2px solid ${activeTab === tab.key ? "#185FA5" : "transparent"}`,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              {tab.label}
              {count > 0 && (
                <span style={{
                  background: activeTab === tab.key ? "#185FA5" : "#e8e8e8",
                  color: activeTab === tab.key ? "#fff" : "#666",
                  borderRadius: 10, padding: "1px 6px", fontSize: 10,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="page-body">
        {/* Same-day Ferrari jobs that were one run — combine before billing. */}
        {!loading && !isBundledTab && (
          <SameRunPanel
            companyId={userProfile?.companyId}
            onOpenJob={(id) => { const j = jobs.find((x) => x.id === id); if (j) setReviewJob(j); }}
          />
        )}
        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : isBundledTab ? (
          // ── Bundled (DAI) tab ─────────────────────────────────────
          // Render one row per BUNDLE (grouped by bundleId), not per job.
          // DAI is flat-rate and the broker invoices off the forwarded
          // schedule email — so "ready to bill" really means "every job
          // in the bundle is in the past, time to forward."
          bundleList.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">📎</div>
              <div>No DAI batches in progress</div>
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>
                A batch appears here as soon as a DAI schedule email is parsed into jobs.
                It shows an "X of N ready" count while jobs are still upcoming, and becomes
                forwardable once every job in the batch is in the past.
              </div>
            </div>
          ) : (
            <BundledTabBody
              bundles={bundleList}
              brokers={brokers}
              userProfile={userProfile}
              onForwarded={(forwardedBundle) => {
                // Optimistic: flip the forwarded batch's jobs to invoiced
                // locally so the row drops out immediately. Match by job
                // id (not bundleId) so solo 1-of-1 batches — whose key is
                // a synthetic "solo:<id>" — get flipped too. The Firestore
                // watcher reconciles shortly after.
                const flipIds = new Set(forwardedBundle?.jobIds || []);
                setJobs((js) => js.map((j) => flipIds.has(j.id)
                  ? { ...j, invoiceStatus: "invoiced" }
                  : j));
                setApprovedToast({
                  jobNumber: forwardedBundle?.isSolo ? "DAI job" : "DAI bundle",
                  count: forwardedBundle?.jobCount || flipIds.size || 0,
                });
                setTimeout(() => setApprovedToast(null), 8000);
              }}
            />
          )
        ) : tabJobs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✅</div>
            <div>No jobs ready to bill in this category</div>
          </div>
        ) : (
          <>
            <div style={{
              background: "#EEF4FF", border: "1px solid #C5D9F5", borderRadius: 8,
              padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 24,
            }}>
              <div>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>JOBS</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#185FA5" }}>{tabJobs.length}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>APPROVED</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#185FA5" }}>
                  {tabJobs.filter((j) => j.billingApprovedAt).length}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>TOTAL BILLABLE</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#185FA5" }}>{formatMoney(totalBillable)}</div>
              </div>
            </div>

            {isPhone ? (
              tabJobs.map((job) => {
                const numGuards = Number(job.numGuards) || (job.guardAssignments?.length || 1);
                return (
                  <div key={job.id} style={{ background: "#fff", border: "1px solid #e3e6ea", borderRadius: 12, padding: "12px 14px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: 15 }}>{jobDisplayNumber(job) || "—"}</span>
                      <span className={`chip ${job.jobType === "inbound" ? "chip-blue" : "chip-gray"}`}>{job.jobType || "—"}</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: "#6b7684", marginTop: 2 }}>
                      {jobDateLabel(job, true)} · {job.brokerName || "—"} · {numGuards} guard{numGuards !== 1 ? "s" : ""}
                    </div>
                    <div style={{ marginTop: 4 }}><StopLines job={job} /></div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 10 }}>
                      <span style={{ fontSize: 13 }}>
                        <b>{formatMoney(job.billedAmount)}</b>
                        {job.billingApprovedAt && <span className="chip chip-green" style={{ fontSize: 10, marginLeft: 6 }}>✓ Approved</span>}
                      </span>
                      <button className="btn btn-blue" style={{ flex: 1, maxWidth: 220, minHeight: 42, fontSize: 14 }} onClick={() => setReviewJob(job)}>Review</button>
                    </div>
                  </div>
                );
              })
            ) : (
            <div className="panel">
              {/* Columns: Job # · Type · Broker (· dispatcher when it
                  differs) · Date · Stops · Guards · Billed · Status ·
                  Actions. Each stop shows its city and order numbers so a
                  residential name is still placeable. */}
              <div style={{
                display: "grid", gridTemplateColumns: "110px 70px 0.8fr 90px 1.8fr 50px 80px 90px 80px",
                padding: "8px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
                fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
              }}>
                <span>Job #</span><span>Type</span><span>Broker</span>
                <span>Date</span><span>Stops</span><span>Guards</span>
                <span>Billed</span><span>Status</span>
                <span style={{ textAlign: "right" }}>Actions</span>
              </div>
              {tabJobs.map((job) => {
                const numGuards = Number(job.numGuards) || (job.guardAssignments?.length || 1);
                const dispatcher = dispatcherFor(job);
                return (
                  <div key={job.id} style={{
                    display: "grid", gridTemplateColumns: "110px 70px 0.8fr 90px 1.8fr 50px 80px 90px 80px",
                    padding: "10px 14px", borderBottom: "1px solid #f0f0f0",
                    alignItems: "center", gap: 8, fontSize: 12,
                  }}>
                    <span style={{ fontWeight: 700, color: "#185FA5" }}>{jobDisplayNumber(job) || "—"}</span>
                    <span style={{ color: "#666" }}>{job.jobType || "—"}</span>
                    <div>
                      <div style={{ fontWeight: 500 }}>{job.brokerName || "—"}</div>
                      {dispatcher && dispatcher.toLowerCase() !== String(job.brokerName || "").toLowerCase() && (
                        <div style={{ fontSize: 10, color: "#888" }}>{dispatcher}</div>
                      )}
                      {/* Schedule-batch context for non-DAI bundled
                          directs — "1 of 3 in schedule" so the owner
                          knows how many came in on the rep's email, even
                          though each is billed individually. */}
                      {job.bundleId && scheduleBatchInfo.has(job.bundleId) && (() => {
                        const info = scheduleBatchInfo.get(job.bundleId);
                        return (
                          <div style={{ fontSize: 9, color: "#185FA5", fontWeight: 700, marginTop: 1 }}>
                            📎 {info.complete} of {info.total} in schedule done
                          </div>
                        );
                      })()}
                    </div>
                    <span style={{ color: "#444", fontSize: 11 }}>{jobDateLabel(job, true)}</span>
                    <div style={{ minWidth: 0 }}><StopLines job={job} withAddress /></div>
                    <span style={{ color: "#444", fontSize: 11, textAlign: "center" }}>
                      {numGuards}
                    </span>
                    <span style={{ fontWeight: 600 }}>{formatMoney(job.billedAmount)}</span>
                    <span>
                      {job.billingApprovedAt ? (
                        <span className="chip chip-green" style={{ fontSize: 10 }}>✓ Approved</span>
                      ) : (
                        <span className="chip" style={{ fontSize: 10, background: "#f0f0f0", color: "#666" }}>Needs review</span>
                      )}
                    </span>
                    <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                      <button className="btn btn-sm btn-blue" onClick={() => setReviewJob(job)}>Review</button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </>
        )}
      </div>

      {reviewJob && (
        <ReviewModal
          job={reviewJob}
          broker={brokerForJob(reviewJob)}
          company={company}
          allJobs={jobs}
          onClose={() => setReviewJob(null)}
          onSaved={(patch) => {
            // Optimistic local update so the row reflects immediately.
            setJobs((js) => js.map((j) => j.id === reviewJob.id ? { ...j, ...patch } : j));
            // Surface a confirmation toast when the user just approved
            // (and N invoices were created). Auto-clears after 6s but the
            // user can also click "View invoices" to jump straight there.
            if (patch.invoicesCreated?.length) {
              setApprovedToast({
                jobNumber: reviewJob.jobNumber || reviewJob.brokerName || "job",
                count: patch.invoicesCreated.length,
                // Surface the Gmail draft outcome inline in the toast.
                // gmailDraftUrl drives the "📧 Open draft" button;
                // gmailError drives the amber "skipped" message.
                gmailDraftUrl: patch.gmailDraft?.draftId
                  ? (patch.gmailDraft.authEmail
                      ? `https://mail.google.com/mail/?authuser=${encodeURIComponent(patch.gmailDraft.authEmail)}#drafts/${patch.gmailDraft.draftId}`
                      : `https://mail.google.com/mail/u/0/#drafts/${patch.gmailDraft.draftId}`)
                  : null,
                gmailError: patch.gmailError || null,
                gmailNeedsConnect: !!patch.gmailNeedsConnect,
              });
              // Leave a reconnect prompt up longer than a normal toast — it
              // needs a decision, not just a glance.
              setTimeout(() => setApprovedToast(null), patch.gmailNeedsConnect ? 30000 : 10000);
            }
            setReviewJob(null);
          }}
        />
      )}
    </div>
  );
}

// Brokers that use the "relaxed" Ferrari-style billing — mileage
// auto-calc starts and ends at the OWNER'S HOME (not the company hub or
// first pickup). Hourly + per-mile with a manual hours entry; the owner
// types one "leave home" time and the rest of the schedule fills from
// the cumulative drive duration. Add new brokers here as the list
// grows; matching is case-insensitive substring on broker name.
// One line per stop: name · city · the order/HAWB numbers handled there.
// The street address goes under it on the desktop table.
function StopLines({ job, withAddress }) {
  const realStops = (job.stops || []).filter((s) => s.type !== "airport");
  if (realStops.length === 0) return <span style={{ color: "#999" }}>—</span>;
  const ids = new Set(realStops.map((s) => s.id));
  const loose = (job.packages || []).filter((p) => !ids.has(p.stopId)).map((p) => p.hawbNumber).filter(Boolean);
  return (
    <>
      {realStops.map((s) => {
        const nums = (job.packages || []).filter((p) => p.stopId === s.id).map((p) => p.hawbNumber).filter(Boolean);
        const city = s.outOfTown ? "" : cityStateOf(s.address);
        return (
          <div key={s.id} style={{ marginBottom: 3 }}>
            <div style={{ color: "#444", fontSize: withAddress ? 11 : 12.5 }}>
              {s.outOfTown ? "OOT" : (s.locationName || "—")}
              {city && <span style={{ color: "#6b7684" }}> · {city}</span>}
              {nums.length > 0 && <span style={{ color: "#185FA5", fontWeight: 600 }}> · {nums.join(", ")}</span>}
            </div>
            {withAddress && s.address && !s.outOfTown && <div style={{ color: "#999", fontSize: 10 }}>{s.address}</div>}
          </div>
        );
      })}
      {loose.length > 0 && (
        <div style={{ color: "#185FA5", fontSize: 11, fontWeight: 600 }}>No stop · {loose.join(", ")}</div>
      )}
    </>
  );
}

// "Tue, Oct 6" from the job's scheduled date (the date the owner thinks
// of the job by), falling back to when it was completed.
function jobDateLabel(job, withWeekday) {
  if (!job.scheduledDate) return formatDate(job.completedAt) || "—";
  const [y, m, d] = String(job.scheduledDate).split("-").map(Number);
  if (!y || !m || !d) return job.scheduledDate;
  return new Date(y, m - 1, d).toLocaleDateString("en-US", withWeekday
    ? { weekday: "short", month: "short", day: "numeric" }
    : { month: "short", day: "numeric" });
}

const FERRARI_STYLE_BROKERS = ["ferrari", "executive"];

function isFerrariStyleBilling(job, dispatcherName) {
  const name = String(job?.brokerName || "").toLowerCase();
  // The dispatcher counts too: a broker like Kallati carries no hint in
  // its name, but its rate card names Executive Security Services as
  // dispatcher — those jobs bill Ferrari-style like any Executive job.
  const dn = String(dispatcherName || job?.dispatcherName || "").toLowerCase();
  return FERRARI_STYLE_BROKERS.some((b) => name.includes(b) || dn.includes(b));
}

// ── REVIEW MODAL ────────────────────────────────────────────
// File-picker styled as a small button, for owner-added POD photos in the
// Proof of delivery panel. Shows an "Uploading…" state while the upload runs.
function OwnerPhotoUpload({ busy, onPick, label = "+ Add photo" }) {
  return (
    <label
      title="Upload a POD photo for this package"
      style={{
        fontSize: 10, fontWeight: 600,
        background: busy ? "#f1f5fb" : "#eef3fb", color: "#185FA5",
        border: "1px solid #d6e0ee", borderRadius: 10, padding: "2px 9px",
        cursor: busy ? "default" : "pointer", whiteSpace: "nowrap",
      }}
    >
      {busy ? "Uploading…" : label}
      {/* No `capture` attr — owners are often on the POD form / gallery, so
          let the OS picker offer BOTH "Take Photo" and "Photo Library". */}
      <input
        type="file"
        accept="image/*"
        disabled={busy}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) onPick(f);
        }}
      />
    </label>
  );
}

// 24-hour HH:MM time entry. Replaces <input type="time">, whose 12-hour
// locale rendering hides an AM/PM slot that's easy to leave blank — which
// silently invalidates the time and throws off the billing auto-calc.
// Accepts "7:45", "745", "0745", "07:45"; commits a clean "HH:MM" (or "").
function TimeField({ value, onChange, title, style, placeholder = "HH:MM" }) {
  const [raw, setRaw] = useState(value || "");
  // Follow the parent value when it changes externally (e.g. Auto-calc fills
  // it) — but NOT while the field is focused. A live commit mid-typing (e.g.
  // "150" parses to 01:50 before the 4th digit lands) would otherwise echo
  // back and clobber what's being typed, stranding it at the wrong time.
  const focusedRef = useRef(false);
  useEffect(() => { if (!focusedRef.current) setRaw(value || ""); }, [value]);

  function normalize(s) {
    const t = String(s).trim();
    if (!t) return null;
    let h, m;
    const colon = t.match(/^(\d{1,2}):(\d{2})$/);
    if (colon) { h = +colon[1]; m = +colon[2]; }
    else {
      const digits = t.replace(/\D/g, "");
      if (digits.length === 3) { h = +digits.slice(0, 1); m = +digits.slice(1); }
      else if (digits.length === 4) { h = +digits.slice(0, 2); m = +digits.slice(2); }
      else return null; // not enough typed yet to parse
    }
    if (h > 23 || m > 59) return null;
    return `${pad2(h)}:${pad2(m)}`;
  }

  function commit(next) {
    if (!String(next).trim()) { onChange(""); return; } // cleared
    const norm = normalize(next);
    if (norm != null) onChange(norm); // only push a valid HH:MM upstream
  }

  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder={placeholder}
      maxLength={5}
      value={raw}
      title={title}
      onFocus={() => { focusedRef.current = true; }}
      onChange={(e) => { setRaw(e.target.value); commit(e.target.value); }}
      onBlur={() => {
        focusedRef.current = false;
        const n = normalize(raw);
        if (n != null) setRaw(n);                 // clean up display to HH:MM
        else if (raw.trim()) setRaw(value || ""); // revert garbage to last committed
      }}
      // minWidth:0 so the field can't hold a grid column open at its
      // intrinsic input width — that's what pushed Miles off-screen in a
      // half-width window.
      style={{ minWidth: 0, ...style }}
    />
  );
}

function ReviewModal({ job, broker, company, allJobs, onClose, onSaved }) {
  useEscapeKey(() => onClose?.());
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  // Invoices already on file for this job (voids left out — they aren't
  // the bill). Shown as a banner with a link once the job is approved.
  const [jobInvoices, setJobInvoices] = useState([]);
  useEffect(() => {
    if (!job.companyId || !job.id) return;
    let cancelled = false;
    getDocs(query(
      collection(db, "invoices"),
      where("companyId", "==", job.companyId),
      where("jobId", "==", job.id),
    ))
      .then((snap) => {
        if (cancelled) return;
        setJobInvoices(snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((i) => i.status !== "void")
          .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0)));
      })
      .catch((err) => console.warn("Couldn't load invoices for job", err));
    return () => { cancelled = true; };
  }, [job.companyId, job.id]);
  const stored = job.billingBreakdown || {};
  const stops = job.stops || [];
  const isOutbound = job.jobType === "outbound";
  const isDirect = job.jobType === "direct";
  // Round trip: airport bookends — starts like an inbound (clock from the
  // arrival flight), ends like an outbound (flight + 15 + return drive).
  const isRoundtrip = job.jobType === "roundtrip";
  // ── Broker / dispatcher classification ──
  // Orion is the DISPATCHER; it sends jobs on behalf of brokers (Malca,
  // MWI, LALI, Iberjoya, DAI, …). Ferrari and Executive are the only other
  // dispatchers. Keying off the dispatcher (not a hard-coded broker list)
  // means any current/future Orion broker is handled automatically.
  const isMalcaBroker = (job.brokerName || "").toLowerCase().includes("malca");
  // LALI bills strictly round-trip drive time: job started = departed pickup
  // (= the job time, no early padding) and arrival = departed drop off (no
  // dwell). EFFY/Iberjoya/MWI keep the standard 15-min-early + 5-min-dwell.
  const isLali = (job.brokerName || "").toLowerCase().includes("lali");
  const isDaiBundle = isDaiJob(job);
  // Rate card first — the job's stored dispatcherName can be stale
  // (e.g. Kallati jobs created while the parser assumed Orion).
  const rateCardDispatcher = broker?.dispatcherName || job.dispatcherName || "";
  const dispatcherLc = rateCardDispatcher.toLowerCase();
  const isOrionDispatched = !(dispatcherLc.includes("ferrari") || dispatcherLc.includes("executive") || isFerrariStyleBilling(job, rateCardDispatcher));
  // Orion direct = a point-to-point direct that Orion dispatches, excluding
  // Malca (own workbook + POD) and DAI (flat fee). Gets the structured
  // drive-time timeline that matches the Orion contractor form.
  const isOrionDirect = isDirect && isOrionDispatched && !isMalcaBroker && !isDaiBundle;
  const realStopIndexes = stops
    .map((s, i) => (s.type !== "airport" ? i : -1))
    .filter((i) => i >= 0);
  const pickupIdx = realStopIndexes.length ? realStopIndexes[0] : 0;
  const dropoffIdx = realStopIndexes.length
    ? realStopIndexes[realStopIndexes.length - 1]
    : Math.max(0, stops.length - 1);
  // Ferrari-style billing flag — drives label changes, auto-calc origin,
  // and time propagation. Calculated once per render off the job + broker.
  const isFerrariStyle = isFerrariStyleBilling(job, rateCardDispatcher);
  // Per-job "bill from my house" override. Lets a Malca/Orion job use the
  // same home-based model (leave home → stops → home) as Ferrari/Executive.
  // Used for vault runs: you pick a piece up to your house and bill the
  // home↔pickup round trip; the next outbound then starts from home since
  // you already hold the piece. `homeBased` is the effective flag every
  // home-origin behavior keys off — broker recall stays on isFerrariStyle.
  const [billFromHome, setBillFromHome] = useState(!!job.billFromHome);
  const homeBased = isFerrariStyle || billFromHome;
  // Owner's home base for Ferrari/Executive auto-calc. Stored on the
  // user doc (set in Settings → Your home base). Falls back to null if
  // the owner hasn't set it yet — the Auto-calc button will show a
  // helpful message rather than silently using the wrong origin.
  const homeCoords = (typeof userProfile?.homeLat === "number" && typeof userProfile?.homeLng === "number")
    ? { lat: userProfile.homeLat, lng: userProfile.homeLng }
    : null;

  // ── Proof-of-delivery requirement ──
  // Of everything Orion dispatches, only Malca (the broker) requires a POD.
  // Jobs from other dispatchers (Ferrari / Executive) keep their existing
  // behavior. A cancelled trip delivered nothing, so it never needs a POD.
  const podRequired = !job.tripCancelled && (isOrionDispatched ? isMalcaBroker : true);

  // ── Times per stop (all HH:MM 24h strings) ──
  // Defaults:
  //   pickupStart = job.scheduledTime if set, else (earliest stop scheduledTime − 30 min), else stop[0].completedAt
  //   each stop time = stop.completedAt (or stop.scheduledTime as fallback)
  //   flightTime = stored.flightTime OR the airport stop's completedAt
  //   returnTime = flightTime + 15 min + driveTime(airport → first pickup) — filled once maps runs
  const defaultPickupStart = useMemo(() => {
    // Inbound: pickup start = airport stop's scheduledTime exactly
    // (the flight arrival time — guard's clock starts when the plane lands).
    // Fallback chain when the airport stop time is missing:
    //   1. job.scheduledTime
    //   2. company.inboundArrivalTime (set in Settings, e.g. "05:15")
    // Without these, inbound jobs would lose the canonical start time
    // and break the workbook's E25.
    // Round trips start at the airport exactly like an inbound — the first
    // airport stop is the arrival flight.
    if (job.jobType === "inbound" || job.jobType === "roundtrip") {
      const airport = stops.find((s) => s.type === "airport");
      if (airport?.scheduledTime) return airport.scheduledTime;
      if (job.scheduledTime) return tsToHHMM(job.scheduledTime);
      if (company?.inboundArrivalTime) return company.inboundArrivalTime;
    }

    // Orion direct: "job started / arrived at pickup" = 15 min before the
    // scheduled pickup time (per the Orion contractor form convention).
    // LALI is the exception — it bills pure drive time, so the job starts AT
    // the job time (no 15-min early padding).
    if (isOrionDirect && job.scheduledTime) {
      const sched = minutesOf(tsToHHMM(job.scheduledTime)) ?? 0;
      return toHHMM(isLali ? sched : sched - 15);
    }

    // Outbound + direct: 30 min before the earliest scheduled pickup
    // (industry convention to be on-site early). We deliberately ignore
    // job.scheduledTime here because brokers set it equal to the pickup
    // time — and the bill should start 30 min before that, not at it.
    const nonAirport = stops.filter((s) => s.type !== "airport");
    const scheduledTimes = nonAirport
      .map((s) => minutesOf(s.scheduledTime))
      .filter((m) => m != null);
    if (scheduledTimes.length) return toHHMM(Math.min(...scheduledTimes) - 30);

    // Fallbacks if no stop scheduled times exist.
    if (job.scheduledTime) return toHHMM((minutesOf(tsToHHMM(job.scheduledTime)) ?? 0) - 30);
    const firstStop = stops.find((s) => s.completedAt);
    if (firstStop) return tsToHHMM(firstStop.completedAt);
    if (job.startedAt) return tsToHHMM(job.startedAt);
    return "";
  }, [job, stops, isOrionDirect, isLali]);

  const [pickupStart, setPickupStart] = useState(job.pickupStartTime || defaultPickupStart);
  // Is the leave-home time actually the OWNER's, or just a seed? job
  // .pickupStartTime means they saved it; otherwise the box was filled from
  // the guard's start stamp / the schedule, which is meaningless on a vault
  // run that never went near the airport. Auto-calc back-derives over a seed
  // but never over a real entry — see handleAutoMileage.
  const [pickupStartTouched, setPickupStartTouched] = useState(!!job.pickupStartTime);
  // Use this from the UI so typing marks the value as owner-entered. Internal
  // callers (auto-calc, the vault-delivery setup) use setPickupStart directly.
  const editPickupStart = (v) => { setPickupStartTouched(true); setPickupStart(v); };
  const [stopTimes, setStopTimes] = useState(() =>
    stops.map((s, i) => {
      const existing = s.billingTime || tsToHHMM(s.completedAt) || tsToHHMM(s.noShowAt) || s.scheduledTime || "";
      if (existing) return existing;
      // Orion direct: default "time departed pick-up" to the scheduled time.
      if (isOrionDirect && i === pickupIdx && job.scheduledTime) return tsToHHMM(job.scheduledTime);
      return "";
    })
  );
  // Flight time = the DEPARTING flight. On a round trip the first airport
  // stop is the arrival — the departure is the last one, so seed from there.
  const airportIdx = isRoundtrip
    ? stops.map((s) => s.type).lastIndexOf("airport")
    : stops.findIndex((s) => s.type === "airport");
  const [flightTime, setFlightTime] = useState(job.flightTime || (airportIdx >= 0 ? stopTimes[airportIdx] : ""));
  const [returnTime, setReturnTime] = useState(job.returnTime || "");
  // Run ended at the house instead of the airport — the guard kept a piece.
  // Redirects the return leg's mileage + arrival time in Auto-calc.
  const [returnHome, setReturnHome] = useState(!!job.returnedHome);
  // Orion direct only: "time departed drop off" (form D26). Distinct from
  // returnTime, which for Orion directs holds the auto-derived "arrival
  // back to pickup" (departed drop off + the return drive time).
  const [departedDropoff, setDepartedDropoff] = useState(job.departedDropoffTime || "");
  // Orion direct only: raw text while the owner is typing in the travel-time
  // field. null = not editing, show the derived value. Cleared on blur.
  const [travelMinEdit, setTravelMinEdit] = useState(null);

  // ── Mileage per segment: [start→stop0, stop0→stop1, ..., stop[N-1]→airport, airport→firstPickup (return)] ──
  // Length = stops.length + 1. The last element is the return leg.
  const initialSegments = Array.isArray(job.mileageSegments) && job.mileageSegments.length === stops.length + 1
    ? job.mileageSegments.map((n) => (n == null ? "" : String(n)))
    : Array.from({ length: stops.length + 1 }, () => "");
  const [segments, setSegments] = useState(initialSegments);
  // Google drive seconds per leg, same indexing as `segments`. Filled by
  // Auto-calc mileage; null until then (and for legs Maps couldn't route).
  // Display-only — drives the per-leg / total drive-time indicators.
  const [segmentSeconds, setSegmentSeconds] = useState(() =>
    Array.from({ length: stops.length + 1 }, () => null)
  );
  // Per-leg pace (seconds per mile) captured from the last miles↔seconds
  // pair (i.e. Google's Auto-calc data), so a manual miles edit can
  // rescale that leg's 🧭 drive-minute estimate instead of blanking it.
  const legSecPerMiRef = useRef({});

  // Which stops are billable. Defaults to ALL true (including noShow stops —
  // in most cases the guard drove there before being told it was scheduled in
  // error, so the leg is still billable). Owner can uncheck to exclude.
  const initialBillable = Array.isArray(job.stopBillable) && job.stopBillable.length === stops.length
    ? job.stopBillable
    : stops.map(() => true);
  const [stopBillable, setStopBillable] = useState(initialBillable);
  // First stop that's actually billed. On a home-based run the drive out
  // from the house is stored in segments[0] regardless of which stop it
  // reaches, so the route table needs to know which row that is.
  const firstBilledIdx = stops.findIndex((s, i) => stopBillable[i] !== false);
  // True when the route actually visits a billed airport — i.e. the piece
  // flies on this job. Drives the outbound return rule: even on a
  // home-based (vault) outbound, once it's flown you're paid through
  // flight + 15 min + drive home, not the plain drive-home cumulative.
  // A vault PICKUP (airport unchecked, didn't fly) has none, so it falls
  // back to the home-based cumulative return.
  const hasBilledAirport = stops.some(
    (s, i) => s.type === "airport" && stopBillable[i] !== false
  );
  // The piece flies OUT on this job only when it's not an inbound. On an
  // inbound the billed airport is the PICKUP — the flight already landed,
  // so the day ends after the last delivery + drive home, not at
  // flight + 15 min. Keying the return rule on hasBilledAirport alone
  // wrongly applied the outbound rule to home-based inbounds (return
  // computed BEFORE the delivery even happened).
  const fliesOut = hasBilledAirport && job.jobType !== "inbound";

  // Prefer rawHours (what the owner typed/computed at save time) over
  // hoursCharged (which gets bumped to the broker minimum). When you
  // reopen a draft, the field should show what you previously entered,
  // not the min-applied billed value.
  // Only restore a saved Hours value if the owner actually HAND-TYPED it last
  // time (stored.hoursManual). A value that was just auto-tracked from the
  // stop times shouldn't be frozen on reopen — it should keep following the
  // live elapsed time. Otherwise every reopen showed a stale number until you
  // hit "↺ Auto". (Jobs billed before this flag existed have no hoursManual →
  // they open in Auto, which is the correct live value anyway.)
  const hoursWasManual = stored.hoursManual === true;
  const [hours, setHours] = useState(
    hoursWasManual
      ? (stored.rawHours != null ? String(stored.rawHours)
        : stored.hoursCharged != null ? String(stored.hoursCharged) : "")
      : ""
  );
  // True once the owner types into the Hours field — then stop auto-tracking
  // derivedHours. The reset button clears this so the field follows the live
  // elapsed time again. Starts true only when the saved hours were manual.
  const [hoursTouched, setHoursTouched] = useState(hoursWasManual);

  // Per-guard end-time overrides for split-job scenarios. When 2+ guards
  // start together and one splits off early (e.g. one stays with the
  // route to a far stop while the other heads back to the airport),
  // the leaving guard's hours are shorter than the route end. Default
  // is empty (= use the job's overall returnTime → all guards billed
  // the same hours, current behavior). Seeded from any per-guard
  // endTime already stored on guardAssignments.
  // Guard count is EDITABLE here (state, seeded from the job) — even after
  // the job was approved and invoiced, so a missed rider can be added
  // later. A new slot has no assignment, so guardOwnsCar defaults it to
  // passenger (G2/G4 ride along) → billed hourly only, NO mileage.
  // Approving writes the count back to the job and rebuilds the per-guard
  // draft invoices (createInvoicesForJob wipes stale drafts first).
  const [numGuards, setNumGuards] = useState(Math.max(1, Number(job.numGuards) || 1));
  // Per-guard overrides, one entry per slot. Each is blank by default and
  // means "same as the job": start falls back to the billing start, end to
  // the route end. hours is a direct entry for when the owner knows the
  // number but not the clock times — e.g. a guard approved partway through
  // whose start time is the only thing that differs.
  const readGuardField = (field) => {
    const arr = Array.from({ length: numGuards }, () => "");
    (job.guardAssignments || []).forEach((g) => {
      const idx = (Number(g.guardNumber) || 0) - 1;
      if (idx >= 0 && idx < arr.length && g[field]) arr[idx] = String(g[field]);
    });
    return arr;
  };
  const [guardEndTimes, setGuardEndTimes] = useState(() => readGuardField("endTime"));
  const [guardStartTimes, setGuardStartTimes] = useState(() => readGuardField("startTime"));
  const [guardHoursEntry, setGuardHoursEntry] = useState(() => readGuardField("hoursOverride"));
  // Typing a clock time hands control back to the clock; typing hours pins
  // the number. Only one of the two can be authoritative at a time, so each
  // setter clears the other rather than letting them silently disagree.
  const setGuardStart = (i, v) => {
    setGuardStartTimes((arr) => arr.map((vv, j) => (j === i ? v : vv)));
    setGuardHoursEntry((arr) => arr.map((vv, j) => (j === i ? "" : vv)));
  };
  const setGuardEnd = (i, v) => {
    setGuardEndTimes((arr) => arr.map((vv, j) => (j === i ? v : vv)));
    setGuardHoursEntry((arr) => arr.map((vv, j) => (j === i ? "" : vv)));
  };
  const setGuardHours = (i, v) => {
    setGuardHoursEntry((arr) => arr.map((vv, j) => (j === i ? v : vv)));
  };
  function changeNumGuards(next) {
    const n = Math.max(1, Math.min(4, Number(next) || 1));
    setNumGuards(n);
    // Keep every per-guard override aligned with the slot count
    // (computeJobBilling only honors guardHours when the lengths match).
    const resize = (arr) => {
      const out = arr.slice(0, n);
      while (out.length < n) out.push("");
      return out;
    };
    setGuardEndTimes(resize);
    setGuardStartTimes(resize);
    setGuardHoursEntry(resize);
  }
  // Per-guard hours. Drives the per-guard line items and the total in the
  // billing preview, and is what's shown as each guard's hours hint.
  // Two cases per guard:
  //   1. A per-guard end-time SPLIT (guardEndTimes[i] set) → compute that
  //      guard's hours straight from pickupStart → their split end time.
  //   2. No split → follow the Hours field the owner typed, so editing
  //      Hours live-updates every guard's line item and the total. (The
  //      auto-track effect keeps Hours in sync with the time-derived value
  //      until the owner types, so this still mirrors the stop times when
  //      untouched.) When Hours is empty (e.g. before auto-calc fills it),
  //      fall back to the time-derived hours (pickupStart → returnTime).
  // Min hours is applied per guard either way.
  //
  // Why this matters: previously every guard's hours came purely from the
  // stop times, so typing a number into Hours moved neither the preview
  // line items nor billing.total (and on a single-guard job it even
  // showed a bogus "G1 split" line). The saved invoice already used the
  // typed hours, so the preview and the saved billedAmount disagreed.
  const minHours = Number(broker?.minHours) || 0;
  const typedHours = hours !== "" && Number.isFinite(Number(hours)) ? Number(hours) : null;

  // ── Relieved-at-the-airport end time (Malca, effective 2026-08-24) ──
  // Per Orion/Malca: "Only 1 officer/guard is to stay at the airport until
  // 15 minutes after wheels up. If you are on a 2 person outbound, one
  // person is relieved when you get to the airport."
  //
  // So on a multi-guard OUTBOUND the guards split at the airport: G1 waits
  // for wheels-up + 15 and then drives back (the existing route end), while
  // everyone else is released on arrival and bills only the drive back to
  // the first pickup. Computed rather than typed — that subtraction is
  // exactly what the owner shouldn't have to do by hand.
  // When the run reached the airport. Also what a relieved guard hands the
  // packages over at, so it's the delivery time on their sheet — they leave
  // on arrival rather than waiting for wheels-up.
  const airportArrivalTime = (() => {
    if (!isOutbound || numGuards < 2) return "";
    const airportIdx = stops.map((s, i) => ({ s, i })).filter(({ s }) => s.type === "airport").pop()?.i;
    if (airportIdx == null) return "";
    // When did they reach the airport? NOT the airport stop's stored time —
    // an outbound hides that box entirely ("see flight time ↓"), so it still
    // holds whatever the guard's app stamped, often a batch-completion time
    // hours off the real run. Derive it instead: last billed pickup + the
    // drive into the airport, both of which the owner can see and correct.
    const legIntoAirportSec = segmentSeconds[airportIdx];
    if (legIntoAirportSec == null) return "";
    let arriveMin = null;
    for (let i = airportIdx - 1; i >= 0; i--) {
      if (stopBillable[i] === false) continue;
      const m = minutesOf(stopTimes[i]);
      if (m != null) { arriveMin = m + Math.round(legIntoAirportSec / 60); break; }
    }
    if (arriveMin == null) return "";
    return toHHMM(arriveMin);
  })();
  // A relieved guard's day ends back at the first pickup — same drive back
  // the route return uses, just started on arrival instead of after the plane.
  const relievedEndTime = (() => {
    if (!airportArrivalTime) return "";
    const backSec = segmentSeconds[stops.length];
    if (backSec == null) return "";
    return toHHMM(minutesOf(airportArrivalTime) + Math.round(backSec / 60));
  })();
  // Which end time a guard falls back to when the owner hasn't typed one.
  // G1 is the one who stays; the rest are relieved on arrival.
  const defaultEndFor = (i) => (i > 0 && relievedEndTime ? relievedEndTime : returnTime);
  // Relieved guards hand over on arrival, so that's their delivery time —
  // not wheels-up + 15, which is only true for whoever stayed.
  const deliveryTimeFor = (i) => (i > 0 && relievedEndTime ? airportArrivalTime : "");

  const guardHoursComputed = guardEndTimes.map((et, i) => {
    // Case 0: hours typed straight in for this guard. Beats the clock — the
    // owner knew the number without knowing both times.
    const he = guardHoursEntry[i];
    if (he !== "" && Number.isFinite(Number(he))) {
      return Math.max(Number(he), minHours);
    }
    // A per-guard start (e.g. G2 not approved until 07:30) shifts only that
    // guard's clock; blank means they started with the job.
    const st = guardStartTimes[i];
    const startMin = minutesOf(st || pickupStart);
    // Case 1: an explicit per-guard start or end wins. A start with no end
    // runs to this guard's default end — the route end for the guard who
    // waits for wheels-up, the relieved-at-the-airport time for the others.
    if (et || st) {
      const endMin = minutesOf(et || defaultEndFor(i));
      if (startMin != null && endMin != null) {
        const wrapped = endMin < startMin ? endMin + 24 * 60 : endMin;
        return Math.max((wrapped - startMin) / 60, minHours);
      }
      // Unparseable split → fall through to the typed/derived hours below.
    }
    // Case 2: no split → the Hours field the owner typed is authoritative.
    // A relieved guard is the exception: their shorter day is a billing rule,
    // not an override, so the job-level Hours must not stretch them back out.
    if (typedHours != null && !(i > 0 && relievedEndTime)) return Math.max(typedHours, minHours);
    // Fallback: time-derived (pickupStart → this guard's end).
    const endMin = minutesOf(defaultEndFor(i));
    if (startMin == null || endMin == null) return null;
    const wrapped = endMin < startMin ? endMin + 24 * 60 : endMin;
    return Math.max((wrapped - startMin) / 60, minHours);
  });

  // Build the guardAssignments array we write to firestore. We can't just
  // map existing entries because an unassigned slot has no entry at all —
  // and the per-guard endTime override needs to survive even when nobody's
  // been picked yet (common workflow: bill the job before assigning G2's
  // pool grab). For each slot 1..numGuards, reuse the existing assignment
  // if present and stamp the current endTime override; otherwise synthesize
  // a stub entry to hold the override.
  function buildAssignmentsWithEndTimes() {
    const existing = job.guardAssignments || [];
    const out = [];
    for (let n = 1; n <= numGuards; n++) {
      const idx = n - 1;
      const et = (idx < guardEndTimes.length ? guardEndTimes[idx] : "") || "";
      const st = (idx < guardStartTimes.length ? guardStartTimes[idx] : "") || "";
      const ho = (idx < guardHoursEntry.length ? guardHoursEntry[idx] : "") || "";
      // Stamp the per-guard billed miles + per-leg segments (derived from the
      // per-stop coverage) so the invoices + worksheets bill each car for only
      // the legs it drove.
      const milesNum = idx < guardMilesEffective.length ? guardMilesEffective[idx] : 0;
      const segsArr = idx < guardSegmentsByNum.length ? guardSegmentsByNum[idx].map((m) => Math.ceil(m)) : null;
      const milesPatch = milesNum > 0 ? { billedMiles: milesNum, billedSegments: segsArr } : {};
      // What this guard ACTUALLY bills, resolved here once. endTime/startTime
      // hold only what the owner typed, so a guard on a computed default (a
      // relieved second guard on an outbound) looks blank to everything
      // downstream — which is how the workbook came out carrying G1's times
      // on G2's sheet. Stamp the resolved values so the invoice and the Malca
      // sheet render the same day the review panel shows.
      const billedPatch = {
        billedStartTime: st || pickupStart || "",
        billedEndTime: et || defaultEndFor(idx) || "",
        billedHours: idx < guardHoursComputed.length && guardHoursComputed[idx] != null
          ? Math.round(guardHoursComputed[idx] * 100) / 100
          : null,
        // Blank for the guard who stayed — their sheet keeps wheels-up + 15.
        billedDeliveryTime: deliveryTimeFor(idx),
      };
      const existingEntry = existing.find((g) => g.guardNumber === n);
      if (existingEntry) {
        out.push({ ...existingEntry, endTime: et, startTime: st, hoursOverride: ho, ...billedPatch, ...milesPatch });
      } else if (et || st || ho || billedPatch.billedHours != null) {
        out.push({
          guardNumber: n,
          guardId: "",
          guardName: "",
          payRate: "",
          endTime: et,
          startTime: st,
          hoursOverride: ho,
          ...billedPatch,
          ...milesPatch,
        });
      }
    }
    // Defensive: keep any out-of-range entries (e.g. orphaned from a prior
    // numGuards=4 → 2 shrink) so a save doesn't silently delete data.
    existing.forEach((g) => {
      const n = Number(g.guardNumber);
      if (!Number.isInteger(n) || n < 1 || n > numGuards) out.push(g);
    });
    return out;
  }

  // Recall: did this destination ever get billed for the same broker
  // before? If yes, surface the previous hours/miles as a hint and let
  // the owner one-click pre-fill. Only meaningful for Ferrari/Executive
  // (the recall stamping in handleApprove only writes for those).
  // nearbyRecall is the same-town fallback: a first delivery to a NEW
  // Mount Pleasant address has no history of its own, but the drive is
  // basically every other Mount Pleasant run — so pool the samples from
  // the other saved locations in the destination's city.
  const [recall, setRecall] = useState(null);
  const [nearbyRecall, setNearbyRecall] = useState(null);
  useEffect(() => {
    if (!isFerrariStyle) return;
    const realStops = stops.filter((s) => s.type !== "airport");
    const destinationStop = realStops[realStops.length - 1];
    if (!destinationStop) return;
    let cancelled = false;
    const brokerKey = String(broker?.name || job.brokerName || "")
      .toLowerCase()
      .includes("ferrari") ? "ferrari" : "executive";

    // Exact-destination history (needs the stop linked to a saved location).
    if (destinationStop.locationId) {
      getDoc(doc(db, "locations", destinationStop.locationId))
        .then((snap) => {
          if (cancelled || !snap.exists()) return;
          const entry = (snap.data().brokerStats || {})[brokerKey];
          const samples = recallSamplesOf(entry);
          if (!samples.length) return;
          setRecall({
            lastHours: entry.lastHours != null ? Number(entry.lastHours) : null,
            lastMiles: entry.lastMiles != null ? Number(entry.lastMiles) : null,
            usualHours: usualFromSamples(samples, "hours", 2),
            usualMiles: usualFromSamples(samples, "miles", 0),
            count: samples.length,
            destinationName: destinationStop.locationName || destinationStop.address || "this destination",
          });
        })
        .catch(() => {});
    }

    // Same-town history — OTHER saved locations in the destination's city
    // (the exact destination is excluded; its history shows above).
    const city = cityFromAddress(destinationStop.address);
    if (city && job.companyId) {
      getLocations(job.companyId)
        .then((locs) => {
          if (cancelled) return;
          const cityNorm = city.toLowerCase();
          let pooled = [];
          let locCount = 0;
          for (const l of locs) {
            if (destinationStop.locationId && l.id === destinationStop.locationId) continue;
            if ((cityFromAddress(l.address) || "").toLowerCase() !== cityNorm) continue;
            const s = recallSamplesOf((l.brokerStats || {})[brokerKey]);
            if (!s.length) continue;
            pooled = pooled.concat(s);
            locCount += 1;
          }
          if (pooled.length) {
            setNearbyRecall({
              city,
              count: pooled.length,
              locCount,
              usualHours: usualFromSamples(pooled, "hours", 2),
              usualMiles: usualFromSamples(pooled, "miles", 0),
            });
          }
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  // Run once on mount per (job, broker, isFerrariStyle).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill hours + mileage from a recalled value (average or last).
  // Hours go straight into the Hours field (marking it touched so the
  // auto-track effect doesn't overwrite it); miles go into segments[0]
  // (the dispatch leg) and clear the other legs so the total doesn't
  // double up. Miles are rounded to a whole number per Orion FAQ.
  function applyRecall(h, m) {
    if (h != null && Number.isFinite(Number(h))) {
      setHours(String(Number(h).toFixed(2)));
      setHoursTouched(true);
    }
    if (m != null && Number.isFinite(Number(m))) {
      setSegments((prev) => {
        const next = [...prev];
        next[0] = String(Math.round(Number(m)));
        for (let i = 1; i < next.length; i++) next[i] = "";
        return next;
      });
    }
  }

  const [expenses, setExpenses] = useState(stored.expenses != null ? String(stored.expenses) : "");
  // Itemized expense lines: { id, label, amount, roundTrip }
  // Migrated from the single-number `expenses` if there are no items saved.
  // Pieces vaulted off this job onto a later run. Held in state so the
  // panel updates the moment a move lands, without waiting for the job
  // doc to round-trip.
  const [stampedAway, setVaultedAway] = useState(
    () => (Array.isArray(job.vaultedAway) ? job.vaultedAway : [])
  );

  // The breadcrumb we stamp at move time can be missing — pieces moved
  // before it existed, or moved via JobDetailModal's "Move packages…",
  // which stamps provenance on the DESTINATION only. So derive the truth
  // from where the pieces actually landed: any job carrying a package
  // whose vaultedFromJobId points back here. allJobs is already in memory
  // from watchJobs, so this costs no extra reads, and it self-heals — move
  // a piece back and the indicator disappears on its own.
  const vaultedAway = useMemo(() => {
    const normAddr = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
    const srcStops = job.stops || [];
    const derived = [];
    (allJobs || []).forEach((j) => {
      if (j.id === job.id) return;
      (j.packages || []).forEach((p) => {
        if (!p?.wasVaulted || p.vaultedFromJobId !== job.id) return;
        // The package's stopId now points at the DESTINATION's stop, so map
        // back to the stop on this job: explicit stamp first, else match the
        // destination stop's saved location / address against ours.
        let srcStopId = p.vaultedFromStopId || "";
        if (!srcStopId) {
          const dstStop = (j.stops || []).find((s) => s.id === p.stopId);
          const hit = srcStops.find((s) =>
            s.id === p.stopId
            || (dstStop?.locationId && s.locationId === dstStop.locationId)
            || (dstStop && normAddr(s.address) && normAddr(s.address) === normAddr(dstStop.address))
          );
          srcStopId = hit?.id || "";
        }
        derived.push({
          packageId:    p.id,
          hawbNumber:   p.hawbNumber || "",
          stopId:       srcStopId,
          toJobId:      j.id,
          toJobNumber:  j.jobNumber || "",
          deliveryDate: j.scheduledDate || "",
          nights:       p.vaultedNights || 1,
        });
      });
    });
    // Derived wins; keep any stamped record we couldn't corroborate (the
    // destination may not have reached this client's snapshot yet).
    const seen = new Set(derived.map((v) => v.packageId));
    return [...derived, ...stampedAway.filter((v) => !seen.has(v.packageId))];
  }, [allJobs, job.id, job.stops, stampedAway]);

  const vaultedAwayAt = (stopId) => vaultedAway.filter((v) => v.stopId === stopId);
  // Did this run keep a piece? Drives whether the "returned to my house"
  // option is worth offering.
  const hasHeldOrVaulted = (job.packages || []).some((p) => p?.held || p?.wasVaulted)
    || vaultedAway.length > 0;

  // Explain the missing POD in the broker-facing notes. Covers pieces moved
  // before the move handler started writing this line, and moves made from
  // the job detail screen. Fills the textarea only — nothing reaches Malca
  // until the owner saves or approves, so the line stays editable.
  useEffect(() => {
    if (!vaultedAway.length) return;
    setNotes((prev) => {
      const lines = vaultedAway
        .map((v) => `${v.hawbNumber || "Piece"} held in vault, delivered on ${v.toJobNumber || "a later job"}${v.deliveryDate ? ` ${v.deliveryDate}` : ""}.`)
        .filter((line) => !(prev || "").includes(line));
      if (!lines.length) return prev;
      return `${prev ? `${prev.trimEnd()}\n` : ""}${lines.join("\n")}`;
    });
  }, [vaultedAway]);

  const [expenseItems, setExpenseItems] = useState(() => {
    const existing = Array.isArray(job.expenseItems) ? job.expenseItems : null;
    if (existing && existing.length) {
      return existing.map((it, i) => ({
        id: it.id || `exp_${i}_${Math.random().toString(36).slice(2, 6)}`,
        label: it.label || "",
        amount: it.amount != null ? String(it.amount) : "",
        roundTrip: !!it.roundTrip,
        receiptPhotoUrl: it.receiptPhotoUrl || "",
      }));
    }
    if (Number(job.expenses) > 0) {
      return [{ id: "exp_seed", label: "Expenses", amount: String(job.expenses), roundTrip: false, receiptPhotoUrl: "" }];
    }
    return [];
  });
  // Per-expense upload state — keyed by expense id so multiple uploads
  // can run independently without one blocking the others.
  const [uploadingReceiptIds, setUploadingReceiptIds] = useState(new Set());
  async function handleReceiptUpload(expenseId, file) {
    if (!file) return;
    setUploadingReceiptIds((prev) => {
      const next = new Set(prev); next.add(expenseId); return next;
    });
    try {
      const url = await uploadExpenseReceipt(file, job.id, "owner");
      updateExpenseItem(expenseId, { receiptPhotoUrl: url });
    } catch (err) {
      console.error("Receipt upload failed", err);
      alert("Receipt upload failed: " + (err?.message || err));
    } finally {
      setUploadingReceiptIds((prev) => {
        const next = new Set(prev); next.delete(expenseId); return next;
      });
    }
  }
  // Computed total — round-trip items count double per Orion FAQ
  // (tolls between pickup and dropoff are reimbursed both ways).
  const expenseItemsTotal = expenseItems.reduce(
    (sum, it) => sum + (Number(it.amount) || 0) * (it.roundTrip ? 2 : 1),
    0
  );
  function addExpenseItem() {
    setExpenseItems((arr) => [
      ...arr,
      { id: `exp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label: "", amount: "", roundTrip: false, receiptPhotoUrl: "" },
    ]);
  }
  function updateExpenseItem(id, patch) {
    setExpenseItems((arr) => arr.map((it) => {
      if (it.id !== id) return it;
      const next = { ...it, ...patch };
      // Auto-flag round-trip when the user types "toll" — common case per FAQ.
      if (patch.label !== undefined) {
        const isToll = /toll/i.test(patch.label);
        if (isToll && !it.label.match(/toll/i)) next.roundTrip = true;
      }
      return next;
    }));
  }
  function removeExpenseItem(id) {
    setExpenseItems((arr) => arr.filter((it) => it.id !== id));
  }
  const [useFlat, setUseFlat] = useState(stored.flatFee > 0);
  // Per-job flat-fee override. Lets the owner bump a one-off DAI run
  // to a higher rate without changing the broker's rate card. Empty
  // string = use broker default. The override flows through
  // computeJobBilling via overrides.flatFeeOverride, and persists on
  // the job doc as job.flatFeeOverride so it survives modal close.
  const [flatFeeOverride, setFlatFeeOverride] = useState(
    job.flatFeeOverride != null ? String(job.flatFeeOverride) : ""
  );
  const [notes, setNotes] = useState(job.billingNotes || "");
  const [saving, setSaving] = useState(false);
  const [computingMaps, setComputingMaps] = useState(false);
  const [mapsMsg, setMapsMsg] = useState("");
  // Drive duration (in seconds) for the return leg; kept so flight-time edits
  // can recompute return arrival without another Maps call.
  const [returnDriveSeconds, setReturnDriveSeconds] = useState(job.returnDriveSeconds || null);
  // Home-based on-site dwell at the pickup (minutes). Sits between the
  // pickup arrival and the drive home, so leave-home → return-home (and
  // the billed hours that follow it) include the time spent loading at
  // the pickup. Only used when billing from home.
  const [pickupDwell, setPickupDwell] = useState(
    job.pickupDwellMin != null ? String(job.pickupDwellMin) : ""
  );
  // Latest billed stop's clock time — for a vault run that's the single
  // pickup. The return-home time derives from it + dwell + drive home.
  function lastBilledStopMinutes() {
    for (let i = stopTimes.length - 1; i >= 0; i--) {
      if (stopBillable[i] !== false) {
        const m = minutesOf(stopTimes[i]);
        if (m != null) return m;
      }
    }
    return null;
  }
  // Owner types the on-site time → recompute return home live (no Maps
  // call) from the pickup arrival + dwell + the stored drive-home leg.
  function applyPickupDwell(raw) {
    setPickupDwell(raw);
    if (!homeBased) return;
    const dwell = raw === "" ? 0 : Math.round(Number(raw));
    if (!Number.isFinite(dwell) || dwell < 0) return;
    const arr = lastBilledStopMinutes();
    if (arr == null || returnDriveSeconds == null) return;
    setReturnTime(toHHMM(arr + dwell + Math.round(returnDriveSeconds / 60)));
  }

  // Totalling rules (consistent across all job types now):
  //   segments[0]                → starting mileage (always counted —
  //                                we drove to the first stop even if it
  //                                later turned into a no-show)
  //   segments[1..N-1]           → leg arriving at stop[idx]. Excluded
  //                                if that stop is unchecked.
  //   segments[stops.length]     → return leg (always counted)
  const totalMiles = segments.reduce((sum, v, idx) => {
    const miles = Number(v) || 0;
    if (idx === 0) return sum + miles;
    if (idx === segments.length - 1) return sum + miles;
    // segments[idx] is the leg arriving at stop[idx]. Skip if that stop
    // was unchecked as non-billable.
    if (stopBillable[idx] === false) return sum;
    return sum + miles;
  }, 0);

  // Total Google drive time across all legs — null until Auto-calc has
  // filled at least one leg (the indicator hides itself before then).
  const totalDriveSec = segmentSeconds.some((s) => s != null)
    ? segmentSeconds.reduce((sum, s) => sum + (s || 0), 0)
    : null;

  // Compute charged hours from pickupStart → returnTime (or latest stop time)
  // if owner hasn't overridden. When the end time is earlier than the start
  // (e.g. return at 00:21 the next morning), wrap +24h so a late-night
  // landing still computes a positive duration.
  // Fall back to the last BILLED stop (not a skipped/unbilled one) when
  // there's no return time yet, so a no-show delivery can't inflate hours.
  // An end EQUAL to the start isn't a full day: it's a job with only its
  // first time filled in (an emailed Ferrari job with just the CLT arrival
  // was billing 24h). It derives no hours, so the broker minimum applies.
  const endMinutesRaw = minutesOf(returnTime) ?? lastBilledStopMinutes() ?? null;
  const startMinutes = minutesOf(pickupStart);
  const endMinutes = (startMinutes != null && endMinutesRaw != null && endMinutesRaw < startMinutes)
    ? endMinutesRaw + 24 * 60
    : endMinutesRaw;
  const derivedHours = startMinutes != null && endMinutes != null && endMinutes > startMinutes
    ? Math.round(((endMinutes - startMinutes) / 60) * 100) / 100
    : null;

  // ── Orion direct timeline (pickup → drop off → back to pickup) ──
  // Mirrors the Orion contractor form's auto-calc:
  //   travel time   = arrival at drop off − departed pickup           (form D23)
  //   arrival back  = departed drop off + travel time                 (form D27)
  //   billed hours  = arrival back − job started                      (form D38)
  // pickupStart = "job started" (D21); stopTimes[pickupIdx] = "departed
  // pickup" (D22); stopTimes[dropoffIdx] = "arrival at drop off" (D25);
  // returnTime = "departed drop off" (D26, what the workbook reads).
  // One-way travel time = arrival at drop off − departed pickup (form D23).
  const orionTravelMin = (() => {
    if (!isOrionDirect) return null;
    const dep = minutesOf(stopTimes[pickupIdx]);
    let arr = minutesOf(stopTimes[dropoffIdx]);
    if (dep == null || arr == null) return null;
    if (arr < dep) arr += 24 * 60;
    return arr - dep;
  })();
  // For Orion directs, returnTime IS the arrival-back-to-pickup time, kept
  // in sync by the effects below — so the standard derivedHours
  // (returnTime − pickupStart) is already the correct billed time. No
  // special-case needed.
  // A cancelled trip (guard showed up, flight scrubbed) is just the broker
  // minimum — don't bill the leftover elapsed time from the old schedule.
  // Owner can still type a different number or add the airport mileage.
  const effectiveDerivedHours = job.tripCancelled && minHours > 0 ? minHours : derivedHours;

  // Orion: default "departed drop off" to 5 min after the drop-off arrival
  // when it hasn't been set yet (matches the form convention).
  useEffect(() => {
    if (!isOrionDirect) return;
    const arr = minutesOf(stopTimes[dropoffIdx]);
    if (arr != null && !departedDropoff) {
      // LALI counts no waiting → departed = arrival. Others keep the 5-min dwell.
      setDepartedDropoff(toHHMM(arr + (isLali ? 0 : 5)));
    }
  }, [isOrionDirect, isLali, stopTimes, dropoffIdx, departedDropoff]);

  // Orion: keep "arrival back to pickup" (returnTime) = departed drop off +
  // the same one-way travel time. This is the round-trip return drive the
  // earlier version was missing.
  useEffect(() => {
    if (!isOrionDirect) return;
    const dd = minutesOf(departedDropoff);
    if (dd == null || orionTravelMin == null) return;
    const back = toHHMM(dd + orionTravelMin);
    setReturnTime((prev) => (prev === back ? prev : back));
  }, [isOrionDirect, departedDropoff, orionTravelMin]);

  // Auto-track: when the owner edits times (pickupStart, stopTimes, returnTime,
  // flightTime), derivedHours updates and we follow it in the Hours field so
  // the total recalculates. As soon as they type into Hours directly, hoursTouched
  // flips to true and we stop overriding their manual value.
  useEffect(() => {
    if (hoursTouched) return;
    if (effectiveDerivedHours == null) return;
    const formatted = effectiveDerivedHours.toFixed(2);
    setHours((prev) => (prev === formatted ? prev : formatted));
  }, [effectiveDerivedHours, hoursTouched]);

  // Live recompute billing — numGuards comes from the job and scales the total.
  // Per Orion FAQ: whole-number mileage. Ceil what we feed the bill so the
  // displayed total matches what the workbook will show after rounding.
  // When itemized expenses exist, they take precedence over the legacy
  // single-number `expenses` field. Round-trip items contribute 2x.
  //
  // Deleting the last item has to mean ZERO. The legacy number is read from
  // job.billingBreakdown, which is a different source than the item list
  // (job.expenseItems / job.expenses) — so on any job that has ever been
  // itemized, falling back to it resurrects the expense the owner just
  // removed and the total never drops. Only trust the legacy number on a job
  // that never had items to begin with; there, the seed above leaves the
  // list empty and the stored number is the only record.
  const expensesItemized = Array.isArray(job.expenseItems) || Number(job.expenses) > 0;
  const effectiveExpenses = expenseItems.length > 0
    ? Math.round(expenseItemsTotal * 100) / 100
    : (expensesItemized ? 0 : (expenses === "" ? 0 : Number(expenses)));
  // Effective per-guard billed miles, DERIVED from which stops each car
  // covered (the per-stop guard chips on the route below). Own-car guards
  // bill the legs into the stops they're on, plus the return if they ran to
  // the end; a guard on every stop bills the full route. Passengers bill 0.
  // Feeds the preview total and each guard's invoice/worksheet.
  const fullMiles = Math.ceil(totalMiles);
  const rawSeg = (idx) => Number(segments[idx]) || 0;
  // Per-guard billed SEGMENTS — one value per route leg, 0 for legs that car
  // didn't drive. This (not a proportional scale) is what each car's
  // worksheet shows and what its miles sum to, so a peel-off car shows
  // "airport→first 46, return 46" rather than smearing 92 across every leg.
  // Layout matches mileageSegments: [dispatch→stop0, stop0→stop1, …, return].
  function buildGuardSegments(gNum, a) {
    const n = stops.length;
    const segs = new Array(n + 1).fill(0);
    if (!guardOwnsCar(a, gNum) || n === 0) return segs; // passenger → no mileage
    if (stops.every((s) => stopCoveredByGuard(s, gNum))) {
      for (let k = 0; k <= n; k++) segs[k] = rawSeg(k); // full route
      return segs;
    }
    let lastCoveredIdx = -1;
    for (let k = 0; k < n; k++) {
      if (stopCoveredByGuard(stops[k], gNum)) { segs[k] = rawSeg(k); lastCoveredIdx = k; } // leg INTO stop k
    }
    if (lastCoveredIdx < 0) return segs;
    // Return drive back to the start/endpoint, placed in the return cell.
    //   • Ran to the route's end → the real route return leg.
    //   • Peeled off at the AIRPORT itself (e.g. helped load, then went
    //     home) → the drive back mirrors the drive out: the leg INTO the
    //     airport, NOT the route's return leg — that one runs from the far
    //     end of a route this car never drove (a CLT load-out guard was
    //     billing the Bluffton→home leg).
    //   • Last stop right after the airport (inbound first delivery) → that
    //     airport↔stop leg IS the drive back (exact).
    //   • Deeper peel-off → the route's return leg as the back-drive proxy.
    if (lastCoveredIdx === n - 1) segs[n] = rawSeg(n);
    else if (stops[lastCoveredIdx].type === "airport") segs[n] = rawSeg(lastCoveredIdx);
    else if (stops[lastCoveredIdx - 1] && stops[lastCoveredIdx - 1].type === "airport") segs[n] = rawSeg(lastCoveredIdx);
    else segs[n] = rawSeg(n);
    return segs;
  }
  const guardSegmentsByNum = Array.from({ length: numGuards }, (_, i) => {
    const gNum = i + 1;
    const a = (job.guardAssignments || []).find((g) => g.guardNumber === gNum);
    return buildGuardSegments(gNum, a);
  });
  const guardMilesEffective = guardSegmentsByNum.map((segs) => segs.reduce((s, m) => s + Math.ceil(m), 0));
  // Human-readable per-leg breakdown for each car (shown in the review so the
  // owner can see exactly how a car's mileage was built). e.g.
  // "CLT→JC Penney 46 · return 46 = 92 mi".
  const guardMilesBreakdown = guardSegmentsByNum.map((segs) => {
    const n = stops.length;
    const parts = [];
    for (let k = 0; k <= n; k++) {
      const v = Math.ceil(segs[k] || 0);
      if (v <= 0) continue;
      const label = k === 0
        ? `start→${stops[0]?.locationName || "stop 1"}`
        : k === n
          ? "return"
          : `${stops[k - 1]?.locationName || `stop ${k}`}→${stops[k]?.locationName || `stop ${k + 1}`}`;
      parts.push(`${label} ${v}`);
    }
    return parts.join(" · ");
  });
  // Toggle an own-car guard on/off a stop for BILLING (which cars drove that
  // leg). Writes the same stop.guardNumbers the phone uses; keeps ≥1 guard.
  // The modal holds a STATIC job snapshot, so we mutate job.stops and bounce
  // fixSaving to force a re-render (same pattern as toggleStopSkipped) —
  // otherwise the chips never recolor and the miles don't recompute.
  async function toggleStopBillGuard(idx, gNum) {
    const all = Array.from({ length: numGuards }, (_, k) => k + 1);
    const updated = stops.map((s, i) => {
      if (i !== idx) return s;
      const cur = (Array.isArray(s.guardNumbers) && s.guardNumbers.length) ? s.guardNumbers : all;
      const next = cur.includes(gNum) ? cur.filter((x) => x !== gNum) : [...cur, gNum].sort((a, b) => a - b);
      if (next.length === 0) return s; // keep at least one car on the stop
      return { ...s, guardNumbers: next };
    });
    setFixSaving(true);
    try {
      await updateJob(job.id, { stops: updated });
      job.stops = updated;
    } catch (e) {
      console.warn("Couldn't update stop guards", e);
    }
    setFixSaving(false);
  }
  const billing = computeJobBilling(job, broker || {}, {
    hours: hours !== "" ? Number(hours) : (effectiveDerivedHours ?? undefined),
    miles: Math.ceil(totalMiles),
    expenses: effectiveExpenses,
    useFlatFee: useFlat,
    // The modal's editable count, not the job snapshot — so bumping
    // guards live-updates the preview total before anything is saved.
    numGuards,
    guardMiles: guardMilesEffective,
    flatFeeOverride: flatFeeOverride !== "" ? Number(flatFeeOverride) : undefined,
    // Per-guard hours — when any guard has a split end time, the preview
    // breaks Hourly into one line per guard so the displayed total matches
    // what createInvoicesForJob will actually invoice. null entries mean
    // "use the full hours". Floored at broker min inside computeJobBilling.
    guardHours: guardHoursComputed,
  });

  function updateSegment(idx, val) {
    // Keep the 🧭 drive-minute estimate in step with a manual miles edit
    // by rescaling at the leg's known pace (seconds-per-mile from the
    // current miles↔seconds pair, i.e. Google's estimate — or a prior
    // rescale, which preserves the same pace). Legs Google never routed
    // fall back to the route's average pace across the other legs. The
    // number stays an estimate — it just recalculates as you type instead
    // of going blank. Billing math is untouched (drive time is display
    // only; billed hours come from the entered times).
    const prevMiles = Number(segments[idx]);
    const prevSec = segmentSeconds[idx];
    if (prevSec != null && prevMiles > 0) {
      legSecPerMiRef.current[idx] = prevSec / prevMiles;
    }
    let pace = legSecPerMiRef.current[idx];
    if (pace == null) {
      let secSum = 0, miSum = 0;
      segmentSeconds.forEach((sec, i) => {
        const mi = Number(segments[i]);
        if (i !== idx && sec != null && mi > 0) { secSum += sec; miSum += mi; }
      });
      if (miSum > 0) pace = secSum / miSum;
    }
    const newMiles = Number(val);
    const newSec = pace != null && Number.isFinite(newMiles) && newMiles > 0
      ? Math.round(pace * newMiles)
      : null;
    setSegments((s) => s.map((v, i) => i === idx ? val : v));
    setSegmentSeconds((sec) => sec.map((v, i) => (i === idx ? newSec : v)));
  }
  function updateStopTime(idx, val) {
    setStopTimes((t) => t.map((v, i) => i === idx ? val : v));
  }
  function toggleStopBillable(idx) {
    setStopBillable((b) => b.map((v, i) => i === idx ? !v : v));
  }

  // One-click "this is a vault delivery": the pieces are already in hand, so
  // bill from home, drop every airport stop from the route (you didn't fly),
  // and seed a vault fee if there isn't one. Fixes an inbound that was created
  // the regular way (airport billed) — the owner then sets the leave-home time
  // + nights and runs Auto-calc. Already-correct vault jobs (from Move
  // packages) won't show this button.
  function convertToVaultDelivery() {
    setBillFromHome(true);
    setStopBillable(() => stops.map((s, i) => (s.type === "airport" ? false : (stopBillable[i] !== false))));
    // Clear the stale airport-scheduled "leave home" time so Auto-calc can
    // back-derive it from the delivery time (delivery − drive out), and clear
    // the airport's stale "departed pickup" time (you never went there).
    setPickupStart("");
    setStopTimes((arr) => stops.map((s, i) => (s.type === "airport" ? "" : arr[i])));
    setExpenseItems((arr) => (
      arr.some((e) => /vault/i.test(e.label || ""))
        ? arr
        : [...arr, { id: `exp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label: "Overnight vault — 1 night", amount: "100", roundTrip: false, receiptPhotoUrl: "" }]
    ));
    setMapsMsg("Vault delivery — airport dropped, billing from home. Make sure the delivery time is set, then Auto-calc mileage — your leave-home time fills in automatically (delivery − drive). Set the vault-fee nights too.");
  }

  // Orion direct: owner edits the travel time directly. Anchor at "departed
  // pickup" (falling back to the scheduled time), move "arrival at drop off"
  // to departed + travel, and shift "departed drop off" by the same delta so
  // the time spent at the drop off is preserved. The effects above then
  // recompute the return time, billed hours, and pay.
  function applyTravelMinutes(raw) {
    setTravelMinEdit(raw);
    const mins = Math.round(Number(raw));
    if (raw === "" || !Number.isFinite(mins) || mins < 0) return;
    let dep = minutesOf(stopTimes[pickupIdx]);
    if (dep == null) {
      dep = job.scheduledTime ? minutesOf(tsToHHMM(job.scheduledTime)) : null;
      if (dep == null) return;
      updateStopTime(pickupIdx, toHHMM(dep));
    }
    const oldArr = minutesOf(stopTimes[dropoffIdx]);
    const newArr = dep + mins;
    updateStopTime(dropoffIdx, toHHMM(newArr));
    const dd = minutesOf(departedDropoff);
    let dwell = isLali ? 0 : 5; // form convention; LALI counts no waiting
    if (dd != null && oldArr != null) {
      dwell = dd - oldArr;
      if (dwell < 0) dwell += 24 * 60;
    }
    setDepartedDropoff(toHHMM(newArr + dwell));
    setHoursTouched(false); // let the timeline drive the Hours field
  }

  // Orion direct: auto-fill the drop-off arrival from Google drive time,
  // set "departed drop off" to +5 min, and the round-trip mileage. Per the
  // spec: arrival = departed pickup + drive; departed drop off = arrival +
  // 5; arrival back (auto) reuses the same drive time.
  async function handleOrionDriveCalc() {
    setComputingMaps(true);
    setMapsMsg("Computing drive time...");
    try {
      const p = await resolveStopCoords(stops[pickupIdx]);
      const d = await resolveStopCoords(stops[dropoffIdx]);
      if (!p || !d) {
        setMapsMsg("Couldn't resolve pickup/drop-off coordinates. Set the addresses on the job first (📍).");
        setComputingMaps(false);
        return;
      }
      const leg = await driveBetween(p, d);
      if (!leg) {
        setMapsMsg("Google Maps didn't return a route. Enter the times/mileage manually.");
        setComputingMaps(false);
        return;
      }
      const travelMin = Math.max(1, Math.round(leg.seconds / 60));
      const depStr = stopTimes[pickupIdx] || (job.scheduledTime ? tsToHHMM(job.scheduledTime) : "");
      const dep = minutesOf(depStr);
      if (dep != null) {
        const arrive = dep + travelMin;
        setStopTimes((t) => t.map((v, i) => (i === dropoffIdx ? toHHMM(arrive) : v)));
        // Departed drop off = arrival + dwell (5 min, or 0 for LALI which
        // counts no waiting). The effect then derives "arrival back to pickup"
        // = departed drop off + this same travel time (the return drive).
        setDepartedDropoff(toHHMM(arrive + (isLali ? 0 : 5)));
        setHoursTouched(false); // let the timeline drive the Hours field
      }
      // Round-trip mileage: one-way pickup→drop off, billed both ways.
      const oneWay = String(Math.round(leg.miles));
      setSegments((segs) => {
        const next = [...segs];
        next[dropoffIdx] = oneWay;     // leg arriving at the drop off
        next[stops.length] = oneWay;   // the return leg
        return next;
      });
      setMapsMsg(`Drive ${travelMin} min · ${Math.round(leg.miles)} mi one-way (round trip billed). Adjust any time if needed.`);
    } catch (err) {
      setMapsMsg("Couldn't reach Google Maps. " + (err?.message || ""));
      console.error(err);
    }
    setComputingMaps(false);
  }

  // Quick check used by the "needs location" indicator. Doesn't hit the
  // network — only counts coords as known when they're locally derivable.
  function hasKnownCoords(stop) {
    if (typeof stop?.lat === "number" && typeof stop?.lng === "number") return true;
    if (stop?.type === "airport") {
      const code = job.airportCode || (stop.locationName || "").toUpperCase().trim();
      if (coordsForAirport(code)) return true;
    }
    return false;
  }

  // ── Inline "fix location" state ──────────────────────────────────────
  // When the owner hits "📍 Set location" on a stop row, we expose a Places
  // autocomplete inline. Picking a result writes lat/lng + address + name
  // back to the job's stops array so future Auto-calc resolves them.
  const [fixingStopIdx, setFixingStopIdx] = useState(null);
  const [fixSaving, setFixSaving] = useState(false);
  // Id (package id or stop key) currently uploading an owner-added POD photo.
  const [uploadingId, setUploadingId] = useState(null);
  // Pending POD crop: { file, apply } — apply(croppedFile) uploads + saves for
  // whichever package/stop opened the cropper. Mirrors the guard PhotoCapture
  // flow so owner-added PODs go through the same crop step.
  const [crop, setCrop] = useState(null);

  async function commitStopLocation(idx, { name, address, lat, lng }) {
    setFixSaving(true);
    try {
      const updatedStops = stops.map((s, i) => i === idx ? {
        ...s,
        locationName: name || s.locationName || "",
        address: address || s.address || "",
        lat: typeof lat === "number" ? lat : (s.lat ?? null),
        lng: typeof lng === "number" ? lng : (s.lng ?? null),
        outOfTown: false, // any real address overrides OOT
      } : s);
      await updateJob(job.id, { stops: updatedStops });
      job.stops = updatedStops;
      setFixingStopIdx(null);
    } catch (err) {
      console.error("Save location failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // A direct whose email never carried a time arrives with scheduledTime
  // blank on the job AND on every stop, so there is nothing for step 1 to
  // default from and the timeline opens empty. We don't invent a clock —
  // the owner sets the missing scheduled start here. It saves to the job
  // (so the guard app and every later screen agree) and seeds the timeline
  // exactly as a parsed time would have.
  async function commitScheduledTime(hhmm) {
    const mins = minutesOf(hhmm);
    if (mins == null) return;
    setFixSaving(true);
    try {
      const updatedStops = stops.map((s, i) =>
        i === pickupIdx && !s.scheduledTime ? { ...s, scheduledTime: hhmm } : s);
      await updateJob(job.id, { scheduledTime: hhmm, stops: updatedStops });
      job.scheduledTime = hhmm;
      job.stops = updatedStops;
      // Only seed what the owner hasn't already filled in by hand, and seed
      // it the way defaultPickupStart would have: an Orion direct starts 15
      // min before the scheduled time (LALI exactly at it, it bills pure
      // drive time), everything else — Malca directs included — starts 30
      // min before, the on-site-early convention.
      const lead = isOrionDirect ? (isLali ? 0 : 15) : 30;
      if (!pickupStartTouched) setPickupStart(toHHMM(mins - lead));
      if (!stopTimes[pickupIdx]) updateStopTime(pickupIdx, hhmm);
    } catch (err) {
      console.error("Save scheduled time failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Reorder a stop within the route by ±1. Airport stays pinned (front for
  // inbound, back for outbound). Pickup/delivery stops can shuffle freely.
  // Saves back to Firestore and clears mileage segments since the route
  // changed — owner re-runs Auto-calc to refresh.
  async function moveStop(idx, dir) {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= stops.length) return;
    if (stops[idx]?.type === "airport" || stops[newIdx]?.type === "airport") return;
    setFixSaving(true);
    try {
      const next = [...stops];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      // Reset mileage segments — old values point to stops in the previous
      // order. Total goes to 0; user re-runs Auto-calc.
      const blanks = Array.from({ length: next.length + 1 }, () => 0);
      await updateJob(job.id, { stops: next, mileageSegments: blanks });
      job.stops = next;
      // Local state mirror so the UI re-renders without a refetch
      setSegments(blanks.map((v) => String(v) || ""));
      setStopTimes(next.map((s) => s.billingTime || tsToHHMM(s.completedAt) || tsToHHMM(s.noShowAt) || s.scheduledTime || ""));
      setStopBillable(next.map(() => true));
      setMapsMsg("Stops reordered — click 🧭 Auto-calc mileage to refresh.");
    } catch (err) {
      alert("Reorder failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Toggle a stop's out-of-town flag and write it back to the job. Mileage
  // for OOT stops is implicitly 0 (no fixed destination — guard breaks
  // contact after a follow window) so the auto-calc skips them.
  async function toggleStopOOT(idx) {
    const cur = stops[idx];
    const next = !cur.outOfTown;
    setFixSaving(true);
    try {
      const updatedStops = stops.map((s, i) => i === idx ? {
        ...s,
        outOfTown: next,
        locationName: next ? "OUT OF TOWN" : (s.locationName === "OUT OF TOWN" ? "" : s.locationName || ""),
        address: next ? "" : s.address,
        lat: next ? null : s.lat,
        lng: next ? null : s.lng,
      } : s);
      await updateJob(job.id, { stops: updatedStops });
      job.stops = updatedStops;
    } catch (err) {
      console.error("Toggle OOT failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Owner correction: flip a stop's "scheduled in error" (no-show) state.
  // A guard sometimes skips a stop by mistake; the owner can restore it here
  // during billing review (clearing every noShow marker so it reads as a
  // normal, billable, photographable stop again), or mark one skipped.
  async function toggleStopSkipped(idx) {
    const cur = stops[idx];
    const makeSkipped = !cur.noShow;
    setFixSaving(true);
    try {
      const updatedStops = stops.map((s, i) => {
        if (i !== idx) return s;
        if (makeSkipped) {
          return {
            ...s,
            noShow: true,
            noShowReason: s.noShowReason || "Marked skipped by owner",
            noShowAt: s.noShowAt || new Date().toISOString(),
            noShowBy: { ownerId: userProfile?.id || "", ownerName: userProfile?.name || "" },
          };
        }
        const { noShow, noShowReason, noShowAt, noShowBy, ...rest } = s;
        return rest;
      });
      await updateJob(job.id, { stops: updatedStops });
      job.stops = updatedStops;
    } catch (err) {
      console.error("Toggle skipped failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Upload a POD photo to the same guard-photos path the field app uses
  // (storage rules already allow the job's owner to write), retrying a few
  // times the way the guard PhotoCapture does so a flaky connection doesn't
  // lose the shot. Returns the download URL or throws after exhausting retries.
  async function uploadPodWithRetry(file, stopKey) {
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        return await uploadGuardPhoto(file, job.id, stopKey, userProfile?.id || "owner", "pod");
      } catch (err) {
        lastErr = err;
        if (attempt < 3) await new Promise((r) => setTimeout(r, attempt * 1000)); // eslint-disable-line no-await-in-loop
      }
    }
    throw lastErr;
  }

  // Owner-added POD photo for a single package (per-package POD jobs) — stamps
  // podPhotoUrl on the package. Receives the already-cropped file.
  // Reopen a photo the guard already uploaded so the owner can straighten it
  // at billing time — a POD that came in sideways otherwise had to be retaken
  // by someone who has long since left the stop. Pulls the stored image back
  // down into the same cropper the capture flow uses (rotate + crop), then
  // re-uploads over it. Same fetch-to-blob the POD download already relies on.
  async function editExistingPhoto(url, apply) {
    if (!url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      setCrop({ file: new File([blob], "pod.jpg", { type: blob.type || "image/jpeg" }), apply });
    } catch (err) {
      console.error("Couldn't load that photo for editing", err);
      alert("Couldn't open that photo to edit: " + (err?.message || err));
    }
  }

  async function uploadOwnerPackagePhoto(pkg, file) {
    if (!file) return;
    setUploadingId(pkg.id);
    try {
      const url = await uploadPodWithRetry(file, pkg.stopId || "owner");
      const newPackages = (job.packages || []).map((p) => p.id === pkg.id ? { ...p, podPhotoUrl: url } : p);
      await updateJob(job.id, { packages: newPackages });
      job.packages = newPackages;
    } catch (err) {
      console.error("Owner POD upload failed", err);
      alert("Photo upload failed: " + (err?.message || err));
    }
    setUploadingId(null);
  }

  // Owner-added POD photo for a legacy single-photo stop (no per-package data).
  async function uploadOwnerStopPhoto(idx, file) {
    if (!file) return;
    const s = stops[idx];
    const key = s.id || `stop_${idx}`;
    setUploadingId(key);
    try {
      const url = await uploadPodWithRetry(file, key);
      const updatedStops = stops.map((st, i) => i === idx ? { ...st, podPhotoUrl: url } : st);
      await updateJob(job.id, { stops: updatedStops });
      job.stops = updatedStops;
    } catch (err) {
      console.error("Owner stop POD upload failed", err);
      alert("Photo upload failed: " + (err?.message || err));
    }
    setUploadingId(null);
  }

  // Remove a POD photo from a package (clears the reference so "+ Add photo"
  // comes back — the owner can re-take it). We leave the file in Storage; only
  // the podPhotoUrl pointer is dropped, matching how the guard "remove package"
  // flow works.
  async function removeOwnerPackagePhoto(pkg) {
    if (!window.confirm("Remove this POD photo? You can add a new one after.")) return;
    setFixSaving(true);
    try {
      const newPackages = (job.packages || []).map((p) => p.id === pkg.id ? { ...p, podPhotoUrl: null } : p);
      await updateJob(job.id, { packages: newPackages });
      job.packages = newPackages;
    } catch (err) {
      console.error("Remove POD failed", err);
      alert("Remove failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Remove a legacy stop-level POD photo.
  async function removeOwnerStopPhoto(idx) {
    if (!window.confirm("Remove this POD photo? You can add a new one after.")) return;
    setFixSaving(true);
    try {
      const updatedStops = stops.map((st, i) => i === idx ? { ...st, podPhotoUrl: null } : st);
      await updateJob(job.id, { stops: updatedStops });
      job.stops = updatedStops;
    } catch (err) {
      console.error("Remove stop POD failed", err);
      alert("Remove failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Flag/unflag a package as "held" (vaulted — kept for a later carrier job).
  // Edited right here on the billing review. Mutates job.packages + saves, so
  // the change flows straight into liveJob() → the workbook. Origin is stamped
  // once so nights-held + provenance survive the later move to a carrier job.
  async function toggleHeldPackage(pkg) {
    setFixSaving(true);
    try {
      const now = new Date().toISOString();
      const newPackages = (job.packages || []).map((p) => {
        if (p.id !== pkg.id) return p;
        if (p.held) {
          const { held, heldAt, heldBy, heldReason, heldDeliveryDate, heldDeliveryDirection, heldFromJobId, heldFromJobNumber, ...rest } = p;
          return rest;
        }
        return {
          ...p,
          held: true,
          heldAt: now,
          heldBy: { userId: userProfile?.id || null, userName: userProfile?.name || "", userRole: "owner" },
          heldFromJobId: p.heldFromJobId || job.id,
          heldFromJobNumber: p.heldFromJobNumber || job.jobNumber || "",
        };
      });
      await updateJob(job.id, { packages: newPackages });
      job.packages = newPackages;
    } catch (err) {
      console.error("Toggle held failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Target delivery date for a held piece — when it should ride a carrier
  // job. Once BOTH date and run are chosen, the piece is moved onto that
  // day's matching job (maybeMoveHeldPieceToRun).
  async function setHeldDeliveryDate(pkg, date) {
    setFixSaving(true);
    try {
      const newPackages = (job.packages || []).map((p) => p.id === pkg.id ? { ...p, heldDeliveryDate: date || "" } : p);
      await updateJob(job.id, { packages: newPackages });
      job.packages = newPackages;
      await maybeMoveHeldPieceToRun({ ...pkg, heldDeliveryDate: date }, date, pkg.heldDeliveryDirection);
    } catch (err) {
      console.error("Set held date failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // Which run (inbound/outbound) the held piece should ride on its delivery
  // day. Once BOTH the date and the run are chosen, the piece is actually
  // moved onto that day's matching job (confirm first) — see
  // maybeMoveHeldPieceToRun below.
  async function setHeldDeliveryDirection(pkg, dir) {
    setFixSaving(true);
    try {
      const newPackages = (job.packages || []).map((p) => p.id === pkg.id ? { ...p, heldDeliveryDirection: dir || "" } : p);
      await updateJob(job.id, { packages: newPackages });
      job.packages = newPackages;
      await maybeMoveHeldPieceToRun({ ...pkg, heldDeliveryDirection: dir }, pkg.heldDeliveryDate, dir);
    } catch (err) {
      console.error("Set held direction failed", err);
      alert("Save failed: " + (err?.message || err));
    }
    setFixSaving(false);
  }

  // ── Move a held piece onto its delivery run ─────────────────────────
  // Fires from the held row once BOTH the delivery date and the run are
  // chosen: finds that day's matching job and — after the owner confirms —
  // records the piece there (delivery stop cloned in before the airport,
  // vault provenance stamped) and removes it from this job. The $100/night
  // vault fee lands HERE, on the job that did the vaulting. When nothing is
  // on the books for that day, asks for the job number and creates the
  // delivery job. Declining either prompt keeps the piece held here with
  // the date/run stored as a note.
  async function maybeMoveHeldPieceToRun(pkg, date, dir) {
    if (!date || !dir || !pkg?.held) return;
    try {
      // The piece rides its own delivery stop to the new job, so both
      // paths below need the source stop and the nights held up front.
      const srcStop = (job.stops || []).find((s) => s.id === pkg.stopId) || null;
      if (!srcStop) {
        setMapsMsg("This piece isn't linked to a delivery stop — use Move packages… on the job detail instead.");
        return;
      }
      // Count from this job's date, not just heldAt — Hold is usually
      // clicked during billing, days after the run.
      const nights = vaultNightsBetween([pkg.heldAt, job.scheduledDate], date);
      // The vault fee bills on THIS job — the one that vaulted the piece —
      // never the run that delivers it. The delivering run bills only its
      // own route (owner confirmed 2026-08-27).
      const vaultFeeItem = () => ({
        id: `exp_vault_${Date.now()}`,
        label: `Vault fee (${nights} night${nights !== 1 ? "s" : ""}) — ${pkg.hawbNumber || "held piece"}`,
        amount: 100 * nights,
        roundTrip: false,
        receiptPhotoUrl: "",
      });
      // Fee goes onto THIS panel's expense list. Built from live state so
      // unsaved edits survive, and returned for persisting alongside the
      // package removal — the move is saved immediately, so the fee has to
      // be too or it's lost if the owner closes without saving. Returns null
      // when a vault-labeled expense is already here: never stack a second
      // fee. A legacy single-number Expenses converts to an item first,
      // since items take precedence in billing.
      const feeItemsForThisJob = () => {
        if (expenseItems.some((e) => /vault/i.test(e.label || ""))) return null;
        const legacyNum = expenseItems.length === 0 && expenses !== "" ? (Number(expenses) || 0) : 0;
        return [
          ...(legacyNum > 0 ? [{ id: `exp_legacy_${Date.now()}`, label: "Expenses", amount: String(legacyNum), roundTrip: false, receiptPhotoUrl: "" }] : []),
          ...expenseItems,
          { ...vaultFeeItem(), amount: String(100 * nights) },
        ];
      };
      const feeTotal = (items) => items.reduce(
        (sum, it) => sum + (Number(it.amount) || 0) * (it.roundTrip ? 2 : 1), 0
      );
      const cleanStop = () => ({
        ...srcStop,
        completedAt: null, completedBy: null, noShow: false,
        noShowReason: "", note: "", podPhotoUrl: null,
      });
      // Goes in `notes` (the guard-visible field the dispatcher edits), not
      // ownerNotes — whoever assigns this run and whoever drives it both need
      // to know the piece is already in hand rather than waiting on a flight.
      const carryNote = `${pkg.hawbNumber || "Piece"} vaulted from ${job.jobNumber || "an earlier job"} — already in hand, not arriving on a flight.`;
      const withCarryNote = (existing) => (
        (existing || "").includes(carryNote)
          ? existing || ""
          : `${existing ? `${existing.trimEnd()}\n` : ""}${carryNote}`
      );
      // Breadcrumb left on THIS job once the piece is gone. Without it the
      // stop reads as an ordinary delivery that never got a POD — the piece
      // and its photo both belong to the delivering run now, and the owner
      // has no way to see where the vault fee went.
      const vaultRecord = (toJobId, toJobNumber) => ({
        packageId:    pkg.id,
        hawbNumber:   pkg.hawbNumber || "",
        stopId:       pkg.stopId || "",
        toJobId:      toJobId || "",
        toJobNumber:  toJobNumber || "",
        deliveryDate: date,
        nights,
        movedAt:      new Date().toISOString(),
      });
      const priorAway = (Array.isArray(job.vaultedAway) ? job.vaultedAway : [])
        .filter((v) => v.packageId !== pkg.id); // re-move overwrites, never stacks
      const { held, heldAt, heldBy, heldReason, heldDeliveryDate, heldDeliveryDirection, heldFromJobId, heldFromJobNumber, ...rest } = pkg;
      const baseMoved = {
        ...rest,
        podPhotoUrl: null,
        photos: {},
        wasVaulted: true,
        vaultedNights: nights,
        vaultedFromJobId: heldFromJobId || job.id,
        vaultedFromJobNumber: heldFromJobNumber || job.jobNumber || "",
        // Which stop it left behind — the piece's own stopId gets rewritten
        // to the destination's stop, so record the origin before that.
        vaultedFromStopId: pkg.stopId || "",
      };

      const snap = await getDocs(query(
        collection(db, "jobs"),
        where("companyId", "==", job.companyId),
      ));
      const candidates = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((j) =>
          j.id !== job.id
          && j.scheduledDate === date
          && j.jobType === dir
          // A COMPLETE run is still a valid target — billing happens after
          // the fact, so the delivery day has usually already been driven
          // by the time the vaulted piece is reconciled. Only an invoiced
          // job is too late to take the piece (and the fee).
          && j.status !== "cancelled"
          && j.invoiceStatus !== "invoiced"
        );
      // Prefer the same broker's run when several jobs share the day.
      const sameBroker = candidates.filter((j) =>
        String(j.brokerName || "").toLowerCase() === String(job.brokerName || "").toLowerCase());
      const pool = sameBroker.length ? sameBroker : candidates;
      if (pool.length > 1) {
        setMapsMsg(`Several ${dir} jobs on ${date} — open this job's detail and use Move packages… to pick the exact one.`);
        return;
      }
      if (pool.length === 0) {
        // Nothing on the books for that day. Ask for the broker's job number
        // and stand the delivery job up here — it's already holding the
        // piece, so it bills from home with no airport leg (same shape as
        // the -V clone from Move packages…). It carries the route only; the
        // vault fee stays on this job.
        const entered = window.prompt(
          `No ${dir} job on ${date} to move ${pkg.hawbNumber || "this piece"} onto.\n\n`
          + `Type the job number to create that ${date} delivery job now — it gets the `
          + `delivery stop, and the ${nights}-night vault fee is added to THIS job. `
          + "Leave blank to keep the piece held here.",
          ""
        );
        const newJobNumber = String(entered || "").trim();
        if (!newJobNumber) {
          setMapsMsg(`No ${dir} job on ${date} yet — the piece stays held here with the date/run noted.`);
          return;
        }
        const newStop = cleanStop();
        const newRef = await createJob({
          companyId:      job.companyId,
          jobNumber:      newJobNumber,
          subJobLabel:    "Vault delivery",
          parentJobId:    job.id,
          jobType:        dir,
          airportCode:    job.airportCode || "",
          brokerName:     job.brokerName || "",
          brokerId:       job.brokerId || null,
          dispatcherName: job.dispatcherName || "",
          dispatcherId:   job.dispatcherId || null,
          numGuards:      1,
          scheduledDate:  date,
          scheduledTime:  "",
          billFromHome:   true,
          notes:          carryNote,
          ownerNotes:     job.ownerNotes || "",
          stops:          [newStop],
          packages:       [{ ...baseMoved, stopId: newStop.id }],
          status:         "intake",
          guardAssignments: [],
          guardIds:       [],
        });
        const keptNew = (job.packages || []).filter((p) => p.id !== pkg.id);
        const awayNew = [...priorAway, vaultRecord(newRef?.id, newJobNumber)];
        const feeNew = feeItemsForThisJob();
        await updateJob(job.id, {
          packages: keptNew,
          vaultedAway: awayNew,
          ...(feeNew ? { expenseItems: feeNew, expenses: feeTotal(feeNew) } : {}),
        });
        job.packages = keptNew;
        job.vaultedAway = awayNew;
        setVaultedAway(awayNew);
        if (feeNew) { job.expenseItems = feeNew; setExpenseItems(feeNew); }
        setMapsMsg(
          `Created ${newJobNumber} (${dir} · ${date}) with ${pkg.hawbNumber || "the piece"} — assign a guard to it. `
          + (feeNew
            ? `The ${nights}-night vault fee was added to THIS job's expenses.`
            : "This job already has a vault fee — kept as-is, no second fee added.")
        );
        return;
      }
      const target = pool[0];
      if (!window.confirm(
        `Move ${pkg.hawbNumber || "this piece"} onto ${target.jobNumber || `the ${dir}`} (${target.brokerName || ""} ${dir} · ${date})?\n\n` +
        `Its delivery stop is added to that job; the ${nights}-night vault fee is added to THIS job's expenses; the piece leaves this job.`
      )) return;

      // Reuse a matching stop already on the target (same saved location or
      // same address); otherwise clone the source stop in — before the
      // airport when the airport closes the route (outbound), else at the
      // end — with execution state cleared.
      const targetStops = Array.isArray(target.stops) ? [...target.stops] : [];
      const normAddr = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
      let targetStop = targetStops.find((s) =>
        (srcStop.locationId && s.locationId === srcStop.locationId)
        || (normAddr(s.address) && normAddr(s.address) === normAddr(srcStop.address))
      );
      if (!targetStop) {
        targetStop = {
          ...srcStop,
          completedAt: null, completedBy: null, noShow: false,
          noShowReason: "", note: "", podPhotoUrl: null,
        };
        const last = targetStops[targetStops.length - 1];
        const insertAt = last && last.type === "airport" ? targetStops.length - 1 : targetStops.length;
        targetStops.splice(insertAt, 0, targetStop);
      }

      const movedPkg = { ...baseMoved, stopId: targetStop.id };
      // Fee stays here; the delivering run takes the stop and nothing else.
      const feeItems = feeItemsForThisJob();

      await updateJob(target.id, {
        stops: targetStops,
        packages: [...(target.packages || []), movedPkg],
        notes: withCarryNote(target.notes),
      });
      const kept = (job.packages || []).filter((p) => p.id !== pkg.id);
      const away = [...priorAway, vaultRecord(target.id, target.jobNumber)];
      await updateJob(job.id, {
        packages: kept,
        vaultedAway: away,
        ...(feeItems ? { expenseItems: feeItems, expenses: feeTotal(feeItems) } : {}),
      });
      job.packages = kept;
      job.vaultedAway = away;
      setVaultedAway(away);
      if (feeItems) { job.expenseItems = feeItems; setExpenseItems(feeItems); }
      setMapsMsg(
        `Moved ${pkg.hawbNumber || "the piece"} onto ${target.jobNumber || `the ${dir}`}. ` +
        (feeItems
          ? `The ${nights}-night vault fee was added to THIS job's expenses${job.invoiceStatus === "invoiced" ? " — this job is already invoiced, so re-bill it to capture the fee" : ""}.`
          : "This job already has a vault fee — kept as-is, no second fee added.")
      );
    } catch (err) {
      console.error("Move held piece failed", err);
      alert("Move failed: " + (err?.message || err));
    }
  }

  // Resolve each stop to a lat/lng using, in order:
  //   1. stop.lat/lng (if set)
  //   2. hardcoded airport coords when stop.type === "airport" or locationName matches an airport code
  //   3. Google Geocoder on the address
  //   4. Google Geocoder on the location name
  async function resolveStopCoords(stop) {
    if (stop?.outOfTown) return null;

    // Airport stops: ALWAYS prefer company hub config when set, even when
    // the stop already has lat/lng. Older jobs were created before the hub
    // address feature and have stale coords baked in (often the airport's
    // geographic center, not your cargo terminal). Treat company config as
    // the source of truth for the airport's actual coordinates.
    if (stop.type === "airport") {
      const code = (job.airportCode || stop.locationName || "").toUpperCase().trim();
      const hub = (company?.hubAirport || "").toUpperCase().trim();
      const codeMatchesHub = !code || !hub || code === hub;
      if (codeMatchesHub) {
        if (company?.hubAirportLat != null && company?.hubAirportLng != null) {
          return { lat: company.hubAirportLat, lng: company.hubAirportLng };
        }
        if (company?.hubAirportAddress) {
          const c = await geocode(company.hubAirportAddress);
          if (c) return c;
        }
      }
      // Fall through to stop-level coords or the constants table.
      if (typeof stop.lat === "number" && typeof stop.lng === "number") {
        return { lat: stop.lat, lng: stop.lng };
      }
      const c = coordsForAirport(code);
      if (c) return c;
    }

    // Non-airport stops: trust the stop's own coords first.
    if (typeof stop.lat === "number" && typeof stop.lng === "number") {
      return { lat: stop.lat, lng: stop.lng };
    }
    // Use the job's airport city (e.g. "Charlotte" for CLT) as a hint so
    // partial names like "ASHCROFT & OAK" resolve to the right metro instead
    // of failing or landing on a same-named place on the other side of the country.
    const city = cityForAirport(job.airportCode);
    if (stop.address) {
      const c = await geocode(stop.address);
      if (c) return c;
      if (city) {
        const c2 = await geocode(`${stop.address}, ${city}`);
        if (c2) return c2;
      }
    }
    if (stop.locationName) {
      if (city) {
        const c = await geocode(`${stop.locationName}, ${city}`);
        if (c) return c;
      }
      const c2 = await geocode(stop.locationName);
      if (c2) return c2;
    }
    return null;
  }

  // ── Auto-compute miles between each consecutive stop via Google Maps ──
  // Two flavors:
  //   Ferrari/Executive: route is HOME → stops → HOME. The owner's
  //     "leave home" time (pickupStart) propagates through every stop
  //     and the return leg using the per-leg drive durations.
  //   Everything else: route is dispatch → stops, with a separate
  //     return-to-first-pickup calculation. Per-stop times stay manual.
  async function handleAutoMileage() {
    setComputingMaps(true);
    setMapsMsg("Loading Google Maps...");
    try {
      // Bail early on the home-based path if no home address is set.
      if (homeBased && !homeCoords) {
        setMapsMsg("Set your home address in Settings → Your home base before auto-calc on home-based jobs.");
        setComputingMaps(false);
        return;
      }

      // Resolve every stop's coordinates (sequential so we don't hammer geocoder).
      setMapsMsg("Resolving stop locations...");
      const stopPoints = [];
      const unresolved = [];
      for (let i = 0; i < stops.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const c = await resolveStopCoords(stops[i]);
        if (!c) unresolved.push({ idx: i + 1, name: stops[i]?.locationName || `Stop ${i + 1}` });
        stopPoints.push(c);
      }

      if (stopPoints.every((p) => p == null)) {
        setMapsMsg("Could not resolve any stop coordinates. Make sure addresses are filled in on the job.");
        setComputingMaps(false);
        return;
      }

      setMapsMsg("Computing drive distances...");

      // Build a "departure time" for the traffic-aware Distance Matrix
      // request. Priority:
      //   1. Ferrari/Executive: the owner-entered "leave home" time on
      //      the form (pickupStart) combined with the job's scheduledDate.
      //   2. Other jobs: the airport-stop scheduledTime (inbound) or the
      //      first pickup's scheduledTime (outbound/direct), combined
      //      with scheduledDate.
      //   3. Last resort: scheduledDate at noon.
      //   4. No date at all: pass nothing, Maps falls back to free-flow.
      // The mapsDistance.normalizeDepartureTime helper handles past
      // timestamps by projecting forward to the same weekday/time.
      const departureTime = (() => {
        const ymd = job.scheduledDate || "";
        const [y, mo, d] = ymd.split("-").map(Number);
        if (!y || !mo || !d) return null;
        // Choose the most representative HH:MM for traffic purposes.
        let hhmm = null;
        if (homeBased) {
          hhmm = pickupStart || null;
        } else if (job.jobType === "inbound" || job.jobType === "roundtrip") {
          const airport = (job.stops || []).find((s) => s.type === "airport");
          hhmm = (airport?.scheduledTime) || job.scheduledTime || pickupStart || null;
        } else {
          const firstPickup = (job.stops || []).find((s) => s.type !== "airport");
          hhmm = (firstPickup?.scheduledTime) || job.scheduledTime || pickupStart || null;
        }
        const [hh, mm] = (hhmm && /^\d{1,2}:\d{2}$/.test(hhmm))
          ? hhmm.split(":").map(Number)
          : [12, 0];
        return new Date(y, mo - 1, d, hh, mm, 0, 0);
      })();
      const mapsOpts = departureTime ? { departureTime } : {};

      let nextSegments = [...segments];
      let nextSegSeconds = [...segmentSeconds];
      let retSeconds = null;
      let missingLegs = 0;

      if (homeBased) {
        // Home-based route: HOME → billed stops → HOME. Unchecked stops
        // (uncheck the airport's Bill box on a vaulted pickup that didn't
        // fly) are skipped entirely, so a vault run bills a clean
        // home → pickup → home. routeStops keeps each billed stop's
        // ORIGINAL index so the per-stop segments line up.
        //   legs[0]    = home → routeStops[0]            → segments[0]
        //   legs[k]    = routeStops[k-1] → routeStops[k] → segments[origIdx]
        //   legs[last] = routeStops[last] → home         → segments[N]
        const billable = stopPoints
          .map((c, idx) => ({ c, idx }))
          .filter(({ c, idx }) => c != null && stopBillable[idx] !== false);
        // Fall back to all resolvable stops if nothing's billable (avoids
        // an empty route) — shouldn't happen in practice.
        const routeStops = billable.length
          ? billable
          : stopPoints.map((c, idx) => ({ c, idx })).filter(({ c }) => c != null);
        const points = [homeCoords, ...routeStops.map((b) => b.c), homeCoords];
        const legs = await driveAlongRoute(points, mapsOpts);
        // Reset every per-stop arriving leg (segments[1..N-1]); we refill
        // only the billed ones below. The FIRST billed stop's leg lives in
        // segments[0] (the start), so its own index stays 0 — which is why
        // a skipped FIRST stop (e.g. a piece already in hand on a later
        // outbound) doesn't leave a stale leg on the next stop.
        for (let i = 1; i < stops.length; i++) { nextSegments[i] = "0"; nextSegSeconds[i] = null; }
        // Start leg → segments[0].
        if (legs[0]) { nextSegments[0] = String(legs[0].miles); nextSegSeconds[0] = legs[0].seconds ?? null; }
        else missingLegs++;
        // Arriving legs for the 2nd..last billed stop → that stop's original index.
        for (let k = 1; k < routeStops.length; k++) {
          const leg = legs[k];
          if (leg) { nextSegments[routeStops[k].idx] = String(leg.miles); nextSegSeconds[routeStops[k].idx] = leg.seconds ?? null; }
          else missingLegs++;
        }
        // Return leg → segments[N].
        const retLeg = legs[routeStops.length];
        if (retLeg) { nextSegments[stops.length] = String(retLeg.miles); nextSegSeconds[stops.length] = retLeg.seconds ?? null; }
        retSeconds = retLeg?.seconds ?? null;

        // Time propagation. Walk the billed route accumulating drive time, but
        // NEVER overwrite a stop time that's already filled in (the owner
        // typed it, or it came from the guard's actual). A filled time
        // re-anchors the chain; return home = last billed stop + final leg.
        let leaveMin = minutesOf(pickupStart);
        // Back-derive the leave-home time from the first billed stop's
        // (delivery) time minus the drive out there — so on a vault delivery
        // the owner just enters when they delivered and we work out when they
        // left. Fires when the box is empty, and also when it only holds a
        // SEED on a run with no billed airport: there the seeded value came
        // from the guard's start stamp or the flight schedule and describes a
        // trip that didn't happen. A time the owner typed or saved is never
        // touched, on any route.
        const leaveHomeIsSeed = !pickupStartTouched && !hasBilledAirport;
        if ((leaveMin == null || leaveHomeIsSeed) && routeStops.length) {
          const firstStopMin = minutesOf(stopTimes[routeStops[0].idx]);
          const driveOutMin = legs[0]?.seconds != null ? Math.round(legs[0].seconds / 60) : null;
          if (firstStopMin != null && driveOutMin != null) {
            leaveMin = firstStopMin - driveOutMin;
            if (leaveMin < 0) leaveMin += 24 * 60;
            setPickupStart(toHHMM(leaveMin));
          }
        }
        if (leaveMin != null) {
          let cumulativeMin = leaveMin;
          const newStopTimes = [...stopTimes];
          for (let k = 0; k < routeStops.length; k++) {
            const legSec = legs[k]?.seconds;
            if (legSec != null) cumulativeMin += Math.round(legSec / 60);
            const origIdx = routeStops[k].idx;
            const existing = minutesOf(newStopTimes[origIdx]);
            if (existing != null) {
              cumulativeMin = existing; // keep the owner's time, chain from it
            } else {
              newStopTimes[origIdx] = toHHMM(cumulativeMin);
            }
          }
          setStopTimes(newStopTimes);
          // Return home. If the piece flies OUT (a billed airport on a
          // non-inbound route), you're paid through flight + 15 min +
          // drive home — the outbound rule, even on a home-based run.
          // Otherwise (an inbound delivery, or a vault pickup that didn't
          // fly) it's last billed stop + on-site dwell + drive home.
          const finalSec = legs[routeStops.length]?.seconds;
          if (finalSec != null) {
            const driveHomeMin = Math.round(finalSec / 60);
            const fm = minutesOf(flightTime);
            if (fliesOut && fm != null) {
              setReturnTime(toHHMM(fm + 15 + driveHomeMin));
            } else {
              const dwell = pickupDwell === "" ? 0 : (Math.round(Number(pickupDwell)) || 0);
              setReturnTime(toHHMM(cumulativeMin + dwell + driveHomeMin));
            }
          }
        }
      } else {
        // Existing non-Ferrari behavior preserved exactly.
        // Between-stops legs: segments[1..N]
        const legs = await driveAlongRoute(stopPoints, mapsOpts);
        legs.forEach((leg, i) => {
          if (leg) { nextSegments[i + 1] = String(leg.miles); nextSegSeconds[i + 1] = leg.seconds ?? null; }
          else missingLegs++;
        });

        // Return leg (last element): from the last BILLED stop back to the
        // first pickup. If every stop after the start is unchecked (e.g. a
        // cancelled-flight inbound — the deliveries never happened), the
        // last billed stop IS the start, so there's no return drive at all:
        // 0 miles, 0 time. They never left.
        // Round trip: stops[0] is the airport itself, so the return leg
        // anchors to the first REAL stop instead — airport → first location,
        // mirroring the outbound convention.
        const firstRealIdx = isRoundtrip
          ? stops.findIndex((s) => s.type !== "airport")
          : 0;
        const first = firstRealIdx >= 0 ? stopPoints[firstRealIdx] : null;
        // "Returned to my house": the guard kept a piece, so the run ends at
        // the vault instead of back at the airport. Same leg, different
        // destination — falls back to the airport if no home base is set.
        const returnTo = (returnHome && homeCoords) ? homeCoords : first;
        let lastBilledIdx = -1;
        for (let i = stopPoints.length - 1; i >= 0; i--) {
          if (stopBillable[i] === false) continue;
          if (stopPoints[i]) { lastBilledIdx = i; break; }
        }
        if (returnTo && lastBilledIdx > 0) {
          const ret = await driveBetween(stopPoints[lastBilledIdx], returnTo, mapsOpts);
          if (ret) {
            nextSegments[stops.length] = String(ret.miles);
            nextSegSeconds[stops.length] = ret.seconds ?? null;
            retSeconds = ret.seconds;
          }
        } else {
          // Last billed stop is the start itself → no return leg.
          nextSegments[stops.length] = "0";
          nextSegSeconds[stops.length] = null;
          retSeconds = 0;
        }

        // Auto-fill the return arrival time:
        //   Outbound: flight time + 15 min + drive back to first pickup
        //   Inbound/direct: last BILLED stop's time + drive back. Skipped /
        //     unbilled stops are ignored — e.g. a cancelled-flight inbound
        //     where the deliveries never happened: the return then anchors
        //     to the airport (the last billed stop), so the bill is the
        //     airport wait, not a phantom drive out to the no-show stops.
        //     And when that last billed stop IS the airport, no drive-back
        //     is added — they never left it.
        if (retSeconds != null) {
          const driveMin = Math.round(retSeconds / 60);
          if (job.jobType === "outbound" || isRoundtrip) {
            const fm = minutesOf(flightTime);
            if (fm != null) setReturnTime(toHHMM(fm + 15 + driveMin));
          } else {
            let lastIdx = -1;
            for (let i = stopTimes.length - 1; i >= 0; i--) {
              if (stopBillable[i] === false) continue; // unchecked = out of this bill, time and all
              if (minutesOf(stopTimes[i]) != null) { lastIdx = i; break; }
            }
            if (lastIdx >= 0) {
              const lastMin = minutesOf(stopTimes[lastIdx]);
              // Ending at the airport normally means no drive-back at all —
              // they never left it. But when the run ends at the house, the
              // airport→house drive is real and still gets paid.
              const atAirport = stops[lastIdx]?.type === "airport" && !returnHome;
              setReturnTime(toHHMM(lastMin + (atAirport ? 0 : driveMin)));
            }
          }
        }
      }

      setSegments(nextSegments);
      setSegmentSeconds(nextSegSeconds);
      setReturnDriveSeconds(retSeconds);

      const parts = [
        homeBased
          ? `Route + drive times computed from your home base${departureTime ? " (with traffic estimate)" : ""}.`
          : `Mileage computed from Google Maps${departureTime ? " (with traffic estimate)" : ""}.`,
      ];
      if (unresolved.length) {
        const list = unresolved.map((u) => `${u.name} (#${u.idx})`).join(", ");
        parts.push(`Couldn't resolve: ${list}. Click 📍 next to each to fix.`);
      }
      if (missingLegs) parts.push(`${missingLegs} leg(s) came back with no route from Google — see the browser console for the reason.`);
      setMapsMsg(parts.join(" "));
    } catch (err) {
      setMapsMsg("Couldn't reach Google Maps. Enter mileage manually. " + (err?.message || ""));
      console.error(err);
    }
    setComputingMaps(false);
  }

  // Flight time changes → recompute return arrival.
  // Only applies to outbound: return = flight + 15 min + drive back to first pickup.
  // For inbound jobs, the return-to-airport time is driven off the last delivery
  // time + drive, not the flight time — so don't touch returnTime here.
  // Priority: use stored drive duration (exact, from last Maps call) if we have it.
  // Fallback: flight + 15 min baseline (matches what we'd set before Maps runs).
  useEffect(() => {
    if (job.jobType === "inbound") return;
    // A home-based run only uses the flight rule once the piece actually
    // flies (a billed airport on the route). A pure vault pickup keeps its
    // drive-home cumulative return.
    if (homeBased && !hasBilledAirport) return;
    const fm = minutesOf(flightTime);
    if (fm == null) return;
    if (returnDriveSeconds != null) {
      setReturnTime(toHHMM(fm + 15 + Math.round(returnDriveSeconds / 60)));
    } else {
      setReturnTime(addMinutes(flightTime, 15));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightTime, returnDriveSeconds]);

  // Persist the current (broker, destination) hours+miles to the
  // destination location's brokerStats. Called from both Approve &
  // Save Draft so recall keeps up with the owner's working values.
  // No-op if not Ferrari/Executive, or if the destination has no
  // saved location to attach to. Always wrapped in try/catch — recall
  // is a convenience and must not block the primary save path.
  async function stampBrokerRecall() {
    if (!isFerrariStyle) return;
    try {
      const realStops = (job.stops || []).filter((s) => s.type !== "airport");
      const destinationStop = realStops[realStops.length - 1];
      if (!destinationStop?.locationId) return;
      const brokerKey = String(broker?.name || job.brokerName || "")
        .toLowerCase()
        .includes("ferrari") ? "ferrari" : "executive";
      const stampHours = Number(billing.hoursCharged) || Number(billing.rawHours) || 0;
      const stampMiles = Math.ceil(totalMiles) || 0;
      const locRef = doc(db, "locations", destinationStop.locationId);
      const locSnap = await getDoc(locRef);
      const existing = locSnap.exists() ? (locSnap.data().brokerStats || {}) : {};
      const prevEntry = existing[brokerKey] || {};
      const nowIso = new Date().toISOString();
      // Maintain a per-trip history so the Review modal can show what we
      // *usually* bill (average across trips), not just the last run.
      // One sample per job — re-saving or re-approving the same job
      // replaces its sample instead of inflating the average/count.
      let samples = Array.isArray(prevEntry.samples) ? prevEntry.samples.slice() : [];
      // Back-compat: if this location predates the history array but has a
      // legacy last* value, seed it as the first sample so the average
      // isn't thrown off by losing that data point.
      if (samples.length === 0 && (prevEntry.lastHours != null || prevEntry.lastMiles != null)) {
        samples.push({
          jobId: prevEntry.lastJobId || null,
          hours: Number(prevEntry.lastHours) || 0,
          miles: Number(prevEntry.lastMiles) || 0,
          billedAt: prevEntry.lastBilledAt || nowIso,
        });
      }
      const sample = { jobId: job.id || null, hours: stampHours, miles: stampMiles, billedAt: nowIso };
      const dupIdx = samples.findIndex((s) => s.jobId && job.id && s.jobId === job.id);
      if (dupIdx >= 0) samples[dupIdx] = sample; else samples.push(sample);
      // Cap history so the location doc can't grow without bound.
      if (samples.length > 50) samples = samples.slice(samples.length - 50);
      const newStats = {
        ...existing,
        [brokerKey]: {
          ...prevEntry,
          lastHours: stampHours,
          lastMiles: stampMiles,
          lastBilledAt: nowIso,
          lastJobId: job.id || null,
          samples,
        },
      };
      await updateLocation(destinationStop.locationId, { brokerStats: newStats });
    } catch (err) {
      // Non-blocking — billing-recall is convenience, not critical.
      console.warn("Couldn't stamp brokerStats on location", err);
    }
  }

  async function handleApprove() {
    setSaving(true);
    // A SENT or PAID invoice on file means this job IS billed — regardless
    // of what job.invoiceStatus says (the flag can get reset by a void, or
    // by a legacy draft-save on an already-invoiced job). Approving again
    // would double-bill the broker, so always check the invoices
    // themselves, not just the flag. Draft invoices are safe: they're
    // wiped and recreated by createInvoicesForJob.
    try {
      const hasSent = await hasSentInvoicesForJob(job.companyId, job.id);
      if (hasSent) {
        if (!["invoiced", "sent", "paid"].includes(job.invoiceStatus)) {
          // Flag got reset while a live sent/paid invoice exists — the job
          // was billed earlier. Self-heal the flag so it stops surfacing
          // on Ready to bill.
          try {
            await updateJob(job.id, { invoiceStatus: "invoiced" });
          } catch (e) {
            console.warn("Couldn't re-flag billed job", e);
          }
          alert(
            "This job was already billed — an invoice for it was marked sent (or paid). " +
            "It's been re-flagged as invoiced and will drop off Ready to bill. " +
            "To genuinely re-bill it, void the old invoice on the Invoices page first."
          );
        } else {
          alert(
            "This job has an invoice that was already marked sent (or paid). " +
            "Open the Invoices page and void it first — then re-approve here to rebuild the invoices."
          );
        }
        setSaving(false);
        return;
      }
    } catch (err) {
      console.warn("Couldn't check for sent invoices before approve", err);
    }
    try {
      const patch = {
        numGuards,
        billedAmount: billing.total,
        billingBreakdown: { ...billing, hoursManual: hoursTouched, reviewedAt: Date.now() },
        billingNotes: notes || "",
        // Ceil each segment per Orion FAQ (whole numbers, round up).
mileageSegments: segments.map((v) => Math.ceil(Number(v) || 0)),
        pickupStartTime: pickupStart || "",
        flightTime: flightTime || "",
        returnTime: returnTime || "",
        returnedHome: returnHome,
        departedDropoffTime: departedDropoff || "",
        stopBillingTimes: stopTimes,
        stopBillable,
        billFromHome,
        pickupDwellMin: pickupDwell === "" ? null : (Number(pickupDwell) || 0),
        returnDriveSeconds: returnDriveSeconds || null,
        expenses: effectiveExpenses,
        expenseItems: expenseItems.map(({ id, label, amount, roundTrip, receiptPhotoUrl }) => ({
          id, label: label || "", amount: Number(amount) || 0, roundTrip: !!roundTrip,
          receiptPhotoUrl: receiptPhotoUrl || "",
        })),
        billingApprovedAt: new Date().toISOString(),
        invoiceStatus: "invoiced",
        // Persist the per-job flat-fee override (or null to clear it
        // and fall back to broker default next time someone re-runs
        // the math without the modal).
        flatFeeOverride: flatFeeOverride !== "" ? Number(flatFeeOverride) : null,
        // Stamp per-guard endTime onto guardAssignments so the workbook
        // generator + invoice creation can read the split times back.
        // Helper synthesizes stub entries for unassigned slots that have
        // an endTime override (e.g. G2 unassigned but split at 17:00).
        guardAssignments: buildAssignmentsWithEndTimes(),
      };
      await updateJob(job.id, patch);
      // Create one invoice per assigned guard. liveJob() merges the current
      // modal state into the saved job so per-guard math uses the just-edited
      // hours/miles/expenses without waiting for a refetch.
      const invoices = await createInvoicesForJob(liveJob(), broker, billing);

      // Stamp brokerStats on the destination location for Ferrari/
      // Executive recall. Both Approve & Save Draft call the same
      // helper — owner-typed values get remembered as soon as a draft
      // is saved, so iterating doesn't lose recall data, and the final
      // approved values are what end up sticking after the last save.
      await stampBrokerRecall();

      // ── Gmail draft (best-effort, never blocks) ──────────────────
      // If the broker has an invoiceEmail configured AND the owner has
      // connected Gmail in Settings, compose a fully-formed draft with
      // the workbook(s) + POD PDF attached. Stamp the resulting Gmail
      // draft ID on every just-created invoice so the Invoices page
      // can show "Open in Gmail" deep links.
      //
      // Anything inside this block is non-fatal: the invoice docs
      // already exist, so a failed draft just means the owner falls
      // back to the manual 📦 download flow. We log and toast but
      // don't roll back the approve.
      let gmailDraft = null;
      let gmailError = null;
      // Distinguishes "couldn't connect" from a genuine compose failure, so
      // the toast can show a Connect button only where it would help.
      let gmailNeedsConnect = false;
      try {
        // The token lives in memory only, so a page reload disconnects Gmail
        // without any sign. This block used to just not run — no draft, no
        // error, nothing to explain it. Say so instead, and let the toast
        // offer to reconnect.
        // No invoice email on the rate card means there is nobody to draft
        // to, so the whole block below is skipped. That used to happen in
        // silence — approve, no draft, no reason given. Say which broker is
        // missing it and where to fix it.
        if (!broker?.invoiceEmail) {
          gmailError = `${broker?.name || "This broker"} has no invoice email on its rate card, so there's nobody to draft to. Add one under Rate cards to get drafts on approve.`;
        }
        if (broker?.invoiceEmail && !isGmailConnected()) {
          gmailNeedsConnect = true;
          gmailError = "Gmail isn't connected in this browser. Connect it and re-approve to get the draft.";
        }
        if (broker?.invoiceEmail && isGmailConnected()) {
          // Generate the attachments in-memory (same code path as the
          // 📦 button — we just don't trigger a browser download).
          const wbResult = await generateInvoiceWorkbooks(liveJob(), broker);
          const files = (wbResult?.files || []).map((f) => ({
            filename: f.filename,
            mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            blob: f.blob,
          }));
          // POD PDF — only for brokers that require one (Malca), and only
          // if the job actually has POD photos worth packaging.
          if (podRequired || anyPodPhoto) {
            try {
              const podBlob = await generatePodPdf(liveJob());
              if (podBlob && podBlob.size > 0) {
                files.push({
                  filename: `POD_${job.jobNumber || job.id || "job"}.pdf`,
                  mimeType: "application/pdf",
                  blob: podBlob,
                });
              }
            } catch (err) {
              console.warn("POD PDF generation failed; continuing without it.", err);
            }
          }
          gmailDraft = await composeJobDraft({
            job: liveJob(),
            broker,
            billing,
            files,
          });
          // Stamp draft id on each invoice so the Invoices row can
          // render an "Open in Gmail" link.
          if (gmailDraft?.draftId) {
            for (const inv of invoices) {
              // eslint-disable-next-line no-await-in-loop
              await updateInvoice(inv.id, {
                gmailDraftId: gmailDraft.draftId,
                // Pin the link to the right Google account so clicking
                // "Open draft" doesn't accidentally land in /u/0/'s
                // (different) Gmail.
                gmailDraftAuthEmail: gmailDraft.authEmail || null,
              });
            }
          }
        }
      } catch (err) {
        console.error("Gmail draft compose failed (non-fatal)", err);
        gmailError = err?.message || String(err);
      }

      onSaved({ ...patch, invoicesCreated: invoices, gmailDraft, gmailError, gmailNeedsConnect });
    } catch (err) {
      console.error("Approve & invoice failed", err);
      // Distinguish the "job is already flagged as invoiced" case from
      // generic errors so the owner knows the recovery path (void the
      // existing invoices for this job first — that auto-unlocks).
      const isPerms = String(err?.message || "").toLowerCase().includes("permission");
      const isLocked = isPerms && (job.invoiceStatus === "invoiced" || job.invoiceStatus === "sent" || job.invoiceStatus === "paid");
      if (isLocked) {
        alert(
          "Approve failed — this job is already flagged as invoiced. " +
          "Open the Invoices page, void every invoice that's still on file for this job, and try again. " +
          "Voiding the last live invoice auto-resets the job so re-approve will work."
        );
      } else {
        alert("Approve failed: " + (err?.message || err));
      }
    }
    setSaving(false);
  }

  // Build a merged job object reflecting the current (possibly-edited) review
  // state so generated files don't lag behind the inputs.
  function liveJob() {
    return {
      ...job,
      // The modal's editable guard count — createInvoicesForJob and the
      // per-guard workbooks read job.numGuards, so a just-bumped count
      // must reach them without waiting for a refetch.
      numGuards,
      // Ceil each segment per Orion FAQ (whole numbers, round up).
mileageSegments: segments.map((v) => Math.ceil(Number(v) || 0)),
      pickupStartTime: pickupStart || job.pickupStartTime || "",
      flightTime: flightTime || job.flightTime || "",
      returnTime: returnTime || job.returnTime || "",
      departedDropoffTime: departedDropoff || job.departedDropoffTime || "",
      stopBillingTimes: stopTimes,
      stopBillable,
      billFromHome,
      pickupDwellMin: pickupDwell === "" ? null : (Number(pickupDwell) || 0),
      expenses: effectiveExpenses,
      // Carry the just-typed notes + an expense label so the downloaded
      // workbook (which reads job.billingNotes → A44 and job.expensesDetail →
      // F40) reflects the current edits, not the last-saved values.
      billingNotes: notes || "",
      // Pieces that left for a later run. The workbook still owes Malca the
      // POD # for the location that was attempted, and job.packages no
      // longer holds it.
      vaultedAway,
      expensesDetail: expenseItems.map((e) => e.label).filter(Boolean).join(", ") || job.expensesDetail || "",
      expenseItems: expenseItems.map(({ id, ...rest }) => ({
        id,
        label: rest.label || "",
        amount: Number(rest.amount) || 0,
        roundTrip: !!rest.roundTrip,
        receiptPhotoUrl: rest.receiptPhotoUrl || "",
      })),
      billedAmount: billing.total,
      flatFeeOverride: flatFeeOverride !== "" ? Number(flatFeeOverride) : (job.flatFeeOverride ?? null),
      // Stamp current per-guard endTime overrides onto guardAssignments
      // so downstream consumers (createInvoicesForJob, malcaExcel,
      // downloadPackage) see the just-edited split times without
      // waiting for a refetch.
      guardAssignments: buildAssignmentsWithEndTimes(),
    };
  }

  const [downloading, setDownloading] = useState(false);
  // Folder these files get filed into, when the owner has granted one.
  const [saveFolder, setSaveFolder] = useState(null);
  useEffect(() => { savedFolderName().then(setSaveFolder); }, []);
  async function chooseSaveFolder() {
    const name = await pickSaveFolder();
    if (name) setSaveFolder(name);
  }
  async function clearSaveFolder() {
    await forgetSaveFolder();
    setSaveFolder(null);
  }
  async function handleDownloadPackage() {
    setDownloading(true);
    try {
      const j = liveJob();
      // Resolve the granted folder up front. prompt:true is legal here — this
      // runs from a click — so a lapsed permission re-asks once instead of
      // silently reverting to the Downloads folder.
      const root = await getSaveFolder({ prompt: true });
      const wb = await downloadInvoiceWorkbook(j, broker, root);
      // POD PDF only for brokers that require one (Malca). LALI/EFFY/etc.
      // don't need a POD packet.
      let podPath = null;
      if (podRequired || anyPodPhoto) podPath = await downloadPodPdf(j, root, {
        brokerFolder: broker?.saveFolderName || broker?.name || job.brokerName,
        dateFolders: broker?.saveDateFolders !== false,
      });
      if (!wb.downloaded && wb.reason) {
        alert(wb.reason);
      } else if (wb.downloaded && wb.count > 1) {
        // Reassure the owner that N files is correct (one per guard).
        console.log(`Generated ${wb.count} per-guard workbooks for ${j.numGuards} guards.`);
      }
      // Say WHERE they went — a file written into a folder never shows up in
      // the browser's download shelf, so without this it looks like nothing
      // happened.
      const wrote = [...(wb.saved || []), ...(typeof podPath === "string" ? [podPath] : [])];
      if (wrote.length) {
        setMapsMsg(`Saved ${wrote.length} file${wrote.length !== 1 ? "s" : ""} to ${wrote[0].split("/").slice(0, -1).join("/")}`);
      }
    } catch (err) {
      alert("Download failed: " + (err?.message || err));
      console.error(err);
    }
    setDownloading(false);
  }

  async function handleSaveDraft() {
    setSaving(true);
    const patch = {
      numGuards,
      billedAmount: billing.total,
      billingBreakdown: { ...billing, hoursManual: hoursTouched, reviewedAt: Date.now() },
      billingNotes: notes || "",
      // Ceil each segment per Orion FAQ (whole numbers, round up).
mileageSegments: segments.map((v) => Math.ceil(Number(v) || 0)),
      pickupStartTime: pickupStart || "",
      flightTime: flightTime || "",
      returnTime: returnTime || "",
      returnedHome: returnHome,
      departedDropoffTime: departedDropoff || "",
      stopBillingTimes: stopTimes,
      stopBillable,
      billFromHome,
      pickupDwellMin: pickupDwell === "" ? null : (Number(pickupDwell) || 0),
      returnDriveSeconds: returnDriveSeconds || null,
      expenses: effectiveExpenses,
      expenseItems: expenseItems.map(({ id, label, amount, roundTrip, receiptPhotoUrl }) => ({
        id, label: label || "", amount: Number(amount) || 0, roundTrip: !!roundTrip,
        receiptPhotoUrl: receiptPhotoUrl || "",
      })),
      // Never downgrade a billed job back to "pending" — reopening an
      // invoiced job and clicking Save draft used to reset this flag,
      // resurrecting the job on Ready to bill while its invoices still
      // existed. Voiding the invoices is the sanctioned way to un-bill.
      invoiceStatus: ["invoiced", "sent", "paid"].includes(job.invoiceStatus)
        ? job.invoiceStatus
        : "pending",
      flatFeeOverride: flatFeeOverride !== "" ? Number(flatFeeOverride) : null,
      // Persist per-guard endTime onto guardAssignments so it survives a
      // draft save → close → reopen cycle. Helper synthesizes a stub
      // entry for unassigned slots that have an endTime — without that,
      // an "Unassigned" G2 split would never make it to firestore.
      guardAssignments: buildAssignmentsWithEndTimes(),
    };
    await updateJob(job.id, patch);
    // Recall stamping for Ferrari/Executive happens on draft saves too,
    // not just on approve. Owner-typed values are remembered the moment
    // they save — last save wins, so iterating updates the recall.
    await stampBrokerRecall();
    setSaving(false);
    onSaved(patch);
  }

  // Lenient airport detection — older stops may have `type: "delivery"` etc.
  // Treat any stop whose locationName matches the job's airportCode (or the
  // company hub) as an airport for purposes of skipping POD requirements.
  function isAirportLike(s) {
    if (s.type === "airport") return true;
    const ln = (s.locationName || "").toUpperCase().trim();
    if (!ln) return false;
    if (job.airportCode && ln === job.airportCode.toUpperCase().trim()) return true;
    if (company?.hubAirport && ln === company.hubAirport.toUpperCase().trim()) return true;
    return false;
  }
  // A stop is "missing a photo" when it has neither a stop-level
  // podPhotoUrl nor any package at this stop with a photo. Per-package
  // jobs (Pass 1) record photos on packages; legacy jobs use stop-level.
  // Either path counts as a satisfied photo.
  // A direct runs one pickup to one delivery, and Malca's POD form is a
  // single sheet signed at BOTH ends — so one photo is the whole proof.
  // Asking for a second one per stop chases a page that doesn't exist.
  // Inbound/outbound keep the per-stop requirement: each delivery is a
  // different customer signing a different sheet.
  const anyPodPhoto = stops.some((s) => s.podPhotoUrl)
    || (job.packages || []).some((p) => p.podPhotoUrl);
  // Only Malca REQUIRES a POD, but a direct now and then wants one anyway —
  // a customer who asks for the signed sheet, a delivery worth evidencing.
  // This opens the photo panel on a job that doesn't require one; the
  // missing-photo warning stays off, because nothing here is owed.
  const [podOptIn, setPodOptIn] = useState(false);
  // Package a POD PDF whenever photos actually exist, required or not — an
  // opt-in with no photos would otherwise produce a one-page "no POD photos"
  // PDF and attach it to the bill.
  const podPanelOpen = podRequired || podOptIn || anyPodPhoto;
  const missingPhotos = (!podRequired || (isDirect && anyPodPhoto)) ? [] : stops.filter((s) => {
    if (s.noShow || isAirportLike(s)) return false;
    if (s.podPhotoUrl) return false;
    const stopPkgs = (job.packages || []).filter((p) => p.stopId === s.id);
    if (stopPkgs.length > 0 && stopPkgs.every((p) => p.podPhotoUrl)) return false;
    // Every piece for this stop was vaulted onto a later run — the delivery
    // (and its POD) happens on THAT job. Chasing a photo here is chasing one
    // that can never exist.
    if (stopPkgs.length === 0 && vaultedAwayAt(s.id).length > 0) return false;
    return true;
  });

  return (
    // in-modal marks this overlay so the narrow-viewport rule that forces
    // .panel grids to min-width 720px (so full-page tables can be swiped)
    // doesn't apply in here — the modal's own grids are sized to fit it.
    <div className="in-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 40px rgba(0,0,0,0.2)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            {/* Same package-number fallback as the list row — a Ferrari job
                with no broker job # shows its NYW… Order No. instead of a
                blank title. */}
            <div style={{ fontSize: 14, fontWeight: 700 }}>Review billing — {jobDisplayNumber(job)}</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              {/* Date first — owners ask for it most when reviewing
                  several jobs in a row and need to keep them straight.
                  Format as "Wed, May 6" for quick scanability. */}
              {(() => {
                if (!job.scheduledDate) return null;
                const [y, m, d] = String(job.scheduledDate).split("-").map(Number);
                if (!y || !m || !d) return null;
                const dt = new Date(y, m - 1, d);
                const label = dt.toLocaleDateString("en-US", {
                  weekday: "short", month: "short", day: "numeric",
                });
                return <span style={{ fontWeight: 600, color: "#444" }}>{label} · </span>;
              })()}
              {job.brokerName} · {job.jobType}
              {(() => {
                // Stop count excludes the airport (every air job has one
                // implicitly). Direct jobs are always A→B, so we suppress
                // the count entirely there to avoid noise.
                if (job.jobType === "direct") return null;
                const realStops = stops.filter((s) => s.type !== "airport");
                if (realStops.length === 0) return null;
                return ` · ${realStops.length} stop${realStops.length === 1 ? "" : "s"}`;
              })()}
              {job.salesRepName && (
                <span>
                  {" · rep: "}
                  <span style={{ color: "#185FA5", fontWeight: 600 }}>{job.salesRepName}</span>
                  {job.salesRepPhone && <span style={{ color: "#888" }}> ({formatPhone(job.salesRepPhone)})</span>}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#888" }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 18 }}>
          {jobInvoices.length > 0 && (
            <div style={{
              background: "#eef7f0", border: "1px solid #b9dcc3", borderRadius: 8,
              padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#24573a",
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <span style={{ fontSize: 16 }}>🧾</span>
              <div style={{ flex: 1, minWidth: 240 }}>
                <strong>Invoiced:</strong>{" "}
                {jobInvoices.map((inv, i) => (
                  <span key={inv.id}>
                    {i > 0 && " · "}
                    <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{inv.invoiceNumber}</span>
                    {" "}G{inv.guardNumber} — {Number(inv.hoursCharged) || 0}h, {Number(inv.miles) || 0} mi,{" "}
                    {formatMoney(inv.total)} <span style={{ textTransform: "capitalize" }}>({inv.status || "draft"})</span>
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  const q = job.jobNumber || jobInvoices[0].invoiceNumber || "";
                  onClose?.();
                  navigate(`/owner/invoices?q=${encodeURIComponent(q)}`);
                }}
              >
                Open in Invoices →
              </button>
            </div>
          )}
          {!broker && (
            <div style={{ background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 7, padding: "10px 12px", fontSize: 12, color: "#a32d2d", marginBottom: 14 }}>
              No rate card found for <b>{job.brokerName || "this broker"}</b>. Totals will be zero until you set one up in Rate cards.
            </div>
          )}

          {broker && (
            <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 11, color: "#555", display: "flex", gap: 16, flexWrap: "wrap" }}>
              <span><b>Rate card:</b> {broker.name}</span>
              {broker.hourlyRate > 0 && <span>${broker.hourlyRate}/hr</span>}
              {broker.mileageRate > 0 && <span>${broker.mileageRate}/mi</span>}
              {broker.minHours > 0 && <span>{broker.minHours}h min</span>}
              {broker.flatFee > 0 && <span>${broker.flatFee} flat</span>}
              <span style={{ color: "#185FA5", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 5 }}>
                {/* Editable guard count — works even after approval, so a
                    missed rider can be added later. Added guards default to
                    passenger (no mileage); removing below an assigned slot
                    just drops that slot from billing (the assignment data
                    is kept). */}
                ·
                <button
                  type="button"
                  onClick={() => changeNumGuards(numGuards - 1)}
                  disabled={numGuards <= 1}
                  title="One fewer guard billed"
                  style={{
                    width: 20, height: 20, lineHeight: "16px", borderRadius: 5,
                    border: "1px solid #c8dcee", background: "#fff", color: "#185FA5",
                    fontWeight: 700, cursor: numGuards <= 1 ? "default" : "pointer",
                    opacity: numGuards <= 1 ? 0.4 : 1, padding: 0,
                  }}
                >−</button>
                {numGuards} guard{numGuards !== 1 ? "s" : ""}
                <button
                  type="button"
                  onClick={() => changeNumGuards(numGuards + 1)}
                  disabled={numGuards >= 4}
                  title="One more guard billed — assumes they rode along (hourly only, no mileage)"
                  style={{
                    width: 20, height: 20, lineHeight: "16px", borderRadius: 5,
                    border: "1px solid #c8dcee", background: "#fff", color: "#185FA5",
                    fontWeight: 700, cursor: numGuards >= 4 ? "default" : "pointer",
                    opacity: numGuards >= 4 ? 0.4 : 1, padding: 0,
                  }}
                >+</button>
                {(billing.mileageSets || 1) > 1 && ` · ${billing.mileageSets} mileage sets`}
              </span>
              {numGuards !== Math.max(1, Number(job.numGuards) || 1) && (
                <span style={{ color: "#a36b1d", fontWeight: 600 }}>
                  was {Math.max(1, Number(job.numGuards) || 1)} — save or approve to apply
                </span>
              )}
            </div>
          )}

          {/* Recall — what we usually bill for this Ferrari/Executive
              destination: most common hours/miles across past trips plus
              the most recent run. When the exact location has no history
              (or as extra context), a same-town line pools every other
              saved location in the destination's city. One-click buttons
              pre-fill; owner can still edit after. */}
          {(recall || nearbyRecall) && (
            <div style={{
              background: "#eef4fa", border: "1px solid #c8dcee",
              borderRadius: 8, padding: "10px 12px", marginBottom: 14,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <span style={{ fontSize: 16 }}>📊</span>
              <div style={{ flex: 1, minWidth: 240, fontSize: 12, color: "#185FA5" }}>
                {recall && (
                  <>
                    <strong>Usually for this trip:</strong>
                    {" "}
                    {recall.usualHours != null && <>{recall.usualHours.toFixed(2)}h </>}
                    {recall.usualMiles != null && <>· {Math.round(recall.usualMiles)} mi </>}
                    <span style={{ color: "#888", fontWeight: 400 }}>
                      (most common of {recall.count} trip{recall.count === 1 ? "" : "s"} · {recall.destinationName})
                    </span>
                  </>
                )}
                {recall && (recall.lastHours != null || recall.lastMiles != null) && (
                  <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                    Last time: {recall.lastHours != null ? `${recall.lastHours.toFixed(2)}h` : "—"}
                    {recall.lastMiles != null ? ` · ${recall.lastMiles} mi` : ""}
                  </div>
                )}
                {nearbyRecall && (
                  <div style={{ fontSize: recall ? 11 : 12, color: recall ? "#888" : "#185FA5", marginTop: recall ? 2 : 0 }}>
                    {!recall && <strong>First trip to this exact location. </strong>}
                    Other {nearbyRecall.city} deliveries:
                    {" "}
                    {nearbyRecall.usualHours != null && <>{nearbyRecall.usualHours.toFixed(2)}h </>}
                    {nearbyRecall.usualMiles != null && <>· {Math.round(nearbyRecall.usualMiles)} mi </>}
                    <span style={{ color: "#888", fontWeight: 400 }}>
                      (most common of {nearbyRecall.count} trip{nearbyRecall.count === 1 ? "" : "s"} ·{" "}
                      {nearbyRecall.locCount} location{nearbyRecall.locCount === 1 ? "" : "s"})
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {recall && (recall.usualHours != null || recall.usualMiles != null) && (
                  <button
                    type="button"
                    className="btn btn-sm btn-blue"
                    onClick={() => applyRecall(recall.usualHours, recall.usualMiles)}
                    title="Fill hours & mileage with the most common values from past trips"
                  >
                    Use usual
                  </button>
                )}
                {recall && (recall.lastHours != null || recall.lastMiles != null) && (
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => applyRecall(recall.lastHours, recall.lastMiles)}
                    title="Fill hours & mileage with the most recent trip's values"
                  >
                    Use last
                  </button>
                )}
                {!recall && nearbyRecall && (
                  <button
                    type="button"
                    className="btn btn-sm btn-blue"
                    onClick={() => applyRecall(nearbyRecall.usualHours, nearbyRecall.usualMiles)}
                    title={`Fill hours & mileage with the most common values from other ${nearbyRecall.city} deliveries`}
                  >
                    Use nearby
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Orion direct drive timeline — the form times with smart
              defaults + a one-click Google drive estimate. Travel time is
              derived from the times but also directly editable (edits push
              the drop-off arrival and everything downstream). "Arrival back
              to pickup" stays auto-derived (matches the Orion contractor
              form), and the billed hours flow into the total. */}
          {isOrionDirect && (
            <div className="panel" style={{ marginBottom: 14 }}>
              <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div className="panel-title">Drive timeline (Orion direct)</div>
                  <div className="panel-subtitle">
                    Job started defaults to 15 min before pickup; departed pickup to the scheduled time.
                    Auto-calc fills the drop-off arrival from Google drive time (+5 min to depart).
                    Editing the travel time moves the later times and pay to match.
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-blue" onClick={handleOrionDriveCalc} disabled={computingMaps}>
                  {computingMaps ? "Computing…" : "🧭 Auto-calc drive time"}
                </button>
              </div>
              <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                {/* Route — pickup → drop off addresses, with inline
                    "set location" when coords are missing (Auto-calc needs
                    them). This grid is hidden for Orion directs, so the
                    locations live here. */}
                {[{ i: pickupIdx, label: "Pickup" }, { i: dropoffIdx, label: "Drop off" }].map(({ i, label }) => {
                  const s = stops[i] || {};
                  const coordsKnown = hasKnownCoords(s);
                  const isFixing = fixingStopIdx === i;
                  return (
                    <div key={label} style={{ borderBottom: "1px solid #f3f3f3", paddingBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#888", width: 60, flexShrink: 0 }}>{label}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, fontWeight: 600 }}>{s.locationName || "—"}</div>
                          <div style={{ fontSize: 10, color: "#888" }}>{s.address || (s.outOfTown ? "Out of town" : "No address")}</div>
                        </div>
                        {/* Scheduled job start beside the first location so
                            the owner can sanity-check the timeline entries
                            below against what was dispatched. */}
                        {label === "Pickup" && (
                          (s.scheduledTime || job.scheduledTime) ? (
                            <span title="Scheduled job start"
                              style={{ fontSize: 11, fontWeight: 700, color: "#185FA5", whiteSpace: "nowrap" }}>
                              🕐 {s.scheduledTime || job.scheduledTime}
                            </span>
                          ) : (
                            // Nothing was dispatched with a time, so the steps
                            // below can't default. Set it and they fill in.
                            <span title="No scheduled time came through on this job"
                              style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: "#a36b1d" }}>⚠ No sched. time</span>
                              <input
                                type="time"
                                disabled={fixSaving}
                                onChange={(e) => commitScheduledTime(e.target.value)}
                                style={{ padding: "3px 5px", borderRadius: 6, border: "1px solid #f0c46a", fontSize: 11 }}
                              />
                            </span>
                          )
                        )}
                        {coordsKnown && !isFixing && <span style={{ fontSize: 11, color: "#2d7a2d" }}>✓</span>}
                        {!s.outOfTown && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setFixingStopIdx(isFixing ? null : i)}
                            style={!coordsKnown ? { borderColor: "#f0c46a", color: "#a36b1d" } : undefined}
                          >
                            {isFixing ? "✕ Cancel" : (coordsKnown ? "✏️ Edit" : "📍 Set location")}
                          </button>
                        )}
                      </div>
                      {isFixing && (
                        <div style={{ marginTop: 6, background: "#fffbe9", borderRadius: 6, padding: 8 }}>
                          <div style={{ fontSize: 10, color: "#a36b1d", fontWeight: 600, marginBottom: 4 }}>
                            Search and pick from the dropdown — coords save to the job.
                          </div>
                          <PlacesAutocomplete
                            value={s.address || s.locationName || ""}
                            placeholder="e.g. Ashcroft & Oak, Winston-Salem NC"
                            onChange={(address, lat, lng) => {
                              if (typeof lat === "number" && typeof lng === "number") {
                                commitStopLocation(i, { name: s.locationName, address, lat, lng });
                              }
                            }}
                            style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                          />
                          {fixSaving && <div style={{ fontSize: 10, color: "#666" }}>Saving…</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
                {[
                  { label: "1 · Job started / arrived at pickup", value: pickupStart, set: (v) => editPickupStart(v) },
                  { label: "2 · Time departed pick-up", value: stopTimes[pickupIdx] || "", set: (v) => updateStopTime(pickupIdx, v) },
                  { travel: true },
                  { label: "4 · Arrival time at drop off", value: stopTimes[dropoffIdx] || "", set: (v) => updateStopTime(dropoffIdx, v) },
                  { label: "5 · Time departed drop off", value: departedDropoff, set: (v) => setDepartedDropoff(v) },
                ].map((row) => row.travel ? (
                  // Editable travel time (form D23). Typing here moves the
                  // drop-off arrival (and everything downstream) to match.
                  <div key="travel" style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#444" }}>
                      3 · Travel time, minutes each way
                      {orionTravelMin != null && (
                        <span style={{ color: "#888" }}> ({Math.floor(orionTravelMin / 60)}:{pad2(orionTravelMin % 60)})</span>
                      )}
                    </span>
                    <input
                      type="number" step="1" min="0"
                      value={travelMinEdit ?? (orionTravelMin != null ? String(orionTravelMin) : "")}
                      onChange={(e) => applyTravelMinutes(e.target.value)}
                      onBlur={() => setTravelMinEdit(null)}
                      placeholder="min"
                      style={{ width: "100%", minWidth: 0, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12, textAlign: "right" }}
                    />
                  </div>
                ) : (
                  <div key={row.label} style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "#444" }}>{row.label}</span>
                    <TimeField
                      value={row.value}
                      onChange={(v) => { row.set(v); setHoursTouched(false); }}
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                    />
                  </div>
                ))}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#185FA5" }}>6 · Arrival back to pickup (auto)</span>
                  <span style={{ fontSize: 12, fontWeight: 700, fontFamily: "monospace", color: "#185FA5", textAlign: "center" }}>
                    {returnTime || "—"}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10, alignItems: "center", borderTop: "1px solid #eee", paddingTop: 8 }}>
                  <span style={{ fontSize: 12 }}>One-way miles (billed round trip)</span>
                  <input
                    type="number" step="1" min="0"
                    value={segments[dropoffIdx] || ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSegments((segs) => { const n = [...segs]; n[dropoffIdx] = v; n[stops.length] = v; return n; });
                    }}
                    placeholder="0"
                    style={{ width: "100%", minWidth: 0, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12, textAlign: "right" }}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>Billed time · mileage</span>
                  <span style={{ fontSize: 12, fontWeight: 700, textAlign: "center" }}>
                    {billing.hoursCharged != null ? `${billing.hoursCharged.toFixed(2)}h` : "—"} · {Math.round(totalMiles)} mi
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Hours & expenses */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                Hours
                {/* When the broker's minimum kicks in, show what's
                    actually being billed (e.g. "billing 4.00h") so the
                    owner can see at a glance that the field's lower
                    raw value isn't what's on the invoice. */}
                {billing.minHoursApplied && (
                  <span style={{ color: "#EF9F27" }}>
                    (billing {Number(billing.hoursCharged || 0).toFixed(2)}h — {billing.minHours}h min applied)
                  </span>
                )}
                {!hoursTouched && derivedHours != null && (
                  <span style={{ color: "#0a7a4a", fontSize: 9, fontWeight: 700 }}>· AUTO</span>
                )}
                {hoursTouched && (
                  <button
                    type="button"
                    onClick={() => setHoursTouched(false)}
                    title="Re-track elapsed time from start → return"
                    style={{ marginLeft: "auto", background: "transparent", border: "1px solid #ddd", color: "#555", borderRadius: 5, padding: "1px 6px", fontSize: 10, cursor: "pointer" }}
                  >
                    ↺ Auto
                  </button>
                )}
              </label>
              <input type="number" step="0.01" value={hours}
                onChange={(e) => { setHours(e.target.value); setHoursTouched(true); }}
                placeholder={billing.rawHours?.toFixed(2)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 7, border: "1px solid #ddd" }} />
              <div style={{ fontSize: 10, color: "#aaa", marginTop: 3 }}>
                {hoursTouched
                  ? `Auto would be ${derivedHours != null ? derivedHours.toFixed(2) : "—"}h (elapsed start→return)`
                  : `Elapsed start→return: ${derivedHours != null ? derivedHours.toFixed(2) : "—"}h`}
              </div>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                Expenses
                {expenseItemsTotal > 0 && (
                  <span style={{ marginLeft: 8, color: "#0E7C46", fontWeight: 600 }}>
                    ${expenseItemsTotal.toFixed(2)} total
                  </span>
                )}
              </label>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {expenseItems.length === 0 && (
                  <div style={{ fontSize: 11, color: "#aaa", padding: "6px 0", fontStyle: "italic" }}>
                    No expenses — click + Add to itemize tolls, parking, etc.
                  </div>
                )}
                {expenseItems.map((it) => {
                  const uploading = uploadingReceiptIds.has(it.id);
                  const hasReceipt = !!it.receiptPhotoUrl;
                  return (
                  <div key={it.id} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{
                      display: "grid", gridTemplateColumns: "80px 1fr auto auto auto",
                      gap: 6, alignItems: "center",
                    }}>
                      <input
                        type="number" step="0.01" min="0"
                        value={it.amount}
                        onChange={(e) => updateExpenseItem(it.id, { amount: e.target.value })}
                        placeholder="$"
                        style={{ padding: "6px 8px", border: "1px solid #ddd", borderRadius: 6, fontSize: 12, textAlign: "right" }}
                      />
                      <input
                        value={it.label}
                        onChange={(e) => updateExpenseItem(it.id, { label: e.target.value })}
                        placeholder="Toll, parking, box truck rental, etc."
                        style={{ padding: "6px 8px", border: "1px solid #ddd", borderRadius: 6, fontSize: 12 }}
                      />
                      <label
                        title="Round-trip — Orion FAQ rule for tolls between pickup and dropoff"
                        style={{
                          display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#666",
                          background: it.roundTrip ? "#E0F4E8" : "transparent",
                          border: `1px solid ${it.roundTrip ? "#a9d4c2" : "#eee"}`,
                          borderRadius: 6, padding: "4px 7px", cursor: "pointer",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={!!it.roundTrip}
                          onChange={(e) => updateExpenseItem(it.id, { roundTrip: e.target.checked })}
                        />
                        ↔ Both ways
                      </label>
                      {/* Receipt upload — disguised <input type=file> behind
                          a button-styled label. When a receipt is on file,
                          shows a thumbnail link + clear button instead. */}
                      {hasReceipt ? (
                        <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                          <a
                            href={it.receiptPhotoUrl}
                            target="_blank"
                            rel="noreferrer"
                            title="View receipt"
                            style={{
                              fontSize: 10, color: "#0E7C46", fontWeight: 600,
                              border: "1px solid #a9d4c2", borderRadius: 6,
                              padding: "4px 7px", textDecoration: "none",
                              whiteSpace: "nowrap",
                            }}
                          >📎 Receipt</a>
                          <button
                            onClick={() => updateExpenseItem(it.id, { receiptPhotoUrl: "" })}
                            title="Remove receipt"
                            style={{ background: "none", border: "none", color: "#888", fontSize: 12, cursor: "pointer", padding: "0 4px" }}
                          >×</button>
                        </div>
                      ) : (
                        <label
                          style={{
                            fontSize: 10, color: "#185FA5",
                            border: "1px dashed #c0d4eb", borderRadius: 6,
                            padding: "4px 7px", cursor: uploading ? "wait" : "pointer",
                            whiteSpace: "nowrap",
                            background: uploading ? "#f0f4fa" : "transparent",
                          }}
                          title="Attach a receipt photo (toll receipt, rental contract, etc.)"
                        >
                          {uploading ? "Uploading..." : "📎 Receipt"}
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            onChange={(e) => handleReceiptUpload(it.id, e.target.files?.[0])}
                            style={{ display: "none" }}
                            disabled={uploading}
                          />
                        </label>
                      )}
                      <button
                        onClick={() => removeExpenseItem(it.id)}
                        title="Remove"
                        style={{ background: "none", border: "1px solid #eee", color: "#888", borderRadius: 6, fontSize: 12, padding: "4px 8px", cursor: "pointer" }}
                      >✕</button>
                    </div>
                  </div>
                  );
                })}
                <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
                  <button
                    type="button"
                    onClick={addExpenseItem}
                    style={{
                      background: "#f5f8ff", border: "1px dashed #c0d4eb", color: "#185FA5",
                      borderRadius: 6, padding: "6px 8px", fontSize: 11, cursor: "pointer",
                    }}
                  >+ Add expense</button>
                  {/* One-click overnight vault fee ($100/night). Adds a normal
                      expense line so it flows through the bill, workbook, and
                      statement like any other expense — edit the amount for
                      multi-night holds. */}
                  <button
                    type="button"
                    onClick={() => setExpenseItems((arr) => [
                      ...arr,
                      { id: `exp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, label: "Overnight vault — 1 night", amount: "100", roundTrip: false, receiptPhotoUrl: "" },
                    ])}
                    title="Add a $100/night overnight vault fee — edit the amount for multiple nights"
                    style={{
                      background: "#fff8e6", border: "1px dashed #f0c878", color: "#a36b1d",
                      borderRadius: 6, padding: "6px 8px", fontSize: 11, cursor: "pointer",
                    }}
                  >🏠 + Vault fee ($100/night)</button>
                </div>
              </div>
            </div>
          </div>

          {/* Per-guard split times — only when 2+ guards on the job.
              Lets the owner record when each guard ended (e.g. one
              guard splits off at an early stop while the others
              continue). If left blank, the guard is billed at the
              full route time (job.returnTime). */}
          {numGuards > 1 && (
            <div className="panel" style={{ marginBottom: 14 }}>
              <div className="panel-header">
                <div>
                  <div className="panel-title">Per-guard time &amp; mileage</div>
                  <div className="panel-subtitle">
                    Set a start, an end, or just the hours — whichever you know.
                    Blank start = same as the job. Blank end = the route end, except
                    on a multi-guard outbound, where only G1 waits for wheels-up +15
                    and everyone else is relieved on arrival at the airport (Malca,
                    Aug 2026) — their end is filled in for you. Typing hours pins
                    that number; typing a time hands it back to the clock. Miles come
                    from the per-stop guard chips below (full route = {fullMiles} mi).
                    Min {minHours || 0}h per guard.
                  </div>
                </div>
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: "58px 1fr 92px 92px 76px 92px",
                padding: "4px 14px", gap: 10, fontSize: 9, fontWeight: 700,
                color: "#98a4b3", textTransform: "uppercase", letterSpacing: .3,
                borderBottom: "1px solid #f0f0f0",
              }}>
                <span /><span />
                <span>Start</span>
                <span>End</span>
                <span>Hours</span>
                <span style={{ textAlign: "right" }}>Miles</span>
              </div>
              {Array.from({ length: numGuards }, (_, i) => i).map((i) => {
                const gNum = i + 1;
                const assignment = (job.guardAssignments || []).find((g) => g.guardNumber === gNum);
                const isDriver = guardOwnsCar(assignment, gNum); // own car → bills mileage
                const et = guardEndTimes[i] || "";
                const st = guardStartTimes[i] || "";
                const he = guardHoursEntry[i] || "";
                const computed = guardHoursComputed[i];
                return (
                  <div key={gNum} style={{
                    display: "grid", gridTemplateColumns: "58px 1fr 92px 92px 76px 92px",
                    padding: "10px 14px", borderBottom: "1px solid #f0f0f0",
                    alignItems: "center", gap: 10, fontSize: 12,
                  }}>
                    <div style={{
                      background: "#0B1C2E", color: "#fff", borderRadius: 6,
                      padding: "3px 9px", fontSize: 11, fontWeight: 700, textAlign: "center",
                    }}>G{gNum}{isDriver ? " · drv" : ""}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: "#111" }}>{assignment?.guardName || "Unassigned"}</div>
                      <div style={{ fontSize: 10, color: "#888" }}>
                        {he
                          ? `${Number(he)}h entered directly`
                          : `${st ? `Starts ${st}` : "Full route"} — ${
                              !et && i > 0 && relievedEndTime
                                ? `relieved at the airport, ends ${relievedEndTime}`
                                : !et && i === 0 && relievedEndTime
                                  ? `waits for wheels-up +15, ends ${returnTime || "—"}`
                                  : `ends at ${et || defaultEndFor(i) || "—"}`
                            }`}
                        {computed != null && <> · billing <strong style={{ color: "#185FA5" }}>{computed.toFixed(2)}h</strong></>}
                        {/* Say so when the broker minimum is doing the work,
                            so the number isn't mistaken for elapsed time. */}
                        {(() => {
                          const raw = (() => {
                            if (he !== "" && Number.isFinite(Number(he))) return Number(he);
                            const s = minutesOf(st || pickupStart);
                            const e = minutesOf(et || defaultEndFor(i));
                            if (s == null || e == null) return null;
                            const w = e < s ? e + 24 * 60 : e;
                            return (w - s) / 60;
                          })();
                          if (raw == null || !(minHours > 0) || raw >= minHours) return null;
                          return <span style={{ color: "#a36b1d" }}> · min applied (raw {raw.toFixed(2)}h)</span>;
                        })()}
                      </div>
                    </div>
                    <TimeField
                      value={st}
                      onChange={(v) => setGuardStart(i, v)}
                      title="When THIS guard started, if later than the job (e.g. approved partway through). Blank = same as the job."
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                      placeholder={pickupStart}
                    />
                    <TimeField
                      value={et}
                      onChange={(v) => setGuardEnd(i, v)}
                      title="When THIS guard finished. Blank uses their default end — wheels-up +15 for the guard who stays, airport arrival + drive back for one who is relieved there."
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                      placeholder={defaultEndFor(i)}
                    />
                    <input
                      type="number" step="0.25" min="0"
                      value={he}
                      onChange={(e) => setGuardHours(i, e.target.value)}
                      title="Bill this guard a set number of hours. Overrides the times above; clear it to go back to the clock."
                      placeholder={computed != null ? computed.toFixed(2) : ""}
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: `1px solid ${he ? "#185FA5" : "#ddd"}`, fontSize: 12, textAlign: "right" }}
                    />
                    {/* Miles this car bills — derived from the per-stop guard
                        chips on the route below. Hover for the leg breakdown. */}
                    {isDriver ? (
                      <div style={{ textAlign: "right" }} title={guardMilesBreakdown[i] ? `${guardMilesBreakdown[i]} = ${guardMilesEffective[i]} mi` : undefined}>
                        <div style={{ fontWeight: 600, color: "#185FA5" }}>{guardMilesEffective[i]} mi</div>
                        {guardMilesBreakdown[i] && (
                          <div style={{ fontSize: 9, color: "#888", lineHeight: 1.3 }}>{guardMilesBreakdown[i]}</div>
                        )}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: "#bbb", textAlign: "right" }}>no mileage</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Route, times, and per-segment mileage. Hidden for Orion
              directs — the "Drive timeline" panel above already covers their
              times and round-trip mileage with the correct model, so showing
              this grid too would double up the inputs and confuse the total. */}
          {/* Nothing to start the clock from. A dispatch sheet with an empty
              service-time column leaves scheduledTime blank on the job AND on
              every stop, so the times below open empty with no hint why. The
              Orion-direct panel grew this first; Malca directs land here. We
              don't invent a time — setting it writes scheduledTime to the job
              and the first stop, then seeds the clock the way a parsed time
              would have. Hidden once anything fills the start, typed or
              derived. */}
          {!isOrionDirect && !pickupStart && (
            <div style={{
              background: "#FFF7E0", border: "1px solid #F0D88C", borderLeft: "4px solid #EF9F27",
              borderRadius: 8, padding: "10px 12px", marginBottom: 14,
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
              fontSize: 12, color: "#74540C",
            }}>
              <span style={{ fontSize: 15 }}>⚠</span>
              <span style={{ flex: 1, minWidth: 220 }}>
                <strong>No scheduled time on this job</strong>{" — "}
                nothing came through on the dispatch sheet, so the times below
                can't fill themselves in. Set it here, or type the times by hand.
              </span>
              <input
                type="time"
                disabled={fixSaving}
                onChange={(e) => commitScheduledTime(e.target.value)}
                style={{ padding: "4px 6px", borderRadius: 6, border: "1px solid #EF9F27", fontSize: 12 }}
              />
            </div>
          )}

          {!isOrionDirect && (
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
              <div className="panel-title">Route &amp; timing</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 11, color: "#666" }}>
                  Total: <b>{Math.round(totalMiles)} mi</b> · <b>{billing.hoursCharged.toFixed(2)}h</b>
                  {totalDriveSec != null && <> · 🧭 <b>{fmtDriveSec(totalDriveSec)}</b> driving</>}
                </span>
                <button
                  className="btn btn-sm"
                  onClick={handleAutoMileage}
                  disabled={computingMaps}
                  title="Fill mileage from Google Maps using each stop's coordinates"
                >
                  {computingMaps ? "Computing..." : "🧭 Auto-calc mileage"}
                </button>
              </div>
            </div>

            {/* Bill-from-my-house toggle. Ferrari/Executive are always
                home-based so the toggle is redundant there — only show it
                for the other (Malca/Orion) jobs. Used for vault runs:
                pick a piece up to your house and bill the home↔pickup
                round trip; the next outbound starts from home. */}
            {!isFerrariStyle && (
              <div style={{ padding: "8px 14px", borderBottom: "1px solid #f0f0f0", background: "#fafbfc", display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={billFromHome}
                    onChange={(e) => setBillFromHome(e.target.checked)}
                  />
                  <span style={{ fontWeight: 600 }}>🏠 Bill from my house</span>
                  <span style={{ color: "#888", fontSize: 11 }}>
                    — leave home → stops → home (vault run / already holding the package)
                  </span>
                </label>
                {/* One-click fix for an inbound that's really a vault delivery
                    (pieces already in hand). Drops the airport, bills from home,
                    seeds the vault fee. Hidden once it's set up. */}
                {!billFromHome && hasBilledAirport && (
                  <button
                    type="button"
                    onClick={convertToVaultDelivery}
                    title="Already holding these pieces — bill home → stops → home, drop the airport, add the vault fee"
                    style={{
                      alignSelf: "flex-start", marginTop: 2,
                      background: "#fff8e6", border: "1px dashed #f0c878", color: "#a36b1d",
                      borderRadius: 6, padding: "5px 9px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                    }}
                  >🏠 This is a vault delivery — set it up</button>
                )}
                {billFromHome && !homeCoords && (
                  <div style={{ fontSize: 11, color: "#a36b1d" }}>
                    ⚠ Set your home base in Settings → Your home base, or Auto-calc can't compute from home.
                  </div>
                )}
                {billFromHome && (
                  <div style={{ fontSize: 11, color: "#888" }}>
                    Uncheck a stop's <b>Bill</b> box to skip it from the route — e.g. the airport on a vaulted pickup that didn't fly.
                  </div>
                )}
                {/* Dwell only matters when there's no flight (a vault pickup).
                    Once it flies, the return is flight + 15 + drive home, so
                    the on-site time doesn't change the billed end. */}
                {billFromHome && !fliesOut && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                    <span style={{ fontSize: 12, color: "#444" }}>
                      {hasBilledAirport ? "Time spent at pickup (min)" : "Time spent at the drop off (min)"}
                    </span>
                    <input
                      type="number" min="0" step="5"
                      value={pickupDwell}
                      onChange={(e) => applyPickupDwell(e.target.value)}
                      placeholder="0"
                      style={{ width: 80, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12, textAlign: "right" }}
                    />
                    <span style={{ fontSize: 11, color: "#888" }}>added before the drive home</span>
                  </div>
                )}
                {billFromHome && fliesOut && (
                  <div style={{ fontSize: 11, color: "#888" }}>
                    It flies on this job, so you're paid through <b>flight time + 15 min + drive home</b> (set the flight time below).
                  </div>
                )}
              </div>
            )}

            {mapsMsg && (
              <div style={{ padding: "6px 14px", background: "#FFF8E6", borderBottom: "1px solid #f0e4c0", fontSize: 11, color: "#8a5a00" }}>
                {mapsMsg}
              </div>
            )}

            {/* Missing airport stop on inbound/outbound — offer to seed
                one from the company hub config. A home-based run is the one
                case where there's genuinely no airport: a vault delivery
                keeps the carrier's inbound/outbound jobType, but the piece
                was already in hand at the house and never flew that day.
                Adding an airport there would bill a drive that never
                happened, so don't nag for one. */}
            {(() => {
              const needsAirport = ["inbound", "outbound", "roundtrip"].includes(job.jobType)
                && !homeBased
                && !stops.some((s) => s.type === "airport")
                && !!company?.hubAirportAddress;
              if (!needsAirport) return null;
              return (
                <div style={{
                  padding: "8px 14px", background: "#fff7e0",
                  borderBottom: "1px solid #f0c878", fontSize: 11, color: "#7a5400",
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                }}>
                  <div>
                    ⚠ This {job.jobType} job has no airport stop. Adding one fixes the mileage calc.
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const mkAirport = (scheduledTime) => ({
                          id: (typeof crypto !== "undefined" && crypto.randomUUID)
                            ? crypto.randomUUID()
                            : `airport_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                          type: "airport",
                          locationName: company.hubAirport || job.airportCode || "CLT",
                          address: company.hubAirportAddress || "",
                          lat: company.hubAirportLat ?? null,
                          lng: company.hubAirportLng ?? null,
                          accessNotes: company.hubAirportNotes || "",
                          scheduledTime,
                        });
                        // Inbound: airport at front. Outbound: airport at end.
                        // Roundtrip: bookends at both.
                        const updated = job.jobType === "inbound"
                          ? [mkAirport(company.inboundArrivalTime || ""), ...stops]
                          : job.jobType === "roundtrip"
                            ? [mkAirport(company.inboundArrivalTime || ""), ...stops, mkAirport("")]
                            : [...stops, mkAirport("")];
                        await updateJob(job.id, { stops: updated });
                        job.stops = updated;
                      } catch (err) {
                        alert("Failed: " + (err?.message || err));
                      }
                    }}
                    style={{
                      background: "#a36b1d", color: "#fff", border: "none", borderRadius: 6,
                      padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    + Add airport stop
                  </button>
                </div>
              );
            })()}

            {/* Airport-stop fixer — shows when an airport stop exists but
                is missing address/coords and the company has a hub configured.
                One click pulls the cargo address into the airport stop,
                writes it to Firestore, and is ready for Auto-calc. */}
            {(() => {
              // Check every airport stop (round trips have two bookends) —
              // surface the first one that's missing address/coords.
              const airports = stops.filter((s) => s.type === "airport");
              if (!airports.length) return null;
              const airport = airports.find((a) =>
                typeof a.lat !== "number" || typeof a.lng !== "number" || !a.address
              ) || airports[0];
              const hasCoords = typeof airport.lat === "number" && typeof airport.lng === "number";
              const hasAddress = !!airport.address;
              const hubReady = !!company?.hubAirportAddress &&
                (company.hubAirportLat != null || true); // address-only is OK; we geocode
              const hubAddrDiffers = company?.hubAirportAddress &&
                airport.address && airport.address !== company.hubAirportAddress;
              if (hasCoords && hasAddress && !hubAddrDiffers) return null;
              if (!hubReady) return (
                <div style={{
                  padding: "8px 14px", background: "#FFF8E6",
                  borderBottom: "1px solid #f0e4c0", fontSize: 11, color: "#8a5a00",
                }}>
                  ⚠ Airport stop has no address. Set <strong>Hub cargo address</strong> in
                  Settings, then come back here.
                </div>
              );
              return (
                <div style={{
                  padding: "8px 14px", background: "#eef3fb",
                  borderBottom: "1px solid #d6e0ee", fontSize: 11, color: "#185FA5",
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                }}>
                  <div>
                    <strong>Airport stop</strong>:{" "}
                    {airport.address ? (
                      <>
                        {airport.address}
                        {!hasCoords && <span style={{ color: "#a36b1d", marginLeft: 6 }}>(no coords)</span>}
                        {hubAddrDiffers && <span style={{ color: "#a36b1d", marginLeft: 6 }}>(differs from hub)</span>}
                      </>
                    ) : (
                      <span style={{ color: "#a36b1d" }}>no address saved</span>
                    )}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        // Apply to EVERY airport stop — a round trip has
                        // bookends at both ends sharing the hub address.
                        const updated = stops.map((s) => s.type === "airport" ? {
                          ...s,
                          address: company.hubAirportAddress,
                          lat: company.hubAirportLat ?? s.lat ?? null,
                          lng: company.hubAirportLng ?? s.lng ?? null,
                          accessNotes: s.accessNotes || company.hubAirportNotes || "",
                        } : s);
                        await updateJob(job.id, { stops: updated });
                        job.stops = updated; // local mirror so auto-calc sees it now
                      } catch (err) {
                        alert("Failed: " + (err?.message || err));
                      }
                    }}
                    style={{
                      background: "#185FA5", color: "#fff",
                      border: "none", borderRadius: 6,
                      padding: "5px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    📍 Use hub address
                  </button>
                </div>
              );
            })()}

            <div style={{
              display: "grid",
              gridTemplateColumns: "20px 34px minmax(0,1fr) 88px 74px",
              padding: "6px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
              fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
            }}>
              <span>#</span>
              <span style={{ textAlign: "center" }}>Bill</span>
              <span>Stop</span>
              <span>Time (24h)</span>
              <span style={{ textAlign: "right" }}>Miles to stop</span>
            </div>

            {/* Generic "Arrival at first pickup" row — hidden on inbound
                because the airport stop row below IS the first pickup and
                duplicating it confuses the layout. Outbound/direct still
                use this since their "first pickup" is a real pickup store. */}
            {!((job.jobType === "inbound" || job.jobType === "roundtrip") && stops.some((s) => s.type === "airport")) && (
            <div style={{
              display: "grid", gridTemplateColumns: "20px 34px minmax(0,1fr) 88px 74px",
              padding: "8px 14px", borderBottom: "1px solid #f0f0f0",
              alignItems: "center", gap: 8, fontSize: 12, background: "#fafbfc",
            }}>
              <span style={{ color: "#888" }}>{homeBased ? "🏠" : "▶"}</span>
              <span />
              <div>
                <div style={{ fontWeight: 600, color: "#444" }}>
                  {homeBased ? "Leave home" : "Arrival at first pickup"}
                </div>
                <div style={{ fontSize: 10, color: "#aaa" }}>
                  {homeBased
                    ? "Type the time you left — Auto-calc fills only the empty times, yours are kept"
                    : job.pickupStartTime
                      ? "Saved"
                      : job.scheduledTime
                        ? `From scheduled time ${job.scheduledTime}`
                        : "Auto: 30 min before earliest scheduled pickup"}
                </div>
              </div>
              <TimeField
                value={pickupStart}
                onChange={editPickupStart}
                style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
              />
              <div>
                <input
                  type="number" step="1" min="0" value={segments[0] || ""}
                  onChange={(e) => updateSegment(0, e.target.value)}
                  placeholder="0"
                  style={{ width: "100%", minWidth: 0, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12, textAlign: "right" }}
                />
                {segmentSeconds[0] != null && (
                  <div style={{ fontSize: 10, color: "#888", textAlign: "right", marginTop: 2 }}>
                    🧭 {fmtDriveSec(segmentSeconds[0])}
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Stops */}
            {stops.map((s, i) => {
              const skipped = !!s.noShow;
              const isAirport = s.type === "airport";
              const billable = stopBillable[i] !== false;
              // For outbound jobs, the airport time is represented by the Flight time row below.
              // We show the mileage segment to the airport but hide the per-stop time input to avoid duplication.
              // Home-based outbound has no flight row, so the airport keeps its own editable time.
              // Hide the airport's own time input when the flight row drives
              // it — i.e. whenever it actually flies. Home-based vault pickups
              // (airport unchecked, no flight) keep their normal input.
              // Round trip: the END airport (last stop) is driven by the
              // flight row like an outbound; the START airport keeps the
              // inbound-style Arrival/Departed inputs below.
              const hideTimeInput = (isOutbound && isAirport && hasBilledAirport)
                || (isRoundtrip && isAirport && i === stops.length - 1 && hasBilledAirport);
              const coordsKnown = hasKnownCoords(s);
              const isFixing = fixingStopIdx === i;
              return (
                <React.Fragment key={i}>
                <div style={{
                  display: "grid", gridTemplateColumns: "20px 34px minmax(0,1fr) 88px 74px",
                  padding: "8px 14px", borderBottom: isFixing ? "none" : "1px solid #f0f0f0",
                  alignItems: "center", gap: 8, fontSize: 12,
                  opacity: billable ? (skipped ? 0.85 : 1) : 0.45,
                }}>
                  <span style={{ color: "#888" }}>{i + 1}</span>
                  <span style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={billable}
                      onChange={() => toggleStopBillable(i)}
                      title={billable ? "Included in billing" : "Excluded from billing"}
                      style={{ cursor: "pointer" }}
                    />
                  </span>
                  <div>
                    <div style={{ fontWeight: 500, textDecoration: billable ? "none" : "line-through" }}>
                      {isAirport ? "🛫 " : ""}
                      {s.outOfTown ? (
                        <span style={{
                          background: "#fff7e0", color: "#a36b1d",
                          border: "1px solid #f0c878", borderRadius: 10,
                          padding: "1px 8px", fontSize: 11, fontWeight: 700,
                        }}>🚗 OUT OF TOWN</span>
                      ) : (
                        s.locationName || (isAirport ? "Airport" : "Stop")
                      )}
                      {skipped && <span style={{ color: "#a32d2d", fontSize: 10, marginLeft: 6 }}>✕ Skipped</span>}
                      {/* Owner can correct a stop the guard wrongly skipped
                          (restore it so it's billable + can take POD photos),
                          or mark one skipped. Airport is never skippable. */}
                      {!isAirport && (
                        <button
                          type="button"
                          onClick={() => toggleStopSkipped(i)}
                          disabled={fixSaving}
                          title={skipped ? "Restore this stop (guard skipped it by mistake)" : "Mark this stop as skipped"}
                          style={{
                            marginLeft: 8, fontSize: 10, fontWeight: 600,
                            background: skipped ? "#eafaef" : "#fff",
                            color: skipped ? "#2d7a2d" : "#a32d2d",
                            border: `1px solid ${skipped ? "#b3d9be" : "#e8a5a5"}`,
                            borderRadius: 10, padding: "1px 7px", cursor: "pointer",
                          }}
                        >
                          {skipped ? "↩ Restore" : "✕ Skip"}
                        </button>
                      )}
                      {!billable && <span style={{ color: "#888", fontSize: 10, marginLeft: 6 }}>· not billed</span>}
                      {/* The drive out here still bills, but the piece rode on
                          to a later run — say which, so the route reads right. */}
                      {vaultedAwayAt(s.id).map((v) => (
                        <span key={v.packageId} style={{
                          color: "#a36b1d", fontSize: 10, marginLeft: 6, whiteSpace: "nowrap",
                        }}>
                          🔒 vaulted → {v.toJobNumber || "later job"}
                        </span>
                      ))}
                      {/* Direct jobs only: let the owner flip a stop to OOT in-place.
                          Saves immediately so re-running mileage uses the new flag. */}
                      {isDirect && !isAirport && (
                        <button
                          type="button"
                          onClick={() => toggleStopOOT(i)}
                          disabled={fixSaving}
                          title={s.outOfTown ? "Clear out-of-town flag" : "Mark as out-of-town (no fixed destination)"}
                          style={{
                            marginLeft: 8, fontSize: 10, fontWeight: 600,
                            background: s.outOfTown ? "#fff7e0" : "#f8f8f8",
                            color: s.outOfTown ? "#a36b1d" : "#666",
                            border: `1px solid ${s.outOfTown ? "#f0c878" : "#ddd"}`,
                            borderRadius: 10, padding: "1px 7px", cursor: "pointer",
                          }}
                        >
                          {s.outOfTown ? "✓ OOT" : "🚗 Mark OOT"}
                        </button>
                      )}
                      {/* Edit affordance per stop — always visible (not just
                          when coords are missing) so the owner can fix a wrong
                          address inline without bouncing to JobDetailModal. */}
                      {!s.outOfTown && (
                        <button
                          type="button"
                          onClick={() => setFixingStopIdx(isFixing ? null : i)}
                          title={coordsKnown ? "Edit this stop's address" : "Coordinates missing — set address from Google Places"}
                          style={{
                            marginLeft: 8, fontSize: 10, fontWeight: 600,
                            background: coordsKnown ? "#eef3fb" : "#fff7e0",
                            color: coordsKnown ? "#185FA5" : "#a36b1d",
                            border: `1px solid ${coordsKnown ? "#d6e0ee" : "#f0c878"}`,
                            borderRadius: 10, padding: "1px 7px", cursor: "pointer",
                          }}
                        >
                          {isFixing ? "✕ Cancel" : (coordsKnown ? "✏️ Edit" : "📍 Set location")}
                        </button>
                      )}
                      {/* Reorder arrows — pickups/deliveries only. Airport
                          stays pinned (inbound first, outbound last). */}
                      {!isAirport && (() => {
                        const prevIsAirport = stops[i - 1]?.type === "airport";
                        const nextIsAirport = stops[i + 1]?.type === "airport";
                        const canMoveUp = i > 0 && !prevIsAirport;
                        const canMoveDown = i < stops.length - 1 && !nextIsAirport;
                        return (
                          <span style={{ display: "inline-flex", gap: 2, marginLeft: 6 }}>
                            <button
                              type="button"
                              onClick={() => moveStop(i, -1)}
                              disabled={!canMoveUp || fixSaving}
                              title="Move up"
                              style={{
                                fontSize: 11, padding: "1px 6px", lineHeight: 1,
                                background: canMoveUp ? "#fff" : "#f5f5f5",
                                color: canMoveUp ? "#555" : "#bbb",
                                border: "1px solid #ddd", borderRadius: 4,
                                cursor: canMoveUp ? "pointer" : "not-allowed",
                              }}
                            >↑</button>
                            <button
                              type="button"
                              onClick={() => moveStop(i, 1)}
                              disabled={!canMoveDown || fixSaving}
                              title="Move down"
                              style={{
                                fontSize: 11, padding: "1px 6px", lineHeight: 1,
                                background: canMoveDown ? "#fff" : "#f5f5f5",
                                color: canMoveDown ? "#555" : "#bbb",
                                border: "1px solid #ddd", borderRadius: 4,
                                cursor: canMoveDown ? "pointer" : "not-allowed",
                              }}
                            >↓</button>
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: 10, color: "#888" }}>
                      {s.type || "pickup"}
                      {s.packages?.length ? ` · ${s.packages.length} pkg` : ""}
                      {s.completedAt && <span> · actual {tsToHHMM(s.completedAt)}</span>}
                      {s.address && <span> · {s.address}</span>}
                    </div>
                    {/* Which cars drove this stop — only own-car guards (they
                        bill mileage). Tap to drop a car from this stop; its
                        leg then bills one fewer car and that guard's miles
                        drop. Defaults to every car. */}
                    {numGuards > 1 && !isAirport && billable && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, color: "#888" }}>Cars here:</span>
                        {Array.from({ length: numGuards }, (_, k) => k + 1)
                          .filter((n) => guardOwnsCar((job.guardAssignments || []).find((g) => g.guardNumber === n), n))
                          .map((n) => {
                            const cov = stopCoveredByGuard(s, n);
                            const legMi = Math.ceil((guardSegmentsByNum[n - 1] || [])[i] || 0);
                            return (
                              <button
                                key={n}
                                type="button"
                                onClick={() => toggleStopBillGuard(i, n)}
                                title={cov ? `G${n} drove to this stop (${legMi} mi this leg) — tap to remove` : `G${n} didn't drive here — tap to add`}
                                style={{
                                  border: `1px solid ${cov ? "#185FA5" : "#ccc"}`,
                                  background: cov ? "#185FA5" : "#fff",
                                  color: cov ? "#fff" : "#aaa",
                                  borderRadius: 6, padding: "1px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer",
                                }}
                              >G{n}{cov && legMi > 0 ? ` · ${legMi}` : ""}</button>
                            );
                          })}
                      </div>
                    )}
                  </div>
                  {hideTimeInput ? (
                    <span style={{ fontSize: 10, color: "#888", textAlign: "center" }}>see flight time ↓</span>
                  ) : isAirport && (job.jobType === "inbound" || (isRoundtrip && i === 0)) ? (
                    // Airport row on inbound carries TWO times:
                    //   Arrival   = billing-clock start (pickupStart, E25)
                    //   Departed  = time the guard left the airport with the
                    //               packages — what the Malca workbook reads
                    //               for "Time departed pick up" (E26). It
                    //               defaults to the guard's actual airport
                    //               completion timestamp but is editable here.
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      <div>
                        {/* On a vault run the clock starts at the house, not
                            at an airport nobody visited. Name it for what it
                            is so the box isn't mistaken for flight arrival. */}
                        <div style={{ fontSize: 9, color: "#888", fontWeight: 600, marginBottom: 1 }}>
                          {homeBased && !hasBilledAirport ? "Left home" : "Arrival"}
                        </div>
                        <TimeField
                          value={pickupStart || ""}
                          onChange={editPickupStart}
                          title={homeBased && !hasBilledAirport
                            ? "Time you left the house — starts the billing clock. Leave blank and Auto-calc works it out from the delivery time."
                            : "Arrival at airport — starts the billing clock"}
                          style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #185FA5", fontSize: 12 }}
                        />
                      </div>
                      {/* Nothing departed an airport on a vault run, and the
                          workbook falls back to the leave-home time there, so
                          this box holds a stale stamp nothing reads. Hide it
                          rather than invite the owner to reconcile it. */}
                      <div style={{ display: homeBased && !hasBilledAirport ? "none" : undefined }}>
                        <div style={{ fontSize: 9, color: "#888", fontWeight: 600, marginBottom: 1 }}>Departed pickup</div>
                        <TimeField
                          value={stopTimes[i] || ""}
                          onChange={(v) => updateStopTime(i, v)}
                          title="Time departed the airport with packages — Malca workbook 'Time departed pick up'"
                          style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                        />
                      </div>
                    </div>
                  ) : (
                    <TimeField
                      value={stopTimes[i] || ""}
                      onChange={(v) => updateStopTime(i, v)}
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                    />
                  )}
                  {(() => {
                    // Mileage shown on each row = leg ARRIVING at this stop
                    // (consistent across all job types). For inbound the
                    // airport sits at i=0 and the arriving leg is
                    // dispatch→airport (segments[0]). For outbound/direct
                    // the first pickup is at i=0 — that arriving leg is
                    // already shown in the "Arrival at first pickup" row
                    // above, so we leave this cell blank to avoid the
                    // duplicate / misleading per-row number.
                    // Home-based runs keep the drive OUT in segments[0] — the
                    // "leave home → first billed stop" leg — no matter which
                    // stop that turns out to be. Rendering it at i=0 puts the
                    // whole drive on a stop that may be skipped (the airport
                    // on a vault run), which reads as "68 miles to a place I
                    // never went" while the stop actually driven to shows 0.
                    // Show it on the row it belongs to instead. Editing
                    // follows, and the stored array is untouched, so the
                    // Malca workbook still reads the same slots.
                    const segIdx = (homeBased && i === firstBilledIdx) ? 0 : i;
                    const isOutboundOrDirectFirstStop =
                      (job.jobType === "outbound" || job.jobType === "direct") && i === 0;
                    if (isOutboundOrDirectFirstStop) {
                      return <span style={{ fontSize: 10, color: "#bbb", textAlign: "right" }}>—</span>;
                    }
                    // A stop that isn't billed contributed no miles to this
                    // invoice — leave its cell empty rather than showing a
                    // number that belongs to the route around it.
                    if (!billable) {
                      return <span style={{ fontSize: 10, color: "#bbb", textAlign: "right" }}>—</span>;
                    }
                    return (
                      <div>
                        <input
                          type="number" step="1" min="0" value={segments[segIdx] || ""}
                          onChange={(e) => updateSegment(segIdx, e.target.value)}
                          placeholder="0"
                          style={{ width: "100%", minWidth: 0, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12, textAlign: "right" }}
                        />
                        {(() => {
                          // Leg right after the inbound airport: we know the
                          // exact departure ("Departed pickup") AND this
                          // stop's arrival, so elapsed IS the drive time —
                          // show it (🕐), recalculating live as either time
                          // is edited. Legs between two regular stops keep
                          // the estimate (🧭): we only have arrival times
                          // there, so elapsed would wrongly include the
                          // dwell at the stop. Display only.
                          //
                          // Only when that airport is BILLED, though. On a
                          // vault run it's unchecked and its "departed pickup"
                          // is a leftover stamp from a trip that never
                          // happened — subtracting it produced absurd drive
                          // times (17:08 to 07:01 read as 13h 53m). With no
                          // real departure to measure from, fall through to
                          // the Maps estimate.
                          let sec = null;
                          let fromTimes = false;
                          if (i > 0 && stops[i - 1]?.type === "airport" && stopBillable[i - 1] !== false) {
                            const dep = minutesOf(stopTimes[i - 1]);
                            const arr = minutesOf(stopTimes[i]);
                            if (dep != null && arr != null) {
                              let d = arr - dep;
                              if (d < 0) d += 24 * 60;
                              sec = d * 60;
                              fromTimes = true;
                            }
                          }
                          if (sec == null) sec = segmentSeconds[segIdx];
                          if (sec == null) return null;
                          return (
                            <div style={{ fontSize: 10, color: "#888", textAlign: "right", marginTop: 2 }}>
                              {fromTimes ? "🕐" : "🧭"} {fmtDriveSec(sec)}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </div>
                {isFixing && (
                  <div style={{
                    padding: "10px 14px 12px 88px", borderBottom: "1px solid #f0f0f0",
                    background: "#fffbe9", display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    <div style={{ fontSize: 10, color: "#a36b1d", fontWeight: 600 }}>
                      Search for the location and select it from the dropdown — coords will save to the job.
                    </div>
                    <PlacesAutocomplete
                      value={s.address || s.locationName || ""}
                      placeholder="e.g. Ashcroft & Oak, Winston-Salem NC"
                      onChange={(address, lat, lng) => {
                        // Only commit when the user actually picked from the dropdown
                        // (lat/lng will be present); free text alone shouldn't save.
                        if (typeof lat === "number" && typeof lng === "number") {
                          commitStopLocation(i, {
                            name: s.locationName,
                            address,
                            lat,
                            lng,
                          });
                        }
                      }}
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                    />
                    {fixSaving && <div style={{ fontSize: 10, color: "#666" }}>Saving…</div>}
                  </div>
                )}
                </React.Fragment>
              );
            })}

            {/* Flight time row — shown whenever the piece flies (a billed
                airport on the route), including a home-based outbound, since
                the return is flight + 15 + drive home. Ferrari/Executive
                (cumulative-drive model, no flight) never show it. */}
            {(isOutbound || isRoundtrip) && hasBilledAirport && !isFerrariStyle && (
              <div style={{
                display: "grid", gridTemplateColumns: "20px 34px minmax(0,1fr) 88px 74px",
                padding: "8px 14px", borderBottom: "1px solid #f0f0f0",
                alignItems: "center", gap: 8, fontSize: 12, background: "#fafbfc",
              }}>
                <span style={{ color: "#888" }}>✈</span>
                <span />
                <div>
                  <div style={{ fontWeight: 600, color: "#444" }}>Flight time</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>Drives the airport drop &amp; return calculation</div>
                </div>
                <TimeField
                  value={flightTime}
                  onChange={setFlightTime}
                  style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
                />
                <span />
              </div>
            )}

            {/* Return to first pickup row */}
            <div style={{
              display: "grid", gridTemplateColumns: "20px 34px minmax(0,1fr) 88px 74px",
              padding: "8px 14px", borderBottom: "1px solid #f0f0f0",
              alignItems: "center", gap: 8, fontSize: 12, background: "#fafbfc",
            }}>
              <span style={{ color: "#888" }}>{homeBased ? "🏠" : job.jobType === "inbound" ? "✈" : "↩"}</span>
              <span />
              <div>
                <div style={{ fontWeight: 600, color: "#444" }}>
                  {homeBased || returnHome
                    ? "Return home"
                    : job.jobType === "inbound"
                      ? "Return to airport"
                      : isRoundtrip
                        ? "Return to first stop"
                        : "Return to first pickup"}
                </div>
                <div style={{ fontSize: 10, color: "#aaa" }}>
                  {homeBased
                    ? (fliesOut
                        ? "Auto: flight time + 15 min + drive home"
                        : "Auto: last stop + drive home")
                    : returnHome
                      ? "Auto: last stop time + Maps drive back to your house"
                      : job.jobType === "inbound"
                        ? "Auto: last delivery time + Maps drive back to airport"
                        : (job.jobType === "outbound" || isRoundtrip)
                          ? "Auto: flight time + 15 min + Maps drive time"
                          : "Auto: last stop time + Maps drive back to first pickup"}
                </div>
                {/* Holding a piece means the run ends at the vault, not the
                    airport. Manual on purpose — only the owner knows where
                    the guard actually finished. Hidden on a plain outbound:
                    the pieces flew out, so there was nothing to take home and
                    the box was just clutter. It still appears on an outbound
                    that actually kept something. */}
                {!homeBased && (job.jobType !== "outbound" || hasHeldOrVaulted) && (
                  <label style={{
                    display: "inline-flex", alignItems: "center", gap: 5, marginTop: 3,
                    fontSize: 10, color: returnHome ? "#a36b1d" : "#888", cursor: "pointer",
                  }}>
                    <input
                      type="checkbox"
                      checked={returnHome}
                      onChange={(e) => setReturnHome(e.target.checked)}
                    />
                    🏠 Returned to my house (vaulted the piece)
                  </label>
                )}
                {returnHome && !homeCoords && (
                  <div style={{ fontSize: 10, color: "#a36b1d", marginTop: 2 }}>
                    Set your home address in Settings → Your home base, then Auto-calc.
                  </div>
                )}
              </div>
              <TimeField
                value={returnTime}
                onChange={setReturnTime}
                style={{ width: "100%", padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12 }}
              />
              <div>
                <input
                  type="number" step="1" min="0" value={segments[stops.length] || ""}
                  onChange={(e) => updateSegment(stops.length, e.target.value)}
                  placeholder="0"
                  style={{ width: "100%", minWidth: 0, padding: "6px 8px", borderRadius: 6, border: "1px solid #ddd", fontSize: 12, textAlign: "right" }}
                />
                {(() => {
                  // The return leg has no dwell inside it, so once the owner
                  // has entered the return time, elapsed time from the leg's
                  // anchor (flight + 15 min on flown outbounds — same
                  // convention as the Auto hint above — otherwise the last
                  // billed stop's time) IS the drive time. Show that,
                  // recalculating live as times are edited (🕐). Falls back
                  // to the Google estimate (🧭) when times are incomplete.
                  // Display only — billing math is unchanged.
                  const flightMin = minutesOf(flightTime);
                  const anchorMin = fliesOut
                    ? (flightMin != null ? flightMin + 15 : null)
                    : lastBilledStopMinutes();
                  const retMin = minutesOf(returnTime);
                  let sec = null;
                  let fromTimes = false;
                  if (anchorMin != null && retMin != null) {
                    let d = retMin - anchorMin;
                    if (d < 0) d += 24 * 60;
                    sec = d * 60;
                    fromTimes = true;
                  }
                  if (sec == null) sec = segmentSeconds[stops.length];
                  if (sec == null) return null;
                  return (
                    <div style={{ fontSize: 10, color: "#888", textAlign: "right", marginTop: 2 }}>
                      {fromTimes ? "🕐" : "🧭"} {fmtDriveSec(sec)}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Totals row */}
            <div style={{
              display: "grid", gridTemplateColumns: "20px 34px minmax(0,1fr) 88px 74px",
              padding: "8px 14px", background: "#EEF4FF",
              alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, color: "#185FA5",
            }}>
              <span />
              <span />
              <span>Totals</span>
              <span>
                {derivedHours != null ? `${derivedHours.toFixed(2)}h` : ""}
              </span>
              <span style={{ textAlign: "right" }}>{Math.round(totalMiles)} mi</span>
            </div>
          </div>
          )}

          {broker?.flatFee > 0 && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                <input type="checkbox" checked={useFlat} onChange={(e) => setUseFlat(e.target.checked)} />
                Use flat fee instead of hourly + mileage
              </label>
              {/* Per-job flat-fee override. When the box is filled,
                  computeJobBilling uses this value instead of the
                  broker default (e.g. a one-off DAI run with extra
                  mileage that's getting a higher flat rate). Empty =
                  use the rate-card default. */}
              {useFlat && (
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "#fffbe9", border: "1px solid #f0d878", borderRadius: 7 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#7a5400", whiteSpace: "nowrap" }}>
                    Flat fee for this job:
                  </span>
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#666", fontSize: 12 }}>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={flatFeeOverride}
                      onChange={(e) => setFlatFeeOverride(e.target.value)}
                      placeholder={String(broker.flatFee)}
                      style={{ width: 110, paddingLeft: 18, fontSize: 12 }}
                    />
                  </div>
                  <span style={{ fontSize: 11, color: "#888" }}>
                    {flatFeeOverride === "" || Number(flatFeeOverride) === Number(broker.flatFee)
                      ? `Using broker default ($${broker.flatFee})`
                      : `Override — broker default is $${broker.flatFee}`}
                  </span>
                  {flatFeeOverride !== "" && (
                    <button
                      type="button"
                      onClick={() => setFlatFeeOverride("")}
                      style={{
                        marginLeft: "auto", background: "transparent",
                        border: "1px solid #d0c890", color: "#7a5400",
                        borderRadius: 5, fontSize: 10, padding: "3px 8px", cursor: "pointer",
                      }}
                    >Reset</button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Line items */}
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-header"><div className="panel-title">Line items</div></div>
            {billing.lineItems.map((li, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 14px", borderBottom: "1px solid #f0f0f0", fontSize: 12 }}>
                <div>
                  <div style={{ fontWeight: 500 }}>{li.label}</div>
                  {li.detail && <div style={{ fontSize: 10, color: "#888", marginTop: 2 }}>{li.detail}</div>}
                </div>
                <span style={{ fontWeight: 600 }}>{formatMoney(li.amount)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: "#EEF4FF", fontWeight: 700 }}>
              <span>Total</span>
              <span style={{ color: "#185FA5", fontSize: 15 }}>{formatMoney(billing.total)}</span>
            </div>
          </div>

          {/* POD status — among the brokers Orion dispatches, only Malca
              requires a proof of delivery. For non-Malca jobs we show a
              compact "not required" note instead of the per-stop photo
              checklist + missing-photo warning. */}
          {!podPanelOpen ? (
            <div style={{
              background: "#f3fbf5", border: "1px solid #c8e6cf", borderRadius: 8,
              padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "#2d7a2d",
              display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            }}>
              <span style={{ flex: 1, minWidth: 220 }}>
                ✓ {job.tripCancelled
                  ? `${job.tripNoShow ? "No show" : "Cancelled trip"} — nothing was delivered, so no proof of delivery (no POD PDF).`
                  : `No proof of delivery required${job.brokerName ? ` for ${job.brokerName}` : ""} — only Malca jobs need a POD.`}
              </span>
              {!job.tripCancelled && (
                <button type="button" className="btn btn-sm" onClick={() => setPodOptIn(true)}
                  title="Attach POD photos to this job even though this broker doesn't require them">
                  📎 Attach a POD anyway
                </button>
              )}
            </div>
          ) : (
          <div className="panel" style={{ marginBottom: 14 }}>
            <div className="panel-header">
              <div className="panel-title">
                {/* Count real stops only — airport is implicit on air
                    jobs and direct jobs are A→B. Falls back to total
                    count if every stop happens to be an airport (edge
                    case, shouldn't normally occur). */}
                {(() => {
                  const realStops = stops.filter((s) => s.type !== "airport");
                  const n = realStops.length || stops.length;
                  return `Proof of delivery (${n} stop${n === 1 ? "" : "s"})`;
                })()}
              </div>
            </div>
            {missingPhotos.length > 0 && (
              <div style={{ background: "#FFF8E6", borderBottom: "1px solid #EF9F27", padding: "8px 14px", fontSize: 11, color: "#8a5a00" }}>
                ⚠ {missingPhotos.length} stop{missingPhotos.length !== 1 ? "s" : ""} missing a POD photo.
              </div>
            )}
            {stops.map((s, i) => {
              // Pull every package bound to this stop. With Pass 1 the
              // guard adds HAWBs + photos per-package at each stop, so
              // most rows now have package-level data; older jobs may
              // still have only stop-level podPhotoUrl as a fallback.
              const stopPkgs = (job.packages || []).filter((p) => p.stopId === s.id);
              const hasPkgs = stopPkgs.length > 0;
              // Pieces that left this stop for a later run. When they're ALL
              // gone there's no POD to chase here — say where it went instead
              // of offering an upload that would never be the real proof.
              const awayHere = vaultedAwayAt(s.id);
              const allVaultedAway = !hasPkgs && awayHere.length > 0;
              return (
                <div key={i} style={{
                  padding: "8px 14px", borderBottom: "1px solid #f0f0f0", fontSize: 12,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ width: 24, color: "#888", fontSize: 10 }}>#{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <div>
                        {s.locationName || (s.type === "airport" ? "Airport" : "Stop")}
                        {s.noShow && <span style={{ color: "#a32d2d", fontSize: 10, marginLeft: 6 }}>✕ Skipped</span>}
                        {awayHere.length > 0 && (
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: "#a36b1d", marginLeft: 6,
                            background: "#fffdf5", border: "1px solid #f0d8a8",
                            borderRadius: 10, padding: "1px 7px", whiteSpace: "nowrap",
                          }}>🔒 Vaulted</span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: "#888" }}>{s.type || "pickup"}</div>
                      {awayHere.map((v) => (
                        <div key={v.packageId} style={{ fontSize: 10, color: "#a36b1d", marginTop: 2 }}>
                          {v.hawbNumber ? `${v.hawbNumber} — ` : ""}
                          delivered on <strong>{v.toJobNumber || "another job"}</strong>
                          {v.deliveryDate ? ` (${v.deliveryDate})` : ""}
                          {" · "}${100 * (v.nights || 1)} vault fee bills here
                          {" · POD lands on that job"}
                        </div>
                      ))}
                    </div>
                    {/* Restore a wrongly-skipped stop right here so the owner
                        can then add its POD photos. Airport never carries PODs. */}
                    {s.noShow && !isAirportLike(s) && (
                      <button
                        type="button"
                        onClick={() => toggleStopSkipped(i)}
                        disabled={fixSaving}
                        title="Restore this stop so you can add its POD photos"
                        style={{
                          fontSize: 10, fontWeight: 600, background: "#eafaef", color: "#2d7a2d",
                          border: "1px solid #b3d9be", borderRadius: 10, padding: "1px 8px",
                          cursor: "pointer", whiteSpace: "nowrap",
                        }}
                      >↩ Restore</button>
                    )}
                    {/* Stop-level photo — only when there's no package-level
                        data (legacy jobs) and the stop isn't skipped. For
                        per-package jobs the photos render in the rows below. */}
                    {!hasPkgs && !s.noShow && !allVaultedAway && (
                      s.podPhotoUrl ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                          <a href={s.podPhotoUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "#185FA5" }}>View photo</a>
                          <button
                              type="button"
                              onClick={() => editExistingPhoto(s.podPhotoUrl, (cf) => uploadOwnerStopPhoto(i, cf))}
                              disabled={fixSaving}
                              title="Rotate or re-crop this photo"
                              style={{
                                background: "none", border: "1px solid #b9cbe4", color: "#185FA5",
                                borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 600, cursor: "pointer",
                              }}
                            >↻ Rotate / crop</button>
                            <button
                            type="button"
                            onClick={() => removeOwnerStopPhoto(i)}
                            disabled={fixSaving}
                            title="Remove this POD photo"
                            style={{
                              background: "none", border: "1px solid #e8a5a5", color: "#a32d2d",
                              borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 600, cursor: "pointer",
                            }}
                          >✕ Remove</button>
                        </span>
                      ) : isAirportLike(s) ? (
                        <span style={{ fontSize: 10, color: "#888" }}>No photo needed</span>
                      ) : (
                        <OwnerPhotoUpload
                          busy={uploadingId === (s.id || `stop_${i}`)}
                          onPick={(f) => setCrop({ file: f, apply: (cf) => uploadOwnerStopPhoto(i, cf) })}
                        />
                      )
                    )}
                  </div>
                  {/* Per-package HAWB + POD rows. Surfaces the scanned
                      HAWB so the owner can verify what's about to land
                      in column J of the Malca workbook. */}
                  {hasPkgs && (
                    <div style={{ marginTop: 6, marginLeft: 34, display: "flex", flexDirection: "column", gap: 4 }}>
                      {stopPkgs.map((pkg, pi) => (
                        <div key={pkg.id || pi} style={{
                          display: "flex", flexDirection: "column", gap: 6,
                          padding: "4px 8px",
                          background: pkg.held ? "#fffdf5" : "#fafbfc",
                          border: `1px solid ${pkg.held ? "#f0d8a8" : "#eef0f3"}`,
                          borderLeft: pkg.held ? "3px solid #a36b1d" : undefined,
                          borderRadius: 6,
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 10, color: "#888", fontWeight: 700, minWidth: 44 }}>
                              {hawbLabel(job)}
                            </span>
                            <span style={{
                              fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#185FA5",
                              flex: 1, minWidth: 0,
                            }}>
                              {pkg.hawbNumber || (
                                <span style={{ color: "#EF9F27", fontStyle: "italic", fontFamily: "inherit", fontWeight: 400 }}>
                                  — missing —
                                </span>
                              )}
                            </span>
                            {pkg.held ? (
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: "#a36b1d" }}>🔒 Held</span>
                                <button
                                  type="button"
                                  onClick={() => toggleHeldPackage(pkg)}
                                  disabled={fixSaving}
                                  title="Un-hold this piece"
                                  style={{ background: "none", border: "1px solid #ddd", color: "#666", borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 600, cursor: "pointer" }}
                                >Undo</button>
                              </span>
                            ) : (
                              <>
                                {pkg.podPhotoUrl ? (
                                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
                                    <a
                                      href={pkg.podPhotoUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ fontSize: 11, color: "#185FA5" }}
                                    >View photo</a>
                                    <button
                                        type="button"
                                        onClick={() => editExistingPhoto(pkg.podPhotoUrl, (cf) => uploadOwnerPackagePhoto(pkg, cf))}
                                        disabled={fixSaving}
                                        title="Rotate or re-crop this photo"
                                        style={{
                                          background: "none", border: "1px solid #b9cbe4", color: "#185FA5",
                                          borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 600, cursor: "pointer",
                                        }}
                                      >↻ Rotate / crop</button>
                                      <button
                                      type="button"
                                      onClick={() => removeOwnerPackagePhoto(pkg)}
                                      disabled={fixSaving}
                                      title="Remove this POD photo"
                                      style={{
                                        background: "none", border: "1px solid #e8a5a5", color: "#a32d2d",
                                        borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 600, cursor: "pointer",
                                      }}
                                    >✕ Remove</button>
                                  </span>
                                ) : s.noShow ? (
                                  // Stop is skipped — restore it (header button) first.
                                  <span style={{ fontSize: 10, color: "#EF9F27", whiteSpace: "nowrap" }}>
                                    No photo
                                  </span>
                                ) : (
                                  <OwnerPhotoUpload
                                    busy={uploadingId === pkg.id}
                                    onPick={(f) => setCrop({ file: f, apply: (cf) => uploadOwnerPackagePhoto(pkg, cf) })}
                                  />
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleHeldPackage(pkg)}
                                  disabled={fixSaving}
                                  title="Hold / vault this piece for a later job"
                                  style={{ background: "none", border: "1px solid #f0c878", color: "#a36b1d", borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
                                >🔒 Hold</button>
                              </>
                            )}
                          </div>
                          {pkg.held && (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 44, fontSize: 11, color: "#a36b1d" }}>
                              <span style={{ fontWeight: 600 }}>Deliver on:</span>
                              <input
                                type="date"
                                value={pkg.heldDeliveryDate || ""}
                                onChange={(e) => setHeldDeliveryDate(pkg, e.target.value)}
                                style={{ fontSize: 11, padding: "3px 6px", border: "1px solid #ddd", borderRadius: 6 }}
                              />
                              <select
                                value={pkg.heldDeliveryDirection || ""}
                                onChange={(e) => setHeldDeliveryDirection(pkg, e.target.value)}
                                title="Which run this piece rides on its delivery day"
                                style={{ fontSize: 11, padding: "3px 6px", border: "1px solid #ddd", borderRadius: 6 }}
                              >
                                <option value="">run: —</option>
                                <option value="inbound">Inbound</option>
                                <option value="outbound">Outbound</option>
                              </select>
                              {/* Explicit trigger — selects only fire onChange
                                  when the value CHANGES, so re-picking an
                                  already-set run does nothing. This button
                                  always works once both fields are set. */}
                              {pkg.heldDeliveryDate && pkg.heldDeliveryDirection && (
                                <button
                                  type="button"
                                  disabled={fixSaving}
                                  onClick={() => maybeMoveHeldPieceToRun(pkg, pkg.heldDeliveryDate, pkg.heldDeliveryDirection)}
                                  title={`Move this piece onto the ${pkg.heldDeliveryDirection} job on ${pkg.heldDeliveryDate}`}
                                  style={{
                                    background: "#185FA5", border: "1px solid #185FA5", color: "#fff",
                                    borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 700,
                                    cursor: "pointer", whiteSpace: "nowrap",
                                  }}
                                >→ Move to that job</button>
                              )}
                              {pkg.heldFromJobNumber && pkg.heldFromJobNumber !== (job.jobNumber || "") && (
                                <span style={{ color: "#888" }}>· from {pkg.heldFromJobNumber}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}

          {/* POD cropper — same step the guard sees on the job. Opens when the
              owner picks/takes a POD photo; on done/skip it uploads + saves to
              whichever package or stop opened it. */}
          {crop && (
            <PodCropper
              file={crop.file}
              onCancel={() => setCrop(null)}
              onSkip={(f) => { const apply = crop.apply; setCrop(null); apply(f); }}
              onDone={(f) => { const apply = crop.apply; setCrop(null); apply(f); }}
            />
          )}

          {/* Notes */}
          <div>
            {/* The Orion FAQ "explain time over 3 hours" rule applies to the
                direct contractor activity log (3hr min). It's not enforced for
                Malca outbound/inbound jobs, so only nudge on direct jobs. */}
            {(() => {
              // Orion's OWN directs bill on the contractor activity log with
              // its 3-hour minimum, which is what the FAQ rule is about.
              // Malca directs bill on Malca's own sheet and routinely run
              // longer, so the nudge doesn't apply to them.
              const flag = isOrionDirect && derivedHours != null && derivedHours > 3 && !notes.trim();
              return (
                <>
                  <label style={{ fontSize: 11, fontWeight: 700, color: "#555", display: "block", marginBottom: 4 }}>
                    Notes (optional)
                    {flag && (
                      <span style={{ fontWeight: 400, color: "#a36b1d", marginLeft: 8 }}>
                        · ⚠ direct over 3 hours — Orion FAQ asks for a brief reason (heavy traffic, late customer, etc.)
                      </span>
                    )}
                  </label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything Orion/Malca should know…"
                    style={{
                      width: "100%", minHeight: 60, padding: "8px 10px", borderRadius: 7,
                      border: flag ? "1px solid #f0c878" : "1px solid #ddd",
                      background: flag ? "#fffbe9" : "#fff",
                      fontSize: 12,
                    }} />
                </>
              );
            })()}
          </div>
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid #e8e8e8", display: "flex", justifyContent: "space-between", gap: 8 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
              <button
                className="btn"
                onClick={handleDownloadPackage}
                disabled={downloading || saving}
                title={saveFolder
                  ? `Files are filed into ${saveFolder}\\${folderPathForJob(job, broker?.saveFolderName || broker?.name, broker?.saveDateFolders !== false).join("\\")}`
                  : "Generate the Malca worksheet and POD PDF for this job"}
              >
                {downloading ? "Preparing..." : "📦 Download package"}
              </button>
              {/* Where the files land. Chrome/Edge only — elsewhere there's
                  nothing to offer, so say nothing rather than dangle a
                  control that can't work. */}
              {isFolderSaveSupported() && (
                <div style={{ fontSize: 9, color: "#888", whiteSpace: "nowrap" }}>
                  {saveFolder ? (
                    <>
                      → {saveFolder}\{folderPathForJob(job, broker?.saveFolderName || broker?.name, broker?.saveDateFolders !== false).join("\\")}
                      {" "}
                      <button type="button" onClick={chooseSaveFolder}
                        style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontSize: 9, padding: 0, textDecoration: "underline" }}>change</button>
                      {" · "}
                      <button type="button" onClick={clearSaveFolder}
                        style={{ background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: 9, padding: 0, textDecoration: "underline" }}>use Downloads</button>
                    </>
                  ) : (
                    <button type="button" onClick={chooseSaveFolder}
                      style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontSize: 9, padding: 0, textDecoration: "underline" }}>
                      Choose a folder to file these in…
                    </button>
                  )}
                </div>
              )}
            </div>
            <button className="btn" onClick={handleSaveDraft} disabled={saving}>
              {saving ? "Saving..." : "Save draft"}
            </button>
            <button
              className="btn btn-green"
              onClick={handleApprove}
              disabled={saving}
              title={["invoiced", "sent", "paid"].includes(job.invoiceStatus)
                ? `Rebuilds the draft invoices with the current numbers — ${(billing.numGuards || 1)} invoice${(billing.numGuards || 1) > 1 ? "s" : ""}`
                : `Approves billing and creates ${(billing.numGuards || 1)} invoice${(billing.numGuards || 1) > 1 ? "s" : ""}`}
            >
              {saving
                ? "Saving..."
                : ["invoiced", "sent", "paid"].includes(job.invoiceStatus)
                  ? `✓ Re-approve & update invoices (${billing.numGuards || 1})`
                  : `✓ Approve & invoice (${billing.numGuards || 1})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── BUNDLED (DAI) TAB BODY ──────────────────────────────────────────
// Renders one row per DAI bundle ready to forward, plus a "Forward"
// modal that bulk-marks every job in the bundle as invoiced. DAI is
// flat-rate so we don't run the per-job Review modal — there's nothing
// to reconcile (no hours, no mileage). The action is purely "yes I
// forwarded the schedule email to accountspayable@orionsecurityservices.com".
function BundledTabBody({ bundles, brokers, userProfile, onForwarded }) {
  const [forwarding, setForwarding] = useState(null); // bundle obj or null
  // Local "today" (YYYY-MM-DD) for per-job ready/upcoming badges in the
  // expanded rows. Matches the parent's todayYmd derivation.
  const todayYmd = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  })();
  // Per-bundle expanded state — keyed by bundleId. Owners can preview
  // the underlying jobs (date, route, $) without opening the Forward
  // modal, so "what does this bundle even contain?" is one click away.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  function toggleExpanded(bundleId) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(bundleId)) next.delete(bundleId);
      else next.add(bundleId);
      return next;
    });
  }

  // Per-job flat amount = (broker.flatFee × numGuards) + expenses, with
  // a fallback to billedAmount if already computed previously. Returns
  // a number; callers sum across the bundle.
  function jobFlatAmount(job) {
    if (typeof job.billedAmount === "number" && job.billedAmount > 0) return job.billedAmount;
    const broker = brokers.find((b) => (b.name || "").toLowerCase() === (job.brokerName || "").toLowerCase());
    const flat = Number(job.flatFeeOverride) || Number(broker?.flatFee) || 0;
    const guards = Math.max(1, Number(job.numGuards) || 1);
    const expenseItems = Array.isArray(job.expenseItems) ? job.expenseItems : [];
    const expenses = expenseItems.reduce(
      (s, it) => s + (Number(it.amount) || 0) * (it.roundTrip ? 2 : 1),
      0
    );
    return Math.round((flat * guards + expenses) * 100) / 100;
  }

  function bundleTotal(b) {
    return b.jobs.reduce((s, j) => s + jobFlatAmount(j), 0);
  }

  // YYYY-MM-DD → "Apr 23". Used for date-range chips on bundle rows.
  function fmtYmd(ymd) {
    if (!ymd) return "—";
    const [y, m, d] = String(ymd).split("-").map(Number);
    if (!y || !m || !d) return ymd;
    return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }

  const grandTotal = bundles.reduce((s, b) => s + bundleTotal(b), 0);
  const readyCount = bundles.filter((b) => b.ready && !b.fullyInvoiced).length;
  const invoicedCount = bundles.filter((b) => b.fullyInvoiced).length;
  const fillingCount = bundles.length - readyCount - invoicedCount;

  return (
    <>
      <div style={{
        background: "#FFF7E0", border: "1px solid #F0D88C", borderRadius: 8,
        padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12,
        fontSize: 12, color: "#74540C",
      }}>
        <span style={{ fontSize: 16 }}>🔔</span>
        <div style={{ flex: 1 }}>
          <strong>{readyCount}</strong> {readyCount === 1 ? "batch is" : "batches are"} ready to forward
          {fillingCount > 0 && <> · <strong>{fillingCount}</strong> still filling</>}.
          {readyCount > 0 && (
            <> Forward the schedule email to{" "}
              <span style={{ fontFamily: "monospace" }}>accountspayable@orionsecurityservices.com</span>,
              then click <strong>Forward bundle</strong> to mark them invoiced.</>
          )}
          {readyCount === 0 && fillingCount > 0 && (
            <> Nothing to forward yet — waiting on upcoming jobs to complete each batch.</>
          )}
        </div>
      </div>

      <div style={{
        background: "#EEF4FF", border: "1px solid #C5D9F5", borderRadius: 8,
        padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 24,
      }}>
        <div>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>READY</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#0E7C46" }}>{readyCount}</div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>FILLING</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#854F0B" }}>{fillingCount}</div>
        </div>
        {invoicedCount > 0 && (
          <div>
            <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>INVOICED</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#666" }}>{invoicedCount}</div>
          </div>
        )}
        <div>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>JOBS</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#185FA5" }}>
            {bundles.reduce((s, b) => s + b.jobCount, 0)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: "#888", fontWeight: 700 }}>TOTAL FLAT-FEE</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#185FA5" }}>{formatMoney(grandTotal)}</div>
        </div>
      </div>

      <div className="panel">
        {/* Extra leading column for the expand chevron so the alignment
            stays tidy whether or not the row is open. */}
        <div style={{
          display: "grid", gridTemplateColumns: "24px 1fr 1.2fr 90px 110px 120px",
          padding: "8px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
          fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
        }}>
          <span />
          <span>Date range</span>
          <span>Broker · Rep</span>
          <span style={{ textAlign: "center" }}>Ready</span>
          <span>Est. total</span>
          <span style={{ textAlign: "right" }}>Actions</span>
        </div>
        {bundles.map((b) => {
          const isOpen = expandedIds.has(b.bundleId);
          const jobsSorted = b.jobs.slice().sort((a, c) =>
            String(a.scheduledDate || "").localeCompare(String(c.scheduledDate || ""))
          );
          // Per-job readiness — the SAME rule the batch counter uses
          // (jobReadyByDate: complete OR today-or-earlier), so the "3/6"
          // badge and the ✅ rows can never disagree. Before this the rows
          // used strictly-past and lagged the counter by a day.
          const isJobReady = (j) => jobReadyByDate(j, todayYmd);
          return (
            <React.Fragment key={b.bundleId}>
              <div
                onClick={() => toggleExpanded(b.bundleId)}
                style={{
                  display: "grid", gridTemplateColumns: "24px 1fr 1.2fr 90px 110px 120px",
                  padding: "10px 14px", borderBottom: isOpen ? "1px solid #f5f5f5" : "1px solid #f0f0f0",
                  alignItems: "center", gap: 8, fontSize: 12,
                  cursor: "pointer",
                  // Filling batches get a faint amber wash so the eye
                  // jumps to the green/ready ones first.
                  background: isOpen ? "#FAFBFC" : (b.ready ? "#fff" : "#FFFCF3"),
                }}
                title={isOpen ? "Collapse" : "Show jobs in this bundle"}
              >
                <span style={{
                  color: "#888", fontSize: 11, display: "inline-block",
                  transition: "transform 0.15s",
                  transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                }}>▶</span>
                <span style={{ color: "#444", fontSize: 11 }}>
                  {b.earliest === b.latest ? fmtYmd(b.earliest) : `${fmtYmd(b.earliest)} – ${fmtYmd(b.latest)}`}
                </span>
                <div>
                  <div style={{ fontWeight: 500 }}>{b.brokerName || "—"}</div>
                  <div style={{ fontSize: 10, color: "#888" }}>
                    {b.repName ? `Rep ${b.repName}` : (b.dispatcherName || "—")}
                  </div>
                </div>
                {/* Ready count badge — gray "Invoiced" for already-billed
                    batches (history view), green when X of N complete and
                    not yet billed, amber + "next: <date>" while filling. */}
                <span style={{ textAlign: "center" }}>
                  <span style={{
                    display: "inline-block", fontSize: 11, fontWeight: 700,
                    padding: "2px 8px", borderRadius: 10,
                    color: b.fullyInvoiced ? "#666" : (b.ready ? "#0E7C46" : "#854F0B"),
                    background: b.fullyInvoiced ? "#EDEDED" : (b.ready ? "#E0F4E8" : "#FFF3CD"),
                  }}>
                    {b.fullyInvoiced ? "🧾 Invoiced" : `${b.ready ? "✅ " : "⏳ "}${b.readyCount}/${b.totalCount}`}
                  </span>
                  {!b.fullyInvoiced && !b.ready && b.nextUpcoming && (
                    <div style={{ fontSize: 9, color: "#a36b1d", marginTop: 2 }}>
                      next {fmtYmd(b.nextUpcoming)}
                    </div>
                  )}
                </span>
                <span style={{ fontWeight: 600 }}>{formatMoney(bundleTotal(b))}</span>
                <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                  {b.fullyInvoiced ? (
                    <span style={{ fontSize: 10, color: "#888" }}>Already billed</span>
                  ) : (
                    <button
                      className="btn btn-sm btn-blue"
                      disabled={!b.ready}
                      title={b.ready
                        ? "Mark this batch forwarded & create invoices"
                        : `Wait — ${b.totalCount - b.readyCount} job${b.totalCount - b.readyCount === 1 ? "" : "s"} still upcoming`}
                      onClick={(e) => {
                        // Stop the row toggle from also firing — we don't
                        // want clicking Forward bundle to expand the row.
                        e.stopPropagation();
                        if (b.ready) setForwarding(b);
                      }}
                    >
                      Forward bundle
                    </button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div style={{
                  padding: "4px 14px 12px 38px",
                  borderBottom: "1px solid #f0f0f0",
                  background: "#FAFBFC",
                }}>
                  <div style={{
                    border: "1px solid #e8e8e8", borderRadius: 6, overflow: "hidden", background: "#fff",
                  }}>
                    {/* No Job # column — DAI schedule jobs rarely carry one
                        (it rendered "—" all the way down); the freed width
                        goes to the route, which wraps in full. */}
                    <div style={{
                      display: "grid", gridTemplateColumns: "60px 48px 78px 1fr 50px 80px",
                      padding: "6px 10px", background: "#f8f8f8",
                      fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
                    }}>
                      <span>Date</span>
                      <span>Time</span>
                      <span>Status</span>
                      <span>Route</span>
                      <span style={{ textAlign: "center" }}>Guards</span>
                      <span style={{ textAlign: "right" }}>Flat-fee</span>
                    </div>
                    {jobsSorted.map((j) => {
                      const realStops = (j.stops || []).filter((s) => s.type !== "airport");
                      const route = realStops.length === 0
                        ? "—"
                        : realStops.map((s) => s.locationName || "—").join(" → ");
                      const guards = Math.max(1, Number(j.numGuards) || 1);
                      const amt = jobFlatAmount(j);
                      const locked = ["invoiced", "sent", "paid"].includes(j.invoiceStatus);
                      const jobReady = isJobReady(j);
                      return (
                        <div key={j.id} style={{
                          display: "grid", gridTemplateColumns: "60px 48px 78px 1fr 50px 80px",
                          padding: "7px 10px", borderTop: "1px solid #f0f0f0",
                          fontSize: 11, alignItems: "center", gap: 8,
                          opacity: locked ? 0.55 : 1,
                        }}>
                          <span style={{ color: "#444" }}>{fmtYmd(j.scheduledDate)}</span>
                          <span style={{ color: "#444", fontFamily: "monospace" }}>{j.scheduledTime || "—"}</span>
                          <span style={{ fontSize: 10, color: jobReady ? "#0E7C46" : "#a36b1d" }}>
                            {jobReady ? "✅ ready" : "⏳ upcoming"}
                          </span>
                          {/* Full route, wrapping — the owner matches these
                              rows against the rep's schedule email, so a
                              clipped "Belk - Moo…" defeats the point. */}
                          <span style={{ color: "#444", minWidth: 0, lineHeight: 1.45, overflowWrap: "anywhere" }}>
                            {route}
                          </span>
                          <span style={{ textAlign: "center", color: "#444" }}>{guards}</span>
                          <span style={{ textAlign: "right", fontWeight: 600 }}>{formatMoney(amt)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {forwarding && (
        <ForwardBundleModal
          bundle={forwarding}
          brokers={brokers}
          userProfile={userProfile}
          jobFlatAmount={jobFlatAmount}
          bundleTotal={bundleTotal}
          fmtYmd={fmtYmd}
          onClose={() => setForwarding(null)}
          onDone={() => {
            const done = forwarding;
            setForwarding(null);
            onForwarded?.(done);
          }}
        />
      )}
    </>
  );
}

// ── FORWARD BUNDLE MODAL ────────────────────────────────────────────
// Confirms the action, lists the jobs in the bundle for sanity-check,
// then on confirm bulk-updates every uninvoiced job to invoiceStatus
// "invoiced" via updateBundle (which already skips locked statuses).
function ForwardBundleModal({ bundle, brokers, userProfile, jobFlatAmount, bundleTotal, fmtYmd, onClose, onDone }) {
  useEscapeKey(() => onClose?.());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const jobsSorted = useMemo(() => {
    return bundle.jobs.slice().sort((a, b) =>
      String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || ""))
    );
  }, [bundle.jobs]);

  // Editable per-job flat fee. Seeded from the computed jobFlatAmount
  // (flatFeeOverride > broker default), but the owner can bump any one
  // before forwarding — e.g. a long-distance run that pays a higher
  // flat rate than the standard DAI fee. Keyed by job id, value is a
  // string so the input stays controlled while typing.
  const [fees, setFees] = useState(() => {
    const init = {};
    for (const j of bundle.jobs) init[j.id] = String(jobFlatAmount(j));
    return init;
  });
  function effectiveFee(j) {
    const v = Number(fees[j.id]);
    return Number.isFinite(v) ? v : jobFlatAmount(j);
  }
  const editedTotal = jobsSorted.reduce((s, j) => s + effectiveFee(j), 0);

  async function handleForward() {
    setSaving(true);
    setError("");
    try {
      // Per Firestore rules, once a job's invoiceStatus flips to
      // "invoiced" the rules engine refuses any further updates to it
      // (the invoice-lock guard on jobs/{id}). So we MUST write the
      // billedAmount + invoice fields in a single update per job —
      // an earlier two-pass (updateBundle then updateJob for amount)
      // tripped the lock between steps and failed with "Missing or
      // insufficient permissions" partway through.
      const now = new Date();
      for (const j of bundle.jobs) {
        if (["invoiced", "sent", "paid"].includes(j.invoiceStatus)) continue;
        const amt = effectiveFee(j);
        const guards = Math.max(1, Number(j.numGuards) || 1);
        // Resolve this job's broker for the invoice doc (name/dispatcher
        // fallbacks live inside createInvoicesForJob).
        const broker = brokers.find(
          (b) => (b.name || "").toLowerCase() === (j.brokerName || "").toLowerCase()
        ) || {};
        // Build a flat-fee billing snapshot so createInvoicesForJob
        // produces invoice docs at the RIGHT amount (it's flat-fee
        // aware now). Per-guard flat = job total / guards so the docs
        // sum back to `amt` (DAI jobs are almost always 1 guard).
        const flatBilling = {
          hoursCharged: 0,
          miles: 0,
          expenses: 0,
          flatFee: Math.round((amt / guards) * 100) / 100,
          total: amt,
          numGuards: guards,
        };
        // Create the invoice doc(s) FIRST, while the job is still
        // unlocked. createInvoicesForJob clears any stale drafts for
        // this job, so re-forwarding replaces rather than duplicates.
        // eslint-disable-next-line no-await-in-loop
        await createInvoicesForJob(
          { ...j, flatFeeOverride: amt },
          broker,
          flatBilling,
        );
        // Then flip the job to invoiced + stamp the fee, AND close out
        // the workflow status. DAI is flat-fee/forwarded — no guard
        // ever works it — so without this the job lingers in the active
        // Job Inbox as "in pool"/"intake" forever even though it's
        // billed and done. Setting status="complete" drops it out of
        // the active inbox (it's not in the active-status set) while
        // keeping it fully tracked in billing.
        // eslint-disable-next-line no-await-in-loop
        await updateJob(j.id, {
          invoiceStatus: "invoiced",
          billingApprovedAt: now,
          billingApprovedBy: userProfile?.id || null,
          billedAmount: amt,
          // Persist the (possibly bumped) fee as the per-job override so
          // the amount sticks even if the broker's default flat fee
          // changes later, and so any re-derive shows the same number.
          flatFeeOverride: amt,
          status: "complete",
          completedAt: j.completedAt || now,
        });
      }
      onDone?.();
    } catch (e) {
      console.error("Forward bundle failed", e);
      setError(e?.message || "Failed to mark bundle forwarded. Try again.");
    }
    setSaving(false);
  }

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
      zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }}>
      <div style={{
        background: "#fff", borderRadius: 12, maxWidth: 640, width: "100%",
        maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
      }}>
        <div style={{
          padding: "14px 18px", borderBottom: "1px solid #e8e8e8",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>
              {bundle.isSolo ? "Forward DAI job" : "Forward DAI bundle"}
            </div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              {bundle.brokerName} · {bundle.repName ? `Rep ${bundle.repName}` : (bundle.dispatcherName || "—")} · {bundle.jobCount} {bundle.jobCount === 1 ? "job" : "jobs"} ·{" "}
              {bundle.earliest === bundle.latest ? fmtYmd(bundle.earliest) : `${fmtYmd(bundle.earliest)} – ${fmtYmd(bundle.latest)}`}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto", flex: 1 }}>
          <div className="info-bar" style={{ margin: "0 0 12px 0" }}>
            Forward the original schedule email to{" "}
            <span style={{ fontFamily: "monospace" }}>accountspayable@orionsecurityservices.com</span>{" "}
            first. Then click <strong>Mark forwarded</strong> below to lock these jobs as
            invoiced so they don't show here again.
          </div>

          <div style={{
            border: "1px solid #e8e8e8", borderRadius: 8, overflow: "hidden", marginBottom: 12,
          }}>
            <div style={{
              display: "grid", gridTemplateColumns: "80px 1fr 60px 90px",
              padding: "6px 12px", background: "#f8f8f8",
              fontSize: 10, fontWeight: 700, color: "#888", gap: 8,
            }}>
              <span>Date</span><span>Job · Route</span>
              <span style={{ textAlign: "center" }}>Guards</span>
              <span style={{ textAlign: "right" }}>Flat-fee</span>
            </div>
            {jobsSorted.map((j) => {
              const realStops = (j.stops || []).filter((s) => s.type !== "airport");
              const route = realStops.length === 0
                ? "—"
                : realStops.map((s) => s.locationName || "—").join(" → ");
              const guards = Math.max(1, Number(j.numGuards) || 1);
              const amt = jobFlatAmount(j);
              const locked = ["invoiced", "sent", "paid"].includes(j.invoiceStatus);
              return (
                <div key={j.id} style={{
                  display: "grid", gridTemplateColumns: "80px 1fr 60px 90px",
                  padding: "8px 12px", borderTop: "1px solid #f0f0f0",
                  fontSize: 11, alignItems: "center", gap: 8,
                  background: locked ? "#fafafa" : "#fff",
                  opacity: locked ? 0.55 : 1,
                }}>
                  <span style={{ color: "#444" }}>{fmtYmd(j.scheduledDate)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: "#185FA5" }}>{j.jobNumber || "—"}</div>
                    <div style={{
                      fontSize: 10, color: "#888",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }} title={route}>{route}</div>
                  </div>
                  <span style={{ textAlign: "center", color: "#444" }}>{guards}</span>
                  {/* Editable flat fee — locked jobs show read-only. */}
                  {locked ? (
                    <span style={{ textAlign: "right", fontWeight: 600 }}>{formatMoney(amt)}</span>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 2, justifyContent: "flex-end" }}>
                      <span style={{ color: "#888" }}>$</span>
                      <input
                        type="number" min={0} step="0.5"
                        value={fees[j.id] ?? ""}
                        onChange={(e) => setFees((f) => ({ ...f, [j.id]: e.target.value }))}
                        style={{
                          width: 64, textAlign: "right", fontSize: 11,
                          padding: "3px 5px", border: "1px solid #ddd", borderRadius: 5,
                        }}
                        title="Override the flat fee for this job (e.g. a long-distance run)"
                      />
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{
              display: "grid", gridTemplateColumns: "80px 1fr 60px 90px",
              padding: "8px 12px", borderTop: "2px solid #e8e8e8",
              background: "#EEF4FF", fontSize: 12, fontWeight: 700, color: "#185FA5",
              alignItems: "center", gap: 8,
            }}>
              <span />
              <span>Total</span>
              <span />
              <span style={{ textAlign: "right" }}>{formatMoney(editedTotal)}</span>
            </div>
          </div>

          {error && (
            <div style={{
              background: "#FBEAEA", border: "1px solid #E8B4B4", borderRadius: 7,
              padding: "8px 12px", fontSize: 12, color: "#A32D2D", marginBottom: 12,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: "12px 18px", borderTop: "1px solid #e8e8e8",
          display: "flex", justifyContent: "flex-end", gap: 8,
        }}>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-green" onClick={handleForward} disabled={saving}>
            {saving ? "Marking..." : `✓ Mark forwarded (${bundle.jobCount})`}
          </button>
        </div>
      </div>
    </div>
  );
}
